import { PublicClientApplication } from '@azure/msal-browser';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CircleStackIcon,
  ClockIcon,
  FolderIcon,
  FolderPlusIcon,
  GlobeAltIcon,
  PencilIcon,
  PlusIcon,
  ShareIcon,
  StopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import * as Modal from '@radix-ui/react-dialog';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { findIndex, forEach } from 'lodash';
import { ArrowUpDown, EyeIcon, Loader2, MoveUpRight, PenOff } from 'lucide-react';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast as hotToast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  checkConditionUploadFiles,
  getSyncStatusInVietnamese,
  isDisableField,
  isTextarea,
  keyMapMetadata,
  normalizeMetadataForDisplay,
  stripServerOwnedMetadata,
} from '@/common/utils';
import ToggleButton from '@/components/buttons/toggle-button/ToggleButton';
import { PendingApprovalPage } from '@/components/external-document-approval/PendingApprovalPage';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Toaster } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import SearchBox from '@/components/utils/SearchBox.tsx';
import { ReportHistoryModal } from '@/features/bld-reports/ReportHistoryModal';
import {
  type EmailCrawlerSourceCreateDefaults,
  EmailCrawlerSourceModal,
} from '@/features/email-crawler/EmailCrawlerSourceModal';
import { DocumentPermission } from '@/hooks/use-shareing-api';
import { useToast } from '@/hooks/use-toast.ts';
import axios from '@/lib/axios';
import { isBldReportsFolderPrefix } from '@/lib/bld-reports-api';
import { API_BASE_URL } from '@/lib/config';
import { type EmailCrawlerSource } from '@/lib/email-crawler-api';
import { type KnownCrawlerFolder } from '@/lib/email-crawler-folders';
import { buildBootstrapRequest, matchesBootstrapContext } from '@/lib/email-scheduler-auth-api';
import { useEmailCrawlerSourceByFolder } from '@/lib/use-email-crawler-source';
import { getAccessTokenInfoFromLocal, getUserInfoFromLocal } from '@/lib/utils.ts';

import FailedFilesDisplay from '../components/FailedFilesDisplay';
import FileUpload from '../components/utils/DragDrop';
import CrawlProgressDialog from './Dialog/CrawlProgressDialog';
import DialogRemoveFiles from './Dialog/DialogRemoveFiles';
import DialogShare from './Dialog/DialogShare';
import PrivacyDialog from './Dialog/PrivacyDialog';
import S3FolderTreeSelector from './Dialog/S3FolderTreeSelector';

// Type definitions for upload metadata form (English field names matching backend storage)
interface UploadMetadataForm {
  creator: string;
  modifier: string;
  modifiedDate: string;
  summary: string;
  effectiveDate: string;
  expirationDate: string;
  issueDate: string;
  processingStatus: string;
  status: string;
  // Additional fields for kho_doi_ngoai/ only
  documentType: string; // Loại văn bản
  field: string; // Lĩnh vực
  dataSource: string; // Nguồn dữ liệu
}

const defaultUploadMetadata: UploadMetadataForm = {
  creator: '',
  modifier: '',
  modifiedDate: '',
  summary: '',
  effectiveDate: '',
  expirationDate: '',
  issueDate: '',
  processingStatus: 'phat_hanh', // Phát hành (default)
  status: 'hieu_luc', // Hiệu lực (default)
  documentType: '', // Loại văn bản
  field: '', // Lĩnh vực
  dataSource: '', // Nguồn dữ liệu
};

// Giới hạn định dạng tệp được phép tải lên theo từng kho (prefix).
// Key là prefix gốc của kho, value là danh sách phần mở rộng cho phép (viết thường, không dấu chấm).
// Kho không khai báo ở đây thì không bị giới hạn định dạng.
const UPLOAD_EXTENSION_RULES: Record<string, string[]> = {
  'kho_ncc/': ['docx', 'pdf'],
  'kho_chat_cn/quy_dinh_phap_luat/': ['pdf'],
  'kho_nguyen_lieu/bo_chi_tieu/': ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'],
};

// Kho tự động trích xuất PDF -> JSON có cấu trúc (structured OCR).
const CHAT_CN_LEGAL_PREFIX = 'kho_chat_cn/quy_dinh_phap_luat/';

// Kho bộ chỉ tiêu chỉ nhận ảnh ở đúng thư mục gốc và tự động tạo JSON structured.
const BO_CHI_TIEU_PREFIX = 'kho_nguyen_lieu/bo_chi_tieu/';

// Trạng thái parse PDF trả về từ server (khớp với StructuredPdfOcrStatus phía backend).
type PdfParseStatus = 'queued' | 'processing' | 'succeeded' | 'failed';
type PdfParseStatusRecord = {
  status: PdfParseStatus;
  progress?: number;
  jsonKey?: string;
  error?: string;
  updatedAt?: string;
};

const mergePdfParseStatuses = (
  previous: Record<string, PdfParseStatusRecord>,
  incoming: Record<string, PdfParseStatusRecord>,
): Record<string, PdfParseStatusRecord> => {
  const next = { ...previous };
  for (const [key, candidate] of Object.entries(incoming)) {
    const current = next[key];
    if (!current) {
      next[key] = candidate;
      continue;
    }

    const currentTime = current.updatedAt ? Date.parse(current.updatedAt) : 0;
    const candidateTime = candidate.updatedAt ? Date.parse(candidate.updatedAt) : 0;
    const currentIsTerminal = current.status === 'succeeded' || current.status === 'failed';
    const candidateIsActive = candidate.status === 'queued' || candidate.status === 'processing';

    // Do not let a delayed poll response move a completed job back to an active state.
    if (currentIsTerminal && candidateIsActive) continue;
    if (candidateTime > 0 && currentTime > candidateTime) continue;
    next[key] = candidate;
  }
  return next;
};

type BoChiTieuOcrStatus = 'queued' | 'processing' | 'succeeded' | 'failed';
type BoChiTieuOcrStatusRecord = {
  status: BoChiTieuOcrStatus;
  progress?: number;
  jsonKey?: string;
  error?: string;
};

// Key localStorage để giữ trạng thái parse qua các lần reload trang.
const PDF_PARSE_STATE_KEY = 'chat-cn-legal:pdf-parse-state';
const BO_CHI_TIEU_OCR_STATE_KEY = 'bo-chi-tieu:ocr-state';

// S3 key có thuộc kho auto-parse không.
const isChatCnLegalKey = (key: string): boolean =>
  `${key || ''}`.replace(/^\/+/, '').startsWith(CHAT_CN_LEGAL_PREFIX);

const isBoChiTieuPrefix = (prefix: string): boolean =>
  `${prefix || ''}`.replace(/^\/+/, '') === BO_CHI_TIEU_PREFIX;

const isBoChiTieuImageName = (fileName: string): boolean =>
  ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'].some((extension) =>
    `${fileName || ''}`.toLowerCase().endsWith(extension),
  );

const isBoChiTieuImageKey = (key: string): boolean => {
  const normalizedKey = `${key || ''}`.replace(/^\/+/, '');
  if (!normalizedKey.startsWith(BO_CHI_TIEU_PREFIX)) return false;
  const fileName = normalizedKey.slice(BO_CHI_TIEU_PREFIX.length);
  return Boolean(fileName) && !fileName.includes('/') && isBoChiTieuImageName(fileName);
};

// Tên file có phải PDF không.
const isPdfName = (fileName: string): boolean => `${fileName || ''}`.toLowerCase().endsWith('.pdf');

// Tìm quy tắc định dạng áp dụng cho prefix hiện tại (bao gồm mọi thư mục con).
const getUploadExtensionRule = (prefix: string): { key: string; allowed: string[] } | null => {
  const normalizedPrefix = `${prefix || ''}`.replace(/^\/+/, '');

  for (const [key, allowed] of Object.entries(UPLOAD_EXTENSION_RULES)) {
    const keyWithoutSlash = key.replace(/\/+$/, '');
    const isExactOnlyRule = key === BO_CHI_TIEU_PREFIX;
    if (
      normalizedPrefix === key ||
      normalizedPrefix === keyWithoutSlash ||
      (!isExactOnlyRule && normalizedPrefix.startsWith(key))
    ) {
      return { key, allowed };
    }
  }

  return null;
};

const getFileExtension = (fileName: string): string =>
  `${fileName || ''}`.split('.').pop()?.toLowerCase() ?? '';

// Tạo một FileList từ mảng File để tương thích với các hàm upload đang nhận FileList.
// Không dùng DataTransfer vì nó tạo lại File mới và làm mất webkitRelativePath khi upload thư mục.
const toFileList = (files: File[]): FileList => {
  const list: Record<number, File> & {
    length: number;
    item: (index: number) => File | null;
    [Symbol.iterator]: () => IterableIterator<File>;
  } = {
    length: files.length,
    item: (index: number) => files[index] ?? null,
    [Symbol.iterator]: () => files[Symbol.iterator](),
  };

  files.forEach((file, index) => {
    list[index] = file;
  });

  return list as unknown as FileList;
};

// Lọc danh sách tệp theo định dạng cho phép của kho.
// validFiles: các tệp được giữ lại để upload; invalidFiles: tên tệp bị loại (để báo cho người dùng).
// Khi kho không có quy tắc (hasRule = false) thì giữ nguyên toàn bộ tệp.
const validateFilesForAllowedExtensions = (
  files: FileList | File[],
  prefix: string,
): { hasRule: boolean; validFiles: File[]; invalidFiles: string[]; allowed: string[] } => {
  const fileArray = Array.from(files).filter(Boolean);
  const rule = getUploadExtensionRule(prefix);

  if (!rule) {
    return { hasRule: false, validFiles: fileArray, invalidFiles: [], allowed: [] };
  }

  const validFiles: File[] = [];
  const invalidFiles: string[] = [];

  fileArray.forEach((file) => {
    if (rule.allowed.includes(getFileExtension(file.name))) {
      validFiles.push(file);
    } else {
      // Với upload thư mục, hiển thị đường dẫn tương đối để người dùng biết tệp nằm ở đâu
      invalidFiles.push(file.webkitRelativePath || file.name);
    }
  });

  return { hasRule: true, validFiles, invalidFiles, allowed: rule.allowed };
};

// Real-time expiration date validation
const validateExpirationDate = (effectiveDate: string, expirationDate: string): string => {
  if (!effectiveDate || !expirationDate) return '';
  const dateEffective = new Date(effectiveDate);
  const dateExpiration = new Date(expirationDate);
  if (dateExpiration <= dateEffective) {
    return 'Ngày hết hiệu lực phải lớn hơn ngày hiệu lực';
  }
  return '';
};

// Form validation for upload metadata
const validateUploadForm = (form: UploadMetadataForm): string[] => {
  const errors: string[] = [];

  // Required fields
  if (!form.summary?.trim()) errors.push('Trích yếu là bắt buộc');
  if (!form.effectiveDate) errors.push('Ngày hiệu lực là bắt buộc');
  if (!form.issueDate) errors.push('Ngày ban hành là bắt buộc');
  if (!form.processingStatus) errors.push('Trạng thái xử lý là bắt buộc');
  if (!form.status) errors.push('Tình trạng là bắt buộc');

  // Date validation: expirationDate > effectiveDate
  if (form.effectiveDate && form.expirationDate) {
    const dateEffective = new Date(form.effectiveDate);
    const dateExpiration = new Date(form.expirationDate);
    if (dateExpiration <= dateEffective) {
      errors.push('Ngày hết hiệu lực phải lớn hơn ngày hiệu lực');
    }
  }

  return errors;
};

// Type definitions
interface Group {
  id: string;
  groupname: string;
  displayPrefixes: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  active: boolean;
  createdDate: string;
  displayPrefixes: string;
  group: Group;
  password: string;
  role: string;
  updatedDate: string;
}

const PICKER_BUILDER_DEVELOPER_KEY = 'AIzaSyBI8KiTWudkw8iR5cjH7ZsA4JBN1NqJw8A';
const CLIENT_ID = '726815747852-5deq413t8kd8j3mgj48d46eatjauapq5.apps.googleusercontent.com';
const SCOPES = 'https://www.googleapis.com/auth/drive';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

type OneDriveFile = {
  id: string;
  name: string;
  size: number;
  lastModified: Date;
  contentType?: string;
  isFolder: boolean;
  parentId?: string;
};

type OneDriveSelectedItem = OneDriveFile & {
  ancestorIds: string[];
};

type SharePointFile = {
  id: string;
  name: string;
  size: number;
  lastModified: Date;
  contentType?: string;
  isFolder: boolean;
  parentId?: string;
};

type SharePointSelectedItem = SharePointFile & {
  ancestorIds: string[];
};

type SharePointSite = {
  id: string;
  name: string;
  description?: string;
  hostname?: string;
  sitePath?: string;
};

// Cache entry for SharePoint folder contents - stores minimal data needed for selection logic
type SharePointCacheItem = {
  id: string;
  name: string;
  isFolder: boolean;
  parentId: string;
};

// Cache keyed by folderId, storing the items (children) of that folder
type SharePointFolderCache = Map<string, SharePointCacheItem[]>;

export type S3File = {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
  contentType?: string;
  isFolder: boolean;
  etag?: string;
  permission?: DocumentPermission;
  /** Người thực hiện lần sửa đổi gần nhất, đọc từ metadata sidecar. */
  modifier?: string;
};

interface SyncJobStatus {
  status: string;
  created_date: string;
  dataSourceId: string;
  ingestionJobId: string;
  knowledgeBaseId: string;
  user_name: string;
  user_id: string;
  is_still_in_stock: boolean;
  node?: string;
  index_name?: string;
  errorMessage?: string;
  statistics?: {
    numberOfDocumentsDeleted: number;
    numberOfDocumentsFailed: number;
    numberOfDocumentsScanned: number;
    numberOfMetadataDocumentsModified: number;
    numberOfMetadataDocumentsScanned: number;
    numberOfModifiedDocumentsIndexed: number;
    numberOfNewDocumentsIndexed: number;
  };
  failureReasons?: string[];
}

const ignoreCache = { current: false };

let runNowPollInterval: ReturnType<typeof setInterval> | null = null;

const CRAWL_JOBS_KEY = 'crawl:jobs';
const CURRENT_CRAWL_JOB_KEY = 'crawl:current_job';

interface CrawledPage {
  url: string;
  title: string;
  status: number;
  depth: number;
  linksCount: number;
}

type CrawlJobStatus = {
  success: boolean;
  status: 'scraping' | 'completed' | 'failed' | 'blocked';
  completed: number;
  total: number;
  creditsUsed: number;
  expiresAt: string;
  startTime?: number;
  isProcessing?: boolean;
  error?: string;
  message?: string;
  pages?: CrawledPage[];
};

type DailyReportSourceType =
  'production_inventory' | 'equipment_status' | 'electrical_structure' | 'production_stoppage';

type LegacyDailyReportSourceType = 'equipment' | 'planning';

type DailyReportSubjectFilters = Record<DailyReportSourceType, string>;

const DAILY_REPORT_FILTER_FIELDS: Array<{
  key: DailyReportSourceType;
  label: string;
  placeholder: string;
}> = [
  {
    key: 'production_inventory',
    label: 'Tồn kho sản xuất',
    placeholder: 'Báo cáo tồn kho sản xuất ngày',
  },
  {
    key: 'equipment_status',
    label: 'Tình trạng thiết bị',
    placeholder: 'Báo cáo tình trạng thiết bị ngày',
  },
  {
    key: 'electrical_structure',
    label: 'Cơ cấu điện',
    placeholder: 'Báo cáo cơ cấu điện ngày',
  },
  {
    key: 'production_stoppage',
    label: 'Hạng mục dừng sản xuất',
    placeholder: 'Báo cáo các hạng mục dừng sản xuất ngày',
  },
];

const normalizeDailyReportSubjectFilters = (raw: unknown): DailyReportSubjectFilters => {
  const normalized = createEmptyDailyReportSubjectFilters();

  if (typeof raw === 'string') {
    normalized.equipment_status = raw;
    return normalized;
  }

  if (Array.isArray(raw)) {
    normalized.equipment_status = typeof raw[0] === 'string' ? raw[0] : '';
    normalized.production_inventory =
      typeof raw[1] === 'string' ? raw[1] : typeof raw[0] === 'string' ? raw[0] : '';
    return normalized;
  }

  if (raw && typeof raw === 'object') {
    const candidate = raw as Partial<
      Record<DailyReportSourceType | LegacyDailyReportSourceType, unknown>
    >;

    for (const field of DAILY_REPORT_FILTER_FIELDS) {
      const value = candidate[field.key];
      normalized[field.key] = typeof value === 'string' ? value : '';
    }

    if (!normalized.equipment_status && typeof candidate.equipment === 'string') {
      normalized.equipment_status = candidate.equipment;
    }
    if (!normalized.production_inventory && typeof candidate.planning === 'string') {
      normalized.production_inventory = candidate.planning;
    }

    return normalized;
  }

  return normalized;
};

const createEmptyDailyReportSubjectFilters = (): DailyReportSubjectFilters => ({
  production_inventory: '',
  equipment_status: '',
  electrical_structure: '',
  production_stoppage: '',
});

const useCurrentCrawlJob = (apiBaseUrl: string) => {
  const [currentJob, setCurrentJob] = useState<{
    id: string;
    url: string;
    limit: number;
    prefix: string;
    startTime?: number;
    status?: CrawlJobStatus;
    taskId: string;
  } | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [pages, setPages] = useState<CrawledPage[]>([]);

  // Load current job from localStorage on mount
  useEffect(() => {
    const savedJob = localStorage.getItem(CURRENT_CRAWL_JOB_KEY);
    if (savedJob) {
      const parsedJob = JSON.parse(savedJob) as typeof currentJob;
      setCurrentJob(parsedJob);

      // Initialize elapsed time from the job's start time
      if (parsedJob?.startTime) {
        setElapsedTime(Math.floor((Date.now() - parsedJob!.startTime) / 1000));
      }
    }
  }, []);

  // Update elapsed time every second while job exists
  useEffect(() => {
    if (!currentJob?.startTime) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - currentJob.startTime!) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [currentJob?.startTime]);

  // Poll for job status
  useEffect(() => {
    if (!currentJob?.id) return;

    const pollStatus = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/api/crawl/${currentJob.id}?taskId=${currentJob.taskId}`,
        );
        const data = response.data as CrawlJobStatus & { pages?: CrawledPage[] };
        const status = data as CrawlJobStatus;

        // Store pages separately for real-time display
        // Always update pages - even if empty (to clear stale data)
        setPages(data.pages || []);

        setCurrentJob((prev) => {
          if (!prev) return null;
          const updated = { ...prev, status };

          // Update localStorage with latest status
          localStorage.setItem(CURRENT_CRAWL_JOB_KEY, JSON.stringify(updated));

          // If job is completed, failed, or blocked, clear the job
          // BUT: if status is 'completed' and isProcessing is true, keep polling
          if (
            (status.status === 'completed' && !status.isProcessing) ||
            status.status === 'failed' ||
            status.status === 'blocked'
          ) {
            // Use setTimeout to clear the job after the status update is processed
            setTimeout(() => {
              setCurrentJob(null);
              localStorage.removeItem(CURRENT_CRAWL_JOB_KEY);
              setPages([]);
            }, 3000);
          }

          return updated;
        });
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    // Poll every 10 seconds (reduced from 3s to avoid excessive polling)
    const interval = setInterval(pollStatus, 10000);
    void pollStatus(); // Initial poll

    return () => clearInterval(interval);
  }, [currentJob?.id, apiBaseUrl]);

  return {
    currentJob,
    elapsedTime,
    pages,
    setCurrentJob: (job: typeof currentJob) => {
      if (job && !job.startTime) {
        job.startTime = Date.now();
      }
      setCurrentJob(job);
      if (job) {
        localStorage.setItem(CURRENT_CRAWL_JOB_KEY, JSON.stringify(job));
      } else {
        localStorage.removeItem(CURRENT_CRAWL_JOB_KEY);
      }
    },
  };
};

const getAllFilesUnderFolder = (fileSystem: Record<string, S3File[]>, prefix: string): string[] => {
  const files: string[] = [];
  const searchInPrefix = (currentPrefix: string) => {
    const currentFiles = fileSystem[currentPrefix] || [];
    currentFiles.forEach((file) => {
      files.push(file.key);
      if (file.isFolder) {
        searchInPrefix(file.key);
      }
    });
  };
  searchInPrefix(prefix);
  return files;
};

const getAllFilesInCurrentView = (
  fileSystem: Record<string, S3File[]>,
  prefix: string,
): string[] => {
  const files: string[] = [];
  // Add current level files
  const currentFiles = fileSystem[prefix] || [];
  currentFiles.forEach((file) => {
    files.push(file.key);
    // If it's a folder, add all files under it
    if (file.isFolder) {
      files.push(...getAllFilesUnderFolder(fileSystem, file.key));
    }
  });
  return files;
};

/**
 * Đọc người sửa đổi gần nhất cho danh sách tệp qua `/api/files/metadata-batch`.
 * Tài liệu chưa từng được sửa thì mặc định lấy theo người tạo.
 * Lỗi đọc metadata không làm hỏng danh sách -> trả về map rỗng.
 */
const fetchModifierByKey = async (
  apiBaseUrl: string,
  keys: string[],
): Promise<Record<string, string>> => {
  if (keys.length === 0) return {};

  try {
    // Server giới hạn 200 key mỗi lần gọi.
    const response = await axios.post(`${apiBaseUrl}/api/files/metadata-batch`, {
      keys: keys.slice(0, 200),
    });

    const metadataMap = (response?.data || {}) as Record<string, any>;
    const result: Record<string, string> = {};

    for (const [key, attrs] of Object.entries(metadataMap)) {
      if (!attrs || typeof attrs !== 'object') continue;

      const modifier = `${(attrs as Record<string, any>)['modifier'] ?? ''}`.trim();
      const creator = `${(attrs as Record<string, any>)['creator'] ?? ''}`.trim();
      const value = modifier && modifier !== '---' ? modifier : creator;

      if (value && value !== '---') {
        result[key] = value;
      }
    }

    return result;
  } catch {
    return {};
  }
};

/** Thu thập key của các tệp (bỏ qua thư mục) trong dữ liệu danh sách. */
const collectFileKeys = (fileData: Record<string, any>): string[] => {
  const keys: string[] = [];
  for (const files of Object.values(fileData)) {
    if (!Array.isArray(files)) continue;
    for (const file of files as S3File[]) {
      if (file && !file.isFolder && file.key) keys.push(file.key);
    }
  }
  return keys;
};

/** Gắn người sửa đổi vào từng tệp trong dữ liệu danh sách. */
const attachModifiers = (
  fileData: Record<string, S3File[]>,
  modifierByKey: Record<string, string>,
): Record<string, S3File[]> => {
  const result: Record<string, S3File[]> = {};

  for (const [folderKey, files] of Object.entries(fileData)) {
    if (!Array.isArray(files)) {
      result[folderKey] = files;
      continue;
    }

    result[folderKey] = files.map((file) =>
      !file?.isFolder && modifierByKey[file?.key]
        ? { ...file, modifier: modifierByKey[file.key] }
        : file,
    );
  }

  return result;
};

const useFiles = (
  apiBaseUrl: string,
  rootPrefix: string,
  displayPrefixes: string[] = [],
  searchQuery: string,
  currentPage: number,
  browsePage: number,
  setSearchQuery: Dispatch<SetStateAction<string>>,
  _idCreator: string | undefined,
  selectedKeys?: Set<string>,
  selectStates?: MutableRefObject<Record<string, boolean>>,
  setSelectedKeys?: any,
) => {
  console.log('1.0.180-hoaphat');
  const [data, setData] = useState<Record<string, S3File[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [estimatedTotalHits, setEstimatedTotalHits] = useState(0);
  const [viewOnly, setViewOnly] = useState(false);
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalItems: number;
    totalPages: number;
    pageSize: number;
    hasMore: boolean;
  } | null>(null);

  const rootPrefixRef = useRef(rootPrefix);
  useEffect(() => {
    rootPrefixRef.current = rootPrefix;
  }, [rootPrefix]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const user = getUserInfoFromLocal();
      const response = await axios.get(`${apiBaseUrl}/api/files`, {
        params: {
          cache: false,
          displayPrefixes: JSON.stringify(displayPrefixes),
          prefix: rootPrefixRef.current,
          userId: user?.id || '',
          groupId: user?.group?.id || '',
          groupIds: JSON.stringify(user?.GroupIds || []),
          groupname: user?.groupname || '',
          page: browsePage,
          limit: 100,
        },
        timeout: 300000,
      });

      const newSelectedKeys: string[] = [];

      const { pagination: paginationData, viewOnly: viewOnlyData, ...fileData } = response.data;
      setPagination(paginationData || null);
      setViewOnly(!!viewOnlyData);

      forEach(fileData, (files, folderKey) => {
        if (selectedKeys?.has(folderKey)) {
          forEach(files, (file) => {
            if (!(selectStates?.current[file.key] === false)) {
              newSelectedKeys.push(file.key);
            }
          });
        }
      });

      // console.log('newSelectedKeys', newSelectedKeys);

      if (newSelectedKeys.length) {
        setSelectedKeys?.((prev: any) => {
          const merged = new Set(prev);
          newSelectedKeys.forEach((key) => merged.add(key));
          return merged;
        });
      }

      setData(fileData);
      setError(null);

      // Người sửa đổi nằm trong metadata sidecar nên phải lấy riêng.
      // Hiển thị danh sách trước, bổ sung cột người sửa đổi sau để không làm chậm màn hình.
      const fileKeys = collectFileKeys(fileData);
      if (fileKeys.length > 0) {
        const modifierByKey = await fetchModifierByKey(apiBaseUrl, fileKeys);
        if (Object.keys(modifierByKey).length > 0) {
          setData((prev) => attachModifiers(prev, modifierByKey));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch files'));
    } finally {
      setIsLoading(false);
      setEstimatedTotalHits(0);
      setSearchQuery('');
    }
  };

  const handleSearch = async (query: string) => {
    try {
      if (!query) {
        return;
      }
      setIsLoading(true);
      const user = getUserInfoFromLocal();
      const response: any = await axios.get(`${apiBaseUrl}/api/search-db`, {
        params: {
          query,
          rootPrefix,
          offset: (currentPage - 1) * 20,
          limit: 20,
          userId: user?.id || '',
          groupId: user?.group?.id || '',
          groupIds: JSON.stringify(user?.GroupIds || []),
          groupname: user?.groupname || '',
        },
        timeout: 300000,
      });
      const searchData = { [rootPrefix]: response?.data?.result || [] };
      setData(searchData);
      setEstimatedTotalHits(response?.data?.estimatedTotalHits || 0);
      setPagination(null); // Clear pagination for search results

      // Kết quả tìm kiếm cũng hiển thị người sửa đổi.
      const fileKeys = collectFileKeys(searchData);
      if (fileKeys.length > 0) {
        const modifierByKey = await fetchModifierByKey(apiBaseUrl, fileKeys);
        if (Object.keys(modifierByKey).length > 0) {
          setData((prev) => attachModifiers(prev, modifierByKey));
        }
      }

      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch files'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery && !isLoading) {
      void fetchData();
    }

    if (searchQuery && !isLoading) {
      void handleSearch(searchQuery);
    }
  }, [searchQuery, currentPage, browsePage]);

  return { data, isLoading, error, estimatedTotalHits, pagination, viewOnly, refetch: fetchData };
};

// Add isTextFile function
const isTextFile = (file: S3File): boolean => {
  const extension = file?.name?.split('.')?.pop()?.toLowerCase() ?? '';
  const textExtensions = [
    'txt',
    'md',
    'json',
    'js',
    'ts',
    'jsx',
    'tsx',
    'css',
    'html',
    'xml',
    'yaml',
    'yml',
  ];
  return textExtensions.includes(extension || '');
};

// Check if file is an image
const isImageFile = (file: S3File | null): boolean => {
  if (!file) return false;
  const extension = file?.name?.split('.')?.pop()?.toLowerCase() ?? '';
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'];
  return imageExtensions.includes(extension || '');
};

// Get OCR file path for an image
const getOcrFilePath = (file: S3File): string => {
  return `${file.key}.ocr.txt`;
};

const msalConfig = {
  auth: {
    clientId: '584d5fa9-5123-4602-a1a5-a3746a83745f',
    authority: 'https://login.microsoftonline.com/consumers',
    redirectUri:
      import.meta.env['VITE_ONE_DRIVE_REDIRECT_URI'] ||
      'https://s3-explorer.cmcts1.studio.ai.vn/sharepoint/redirect',
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const weightPermission = {
  [DocumentPermission.VIEW_DOCUMENT]: 1,
  [DocumentPermission.DOWNLOAD_DOCUMENT]: 2,
  [DocumentPermission.EDIT_DOCUMENT]: 10,
  [DocumentPermission.OWNER_DOCUMENT]: 100,
};

let isViewOnlyMode = false;
export const setViewOnlyMode = (value: boolean) => {
  isViewOnlyMode = value;
};

// const isAdmin = () => {
//   const user = getUserInfoFromLocal();

//   return (
//     `${user?.groupname}`.toUpperCase() === 'MASTER_ADMIN' ||
//     `${user?.role}`.toUpperCase() === 'ADMIN' ||
//     `${user?.role}`.toUpperCase() === 'SITE_ADMIN'
//   );
// };

const canEditDocument = (permission?: DocumentPermission) => {
  if (isViewOnlyMode || !permission) return false;
  return weightPermission[permission] >= weightPermission[DocumentPermission.EDIT_DOCUMENT];
};

const canDownloadDocument = (permission?: DocumentPermission) => {
  if (!permission) return false;
  return weightPermission[permission] >= weightPermission[DocumentPermission.DOWNLOAD_DOCUMENT];
};

const canViewDocument = (permission?: DocumentPermission) => {
  if (!permission) return false;
  return weightPermission[permission] >= weightPermission[DocumentPermission.VIEW_DOCUMENT];
};

const HomeWithoutContext = memo<{
  homeLabel?: string;
  apiBaseUrl?: string;
  rootUrlHostApi?: string;
  rootPrefix?: string;
  hiddenSynchronization?: boolean;
  asSelector?: boolean;
  onSelected?: (selectedFiles: S3File[]) => void;
  displayPrefixes?: string[];
  preSelectedFiles?: string[];
  idCreator?: string;
}>(
  ({
    homeLabel = 'Kho tài liệu',
    apiBaseUrl = API_BASE_URL,
    rootPrefix = '',
    rootUrlHostApi = 'https://hoaphat.cagent.cmcts.ai/api/v1/',
    hiddenSynchronization = true,
    asSelector = false,
    onSelected = (files) => console.log('Đã chọn:', files),
    displayPrefixes,
    preSelectedFiles,
    idCreator,
  }) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set(preSelectedFiles));
    const [currentPrefix, setCurrentPrefix] = useState(rootPrefix);
    const [forcePrefix, setForcePrefix] = useState<string | null>('');
    const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<S3File | null>(null);
    const [fileToMove, setFileToMove] = useState<S3File | null>(null);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [, setUploadType] = useState<'files' | 'folder' | null>(null);
    const searchBoxRef = useRef<any>(null);
    const { toast } = useToast();
    const [fileToRename, setFileToRename] = useState<S3File | null>(null);
    const [fileToView, setFileToView] = useState<S3File | null>(null);
    const [metadataForEdit, setMetadataForEdit] = useState<any>(null);
    const [newName, setNewName] = useState('');
    const [openDialogRename, setOpenDialogRename] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [downloadingItems, setDownloadingItems] = useState<Set<string>>(new Set());
    const [fileContent, setFileContent] = useState<string>('');
    const [isLoadingContent, setIsLoadingContent] = useState(false);
    const [isSavingContent, setIsSavingContent] = useState(false);
    const [activeTab, setActiveTab] = useState<'rename' | 'edit' | 'edit-metadata'>('rename');
    const [isWebsiteDialogOpen, setIsWebsiteDialogOpen] = useState(false);
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [websiteUrlError, setWebsiteUrlError] = useState('');
    const [crawlLimit, setCrawlLimit] = useState('10');
    const [maxDepth, setMaxDepth] = useState('2');
    const [isCrawling, setIsCrawling] = useState(false);
    // Pagination support - crawl multiple pages with page=1, page=2, etc.
    const [maxPagesToCrawl, setMaxPagesToCrawl] = useState('10');
    // S3 folder selection states
    const [selectedS3Folder, setSelectedS3Folder] = useState<string>('');
    const [isFetchingFolders, setIsFetchingFolders] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [_isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [browsePage, setBrowsePage] = useState(1);
    const [isFolderClicking, setIsFolderClicking] = useState(false);
    const { currentJob, elapsedTime, pages, setCurrentJob } = useCurrentCrawlJob(apiBaseUrl);
    const [duplicateFiles, setDuplicateFiles] = useState<FileList | null>(null);
    const [folderExist, setFolderExist] = useState<string>('');
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [folderToShowFlow, setFolderToShowFlow] = useState('');
    const [showInfoFlowsDialogs, setShowInfoFlowsDialogs] = useState(false);
    const [loadingFlowsDialogs, setLoadingFlowsDialogs] = useState(false);
    const [dataFlowModels, setDataFlowModels] = useState([]);
    const [isLoadingHandleExportReport, setIsLoadingHandleExportReport] = useState(false);
    const [rootFolderName, setRootFolderName] = useState('');
    const selectStates = useRef<Record<string, boolean>>({});
    const [fileToShare, setFileToShare] = useState<S3File | null>(null);
    const [fileToEditPrivacy, setFileToEditPrivacy] = useState<S3File | null>(null);
    const [isCrawlProgressDialogOpen, setIsCrawlProgressDialogOpen] = useState(false);
    const [outlookEmailsBySource, setOutlookEmailsBySource] = useState<
      Record<string, string | null>
    >({});
    const [outlookDialogOpen, setOutlookDialogOpen] = useState(false);
    const [bootstrapContext, setBootstrapContext] = useState<KnownCrawlerFolder | null>(null);
    const [outlookDialogContext, setOutlookDialogContext] = useState<KnownCrawlerFolder | null>(
      null,
    );
    const [bootstrapId, setBootstrapId] = useState<string | null>(null);
    const [bootstrapInfo, setBootstrapInfo] = useState<{
      userCode?: string;
      verificationUri?: string;
      message?: string;
    } | null>(null);
    const [bootstrapStatus, setBootstrapStatus] = useState<string | null>(null);
    const [bootstrapError, setBootstrapError] = useState<string | null>(null);
    const [bootstrapLoading, setBootstrapLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Email scheduler settings popup state
    const [outlookSettingsOpen, setOutlookSettingsOpen] = useState(false);
    const [emailConfig, setEmailConfig] = useState<Record<string, unknown> | null>(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    const [formCron, setFormCron] = useState('');
    const vnTimeOptions = useMemo(
      () =>
        Array.from({ length: 96 }, (_, index) => {
          const totalMinutes = index * 15;
          const hours = Math.floor(totalMinutes / 60)
            .toString()
            .padStart(2, '0');
          const minutes = (totalMinutes % 60).toString().padStart(2, '0');

          return `${hours}:${minutes}`;
        }),
      [],
    );
    const vnTimeToUtcCron = (vnTime: string): string => {
      const [vnHour = '0', vnMinute = '0'] = vnTime.split(':');
      // const utcHour = (Number(vnHour) - 7 + 24) % 24;
      // đã lưu timezone  nên không cần sửa
      const utcHour = (Number(vnHour) + 24) % 24;

      return `${Number(vnMinute)} ${utcHour} * * *`;
    };
    const utcCronToVnTime = (cron: string): string => {
      const [minute, hour] = cron.trim().split(/\s+/);
      const utcHour = Number(hour);
      const utcMinute = Number(minute);

      if (!Number.isInteger(utcHour) || !Number.isInteger(utcMinute)) return '';
      if (utcHour < 0 || utcHour > 23 || utcMinute < 0 || utcMinute > 59) return '';
      if (utcMinute % 15 !== 0) return '';

      // đã lưu timezone nên ko cần sửa
      // const vnHour = (utcHour + 7) % 24;
      const vnHour = (utcHour + 0) % 24;

      return `${vnHour.toString().padStart(2, '0')}:${utcMinute.toString().padStart(2, '0')}`;
    };
    const formVnTime = utcCronToVnTime(formCron);
    // const [formPrefix, setFormPrefix] = useState('');
    // const [formMaxEmails, setFormMaxEmails] = useState(50);
    // const [formClientId, setFormClientId] = useState('');
    // const [formAuthority, setFormAuthority] = useState('');
    const [formEnabled, setFormEnabled] = useState(false);
    const [formFilters, setFormFilters] = useState<DailyReportSubjectFilters>(
      createEmptyDailyReportSubjectFilters,
    );
    const [formFiltersWeek, setFormFiltersWeek] = useState<DailyReportSubjectFilters>(
      createEmptyDailyReportSubjectFilters,
    );
    const [formFiltersMonth, setFormFiltersMonth] = useState<DailyReportSubjectFilters>(
      createEmptyDailyReportSubjectFilters,
    );
    const [formFiltersQuarter, setFormFiltersQuarter] = useState<DailyReportSubjectFilters>(
      createEmptyDailyReportSubjectFilters,
    );
    const [settingsRunResult, setSettingsRunResult] = useState<string | null>(null);
    const [settingsRunning, setSettingsRunning] = useState(false);
    const [crawlerSourceModalOpen, setCrawlerSourceModalOpen] = useState(false);
    // Modal lịch sử báo cáo: mở ngay trong S3Explorer để chạy được khi package được nhúng
    // vào frontend host (host không có route `/report-history` của app standalone).
    const [reportHistoryOpen, setReportHistoryOpen] = useState(false);
    const [crawlerSource, setCrawlerSource] = useState<EmailCrawlerSource | null>(null);
    const [crawlerCreateDefaults, setCrawlerCreateDefaults] =
      useState<EmailCrawlerSourceCreateDefaults | null>(null);
    const [crawlerBldReports, setCrawlerBldReports] = useState(false);
    const {
      source: resolvedCrawlerSource,
      knownFolder: resolvedCrawlerKnownFolder,
      loading: resolvedCrawlerSourceLoading,
      refresh: refreshResolvedCrawlerSource,
    } = useEmailCrawlerSourceByFolder(apiBaseUrl, currentPrefix);
    const activeCrawlerFolder = resolvedCrawlerKnownFolder;
    const displayedOutlookFolder = outlookDialogContext ?? activeCrawlerFolder;
    const outlookEmail = displayedOutlookFolder
      ? (outlookEmailsBySource[displayedOutlookFolder.sourceKey] ?? null)
      : null;
    const crawlerProcessorType =
      resolvedCrawlerSource?.processorType ?? resolvedCrawlerKnownFolder?.processorType;
    // Thư mục `bld-reports/` là nơi duy nhất mở cấu hình BLD Reports + lịch sử báo cáo.
    const isBldReportsFolder = isBldReportsFolderPrefix(currentPrefix);
    // Nút bánh răng: `bld-reports/` (cấu hình BLD Reports) hoặc các crawler còn lại
    // (vd tuyển dụng). Cấu hình BLD không còn mở từ `kho_ban_lanh_dao/`.
    const showCrawlerSettingsButton =
      isBldReportsFolder ||
      (crawlerProcessorType !== undefined && crawlerProcessorType !== 'email_dashboard');
    const openCrawlerSettingsModal = () => {
      // Chờ resolve xong nguồn đã tồn tại trước khi mở modal, tránh mở nhầm
      // create-mode (gọi POST -> duplicate key) khi source thật ra đã có trong DB.
      if (resolvedCrawlerSourceLoading) return;
      // Dialog cấu hình Outlook bên dưới là legacy (không còn đường mở), giữ lại
      // nguyên trạng để tham chiếu.
      setCrawlerBldReports(isBldReportsFolder);
      if (isBldReportsFolder) {
        setCrawlerSource(null);
        setCrawlerCreateDefaults(null);
      } else if (resolvedCrawlerSource) {
        setCrawlerSource(resolvedCrawlerSource);
        setCrawlerCreateDefaults(null);
      } else if (resolvedCrawlerKnownFolder) {
        setCrawlerSource(null);
        setCrawlerCreateDefaults({
          sourceKey: resolvedCrawlerKnownFolder.sourceKey,
          processorType: resolvedCrawlerKnownFolder.processorType,
          uiFolderPrefix: resolvedCrawlerKnownFolder.prefix,
        });
      }
      setCrawlerSourceModalOpen(true);
    };

    // Upload metadata form states
    const [uploadMetadataForm, setUploadMetadataForm] =
      useState<UploadMetadataForm>(defaultUploadMetadata);
    const [showUploadFormDialog, setShowUploadFormDialog] = useState(false);
    const [pendingUploadInfo, setPendingUploadInfo] = useState<{
      files: FileList;
      prefix: string;
      type: 'files' | 'folder';
    } | null>(null);
    const [uploadFormErrors, setUploadFormErrors] = useState<string[]>([]);
    const [expirationDateError, setExpirationDateError] = useState<string>('');

    // Trạng thái parse PDF -> JSON cho kho kho_chat_cn/quy_dinh_phap_luat/.
    // Map theo S3 key của file PDF gốc. Khôi phục từ localStorage để giữ qua reload.
    const [pdfParseStatus, setPdfParseStatus] = useState<Record<string, PdfParseStatusRecord>>(
      () => {
        try {
          const saved = localStorage.getItem(PDF_PARSE_STATE_KEY);
          if (!saved) return {};
          const parsed = JSON.parse(saved) as {
            statuses?: Record<string, PdfParseStatusRecord>;
          };
          return parsed.statuses || {};
        } catch {
          return {};
        }
      },
    );
    // Các key đang được theo dõi (poll). Khi rỗng thì dừng poll.
    // Khôi phục các key chưa hoàn tất (queued/processing) để tiếp tục poll sau reload.
    const [trackedPdfKeys, setTrackedPdfKeys] = useState<string[]>(() => {
      try {
        const saved = localStorage.getItem(PDF_PARSE_STATE_KEY);
        if (!saved) return [];
        const parsed = JSON.parse(saved) as {
          statuses?: Record<string, PdfParseStatusRecord>;
        };
        return Object.entries(parsed.statuses || {})
          .filter(([, record]) => record.status === 'queued' || record.status === 'processing')
          .map(([key]) => key);
      } catch {
        return [];
      }
    });
    // Lưu status đã toast để tránh toast lặp khi poll nhiều lần.
    const toastedPdfKeysRef = useRef<Record<string, PdfParseStatus>>({});

    const [boChiTieuOcrStatus, setBoChiTieuOcrStatus] = useState<
      Record<string, BoChiTieuOcrStatusRecord>
    >(() => {
      try {
        const saved = localStorage.getItem(BO_CHI_TIEU_OCR_STATE_KEY);
        return saved
          ? (JSON.parse(saved) as { statuses?: Record<string, BoChiTieuOcrStatusRecord> })
              .statuses || {}
          : {};
      } catch {
        return {};
      }
    });
    const [trackedBoChiTieuKeys, setTrackedBoChiTieuKeys] = useState<string[]>(() => {
      try {
        const saved = localStorage.getItem(BO_CHI_TIEU_OCR_STATE_KEY);
        const statuses = saved
          ? (JSON.parse(saved) as { statuses?: Record<string, BoChiTieuOcrStatusRecord> })
              .statuses || {}
          : {};
        return Object.entries(statuses)
          .filter(([, record]) => record.status === 'queued' || record.status === 'processing')
          .map(([key]) => key);
      } catch {
        return [];
      }
    });
    const toastedBoChiTieuKeysRef = useRef<Record<string, BoChiTieuOcrStatus>>({});

    useEffect(() => {
      try {
        localStorage.setItem(
          BO_CHI_TIEU_OCR_STATE_KEY,
          JSON.stringify({ statuses: boChiTieuOcrStatus }),
        );
      } catch {
        /* bỏ qua lỗi quota localStorage */
      }
    }, [boChiTieuOcrStatus]);

    // Lưu trạng thái parse vào localStorage mỗi khi thay đổi để chịu được reload trang.
    useEffect(() => {
      try {
        localStorage.setItem(PDF_PARSE_STATE_KEY, JSON.stringify({ statuses: pdfParseStatus }));
      } catch {
        /* bỏ qua lỗi quota localStorage */
      }
    }, [pdfParseStatus]);

    // Bắt đầu theo dõi tiến trình parse cho danh sách S3 key PDF.
    const trackPdfParseKeys = (keys: string[]) => {
      const legalKeys = keys.filter((key) => isChatCnLegalKey(key) && isPdfName(key));
      if (legalKeys.length === 0) return;

      setPdfParseStatus((prev) => {
        const next = { ...prev };
        for (const key of legalKeys) {
          if (!next[key]) next[key] = { status: 'queued' };
        }
        return next;
      });
      setTrackedPdfKeys((prev) => Array.from(new Set([...prev, ...legalKeys])));
    };

    // Tính S3 key kỳ vọng cho các file PDF vừa upload rồi bắt đầu theo dõi.
    // Key backend dùng là `${prefix}${fileName}` (kho này không bị redirect prefix).
    const trackUploadedPdfParse = (uploadedFiles: FileList | File[], prefix: string) => {
      const normalizedPrefix = `${prefix || ''}`.replace(/^\/+/, '');
      if (!normalizedPrefix.startsWith(CHAT_CN_LEGAL_PREFIX)) return;

      const base = normalizedPrefix.endsWith('/') ? normalizedPrefix : `${normalizedPrefix}/`;
      const keys = Array.from(uploadedFiles)
        .filter((file) => isPdfName(file.name))
        .map((file) => `${base}${file.name}`);

      trackPdfParseKeys(keys);
    };

    // Theo dõi (poll) các key PDF mà KHÔNG giả định trạng thái "queued" ở local.
    // Dùng khi mở kho từ tab/máy khác: trạng thái thật nằm trên server (Redis), nên
    // ta chỉ thêm key vào danh sách poll; UI chỉ hiện spinner nếu server trả về trạng thái.
    const observePdfParseKeys = (keys: string[]) => {
      const legalKeys = keys.filter((key) => isChatCnLegalKey(key) && isPdfName(key));
      if (legalKeys.length === 0) return;
      setTrackedPdfKeys((prev) => {
        const merged = Array.from(new Set([...prev, ...legalKeys]));
        return merged.length === prev.length ? prev : merged;
      });
    };

    const trackBoChiTieuOcrKeys = (keys: string[]) => {
      const imageKeys = keys.filter(isBoChiTieuImageKey);
      if (imageKeys.length === 0) return;

      setBoChiTieuOcrStatus((prev) => {
        const next = { ...prev };
        for (const key of imageKeys) {
          if (!next[key]) next[key] = { status: 'queued' };
        }
        return next;
      });
      setTrackedBoChiTieuKeys((prev) => Array.from(new Set([...prev, ...imageKeys])));
    };

    const trackUploadedBoChiTieuOcr = (uploadedFiles: FileList | File[], prefix: string) => {
      if (!isBoChiTieuPrefix(prefix)) return;
      const normalizedPrefix = `${prefix || ''}`.replace(/^\/+/, '');
      const keys = Array.from(uploadedFiles)
        .filter((file) => isBoChiTieuImageName(file.name))
        .map((file) => `${normalizedPrefix}${file.name}`);
      trackBoChiTieuOcrKeys(keys);
    };

    const observeBoChiTieuOcrKeys = (keys: string[]) => {
      const imageKeys = keys.filter(isBoChiTieuImageKey);
      if (imageKeys.length === 0) return;
      setTrackedBoChiTieuKeys((prev) => {
        const merged = Array.from(new Set([...prev, ...imageKeys]));
        return merged.length === prev.length ? prev : merged;
      });
    };

    // Poll trạng thái OCR ảnh mỗi 3s cho tới khi tất cả key đã xong.
    useEffect(() => {
      if (trackedBoChiTieuKeys.length === 0) return;
      let cancelled = false;

      const poll = async () => {
        try {
          const res = await axios.get(`${apiBaseUrl}/api/ocr/bo-chi-tieu-status`, {
            params: { keys: JSON.stringify(trackedBoChiTieuKeys) },
          });
          if (cancelled) return;
          const statuses = (res.data?.statuses || {}) as Record<string, BoChiTieuOcrStatusRecord>;
          setBoChiTieuOcrStatus((prev) => ({ ...prev, ...statuses }));

          let anyFinished = false;
          for (const key of trackedBoChiTieuKeys) {
            const record = statuses[key];
            if (!record) continue;
            if (
              (record.status === 'succeeded' || record.status === 'failed') &&
              toastedBoChiTieuKeysRef.current[key] !== record.status
            ) {
              toastedBoChiTieuKeysRef.current[key] = record.status;
              anyFinished = true;
              const fileName = key.split('/').pop() || key;
              if (record.status === 'succeeded') {
                toast({
                  title: 'Trích xuất JSON thành công',
                  description: `Đã tạo JSON từ ${fileName}.`,
                });
              } else {
                toast({
                  title: 'Trích xuất JSON thất bại',
                  description: `Không thể trích xuất ${fileName}${record.error ? `: ${record.error}` : ''}.`,
                  variant: 'destructive',
                });
              }
            }
          }
          if (anyFinished) void refreshFiles();

          const remaining = trackedBoChiTieuKeys.filter((key) => {
            const record = statuses[key];
            return !record || (record.status !== 'succeeded' && record.status !== 'failed');
          });
          if (remaining.length !== trackedBoChiTieuKeys.length) setTrackedBoChiTieuKeys(remaining);
        } catch (error) {
          console.error('Error polling bo chi tieu OCR status:', error);
        }
      };

      void poll();
      const interval = setInterval(poll, 3000);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [trackedBoChiTieuKeys, apiBaseUrl]);

    // Poll trạng thái parse mỗi 3s cho tới khi tất cả key đã xong (succeeded/failed).
    useEffect(() => {
      if (trackedPdfKeys.length === 0) return;

      let cancelled = false;

      const poll = async () => {
        try {
          const res = await axios.get(`${apiBaseUrl}/api/ocr/pdf-parse-status`, {
            params: { keys: JSON.stringify(trackedPdfKeys) },
          });
          const statuses = (res.data?.statuses || {}) as Record<string, PdfParseStatusRecord>;
          if (cancelled) return;

          setPdfParseStatus((prev) => mergePdfParseStatuses(prev, statuses));

          // Toast khi có key vừa hoàn tất/lỗi (chỉ toast một lần cho mỗi trạng thái cuối).
          let anyFinished = false;
          for (const key of trackedPdfKeys) {
            const record = statuses[key];
            if (!record) continue;
            if (
              (record.status === 'succeeded' || record.status === 'failed') &&
              toastedPdfKeysRef.current[key] !== record.status
            ) {
              toastedPdfKeysRef.current[key] = record.status;
              anyFinished = true;
              const fileName = key.split('/').pop() || key;
              if (record.status === 'succeeded') {
                toast({
                  title: 'Trích xuất JSON thành công',
                  description: `Đã trích xuất dữ liệu từ ${fileName}.`,
                });
              } else {
                toast({
                  title: 'Trích xuất JSON thất bại',
                  description: `Không thể trích xuất ${fileName}${record.error ? `: ${record.error}` : ''}. Bạn có thể tải lên lại để thử lại.`,
                  variant: 'destructive',
                });
              }
            }
          }

          // Nếu có file JSON mới xuất hiện, làm mới danh sách để hiển thị.
          if (anyFinished) {
            void refreshFiles();
          }

          // Bỏ theo dõi các key đã xong.
          const remaining = trackedPdfKeys.filter((key) => {
            const record = statuses[key];
            return !record || (record.status !== 'succeeded' && record.status !== 'failed');
          });
          if (remaining.length !== trackedPdfKeys.length) {
            setTrackedPdfKeys(remaining);
          }
        } catch (error) {
          console.error('Error polling PDF parse status:', error);
        }
      };

      void poll();
      const interval = setInterval(poll, 3000);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [trackedPdfKeys, apiBaseUrl]);

    // Auto-open crawl progress dialog when job starts
    useEffect(() => {
      if (currentJob && currentJob.status) {
        setIsCrawlProgressDialogOpen(true);
      }
    }, [currentJob?.id]);

    useEffect(() => {
      if (
        !bootstrapId ||
        bootstrapStatus === 'success' ||
        bootstrapStatus === 'error' ||
        bootstrapStatus === 'timeout'
      )
        return;

      const interval = setInterval(() => {
        fetch(`${apiBaseUrl}/api/email-scheduler/bootstrap/${bootstrapId}/status`)
          .then((res) =>
            res.ok
              ? (res.json() as Promise<{
                  status?: string;
                  userCode?: string;
                  verificationUri?: string;
                  message?: string;
                  authenticatedAs?: string;
                  canonicalPrefix?: string;
                  sourceKey?: string;
                  error?: string;
                }>)
              : null,
          )
          .then((data) => {
            if (!data) return;
            if (data.userCode && !bootstrapInfo?.userCode) {
              setBootstrapInfo({
                userCode: data.userCode,
                verificationUri: data.verificationUri,
                message: data.message,
              });
            }
            if (data.status === 'success') {
              if (!bootstrapContext || !matchesBootstrapContext(data, bootstrapContext)) {
                setBootstrapStatus('error');
                setBootstrapError('Authentication context changed. Please start again.');
                return;
              }
              setBootstrapStatus('success');
              setOutlookEmailsBySource((current) => ({
                ...current,
                [bootstrapContext.sourceKey]: data.authenticatedAs ?? null,
              }));
              void refreshResolvedCrawlerSource();
              // Không đóng modal "Cấu hình tuyển dụng" ở đây: đóng modal đúng lúc
              // Outlook vừa kết nối khiến người dùng không kịp thấy nút "Tạo cấu
              // hình" đã được bật (mailboxAddress vừa được đồng bộ từ outlookEmail).
              setTimeout(() => setOutlookDialogOpen(false), 2000);
            } else if (data.status === 'error') {
              setBootstrapStatus('error');
              setBootstrapError(data.error || 'Authentication failed');
            } else if (data.status === 'timeout') {
              setBootstrapStatus('timeout');
            }
          })
          .catch(() => {
            setBootstrapStatus('error');
            setBootstrapError('Unable to poll authentication status. Please try again.');
          });
      }, 3000);

      return () => clearInterval(interval);
    }, [
      bootstrapId,
      bootstrapStatus,
      apiBaseUrl,
      bootstrapInfo?.userCode,
      bootstrapContext,
      refreshResolvedCrawlerSource,
    ]);

    const handleBootstrap = async (folder: KnownCrawlerFolder | null = activeCrawlerFolder) => {
      if (!folder) {
        setBootstrapError('Unsupported crawler folder.');
        return;
      }
      setBootstrapLoading(true);
      setBootstrapStatus(null);
      setBootstrapError(null);
      setBootstrapInfo(null);
      setCopied(false);
      setBootstrapContext(folder);

      try {
        const res = await fetch(
          `${apiBaseUrl}/api/email-scheduler/bootstrap`,
          buildBootstrapRequest(folder),
        );
        if (!res.ok) {
          const err = (await res.json().catch(() => ({ error: 'Failed to start bootstrap' }))) as {
            error?: string;
          };
          setBootstrapError(err.error || 'Failed to start bootstrap');
          setBootstrapStatus('error');
          setBootstrapLoading(false);
          return;
        }
        const data = (await res.json()) as {
          bootstrapId?: string;
          userCode?: string;
          verificationUri?: string;
          message?: string;
          canonicalPrefix?: string;
          sourceKey?: string;
        };
        if (!matchesBootstrapContext(data, folder)) {
          setBootstrapError('Authentication context changed. Please start again.');
          setBootstrapStatus('error');
          setBootstrapLoading(false);
          return;
        }
        setBootstrapId(data.bootstrapId ?? null);
        if (data.userCode) {
          setBootstrapInfo({
            userCode: data.userCode,
            verificationUri: data.verificationUri,
            message: data.message,
          });
        }
        setBootstrapStatus('pending');
      } catch {
        setBootstrapError('Network error');
        setBootstrapStatus('error');
      }
      setBootstrapLoading(false);
    };

    const handleCopyCode = () => {
      if (bootstrapInfo?.userCode) {
        navigator.clipboard.writeText(bootstrapInfo.userCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    const loadEmailConfig = async () => {
      setConfigLoading(true);
      try {
        const res = await fetch(`${apiBaseUrl}/api/system-config?category=email_scheduler`);
        if (!res.ok) throw new Error('Failed');
        const raw = (await res.json()) as Record<string, { value: unknown }>;
        const cfg: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(raw)) cfg[k] = v.value;
        setEmailConfig(cfg);
        setFormCron((cfg['schedule_cron'] as string) ?? '');
        // setFormPrefix((cfg['s3_prefix'] as string) ?? '');
        // setFormMaxEmails((cfg['max_emails_per_run'] as number) ?? 50);
        // setFormClientId((cfg['outlook_client_id'] as string) ?? '');
        // setFormAuthority((cfg['outlook_authority'] as string) ?? '');
        setFormEnabled(!!(cfg['enabled'] as boolean));
        setFormFilters(normalizeDailyReportSubjectFilters(cfg['subject_filters']));
        setFormFiltersWeek(normalizeDailyReportSubjectFilters(cfg['subject_filters_week']));
        setFormFiltersMonth(normalizeDailyReportSubjectFilters(cfg['subject_filters_month']));
        setFormFiltersQuarter(normalizeDailyReportSubjectFilters(cfg['subject_filters_quarter']));
      } catch {
        /* silently fail */
      }
      setConfigLoading(false);
    };

    const saveConfig = async (key: string, value: unknown) => {
      setConfigSaving(true);
      try {
        await fetch(`${apiBaseUrl}/api/system-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: 'email_scheduler', key, value }),
        });
      } catch {
        /* silently fail */
      }
      setConfigSaving(false);
    };

    const renderSubjectFilterInput = (params: {
      id: string;
      label: string;
      value: string;
      placeholder: string;
      onChange: (value: string) => void;
      onBlur: () => void;
    }) => (
      <div className="space-y-1" key={params.id}>
        <Label htmlFor={params.id} className="text-xs text-muted-foreground">
          {params.label}
        </Label>
        <Input
          id={params.id}
          value={params.value}
          onChange={(e) => params.onChange(e.target.value)}
          onBlur={params.onBlur}
          placeholder={params.placeholder}
          disabled={configSaving}
        />
      </div>
    );

    type LastRunResult =
      | { processed: number; skipped: number; errors: number; predictionError?: string }
      | { error: string }
      | null;

    const displayResult = (lastRunResult: LastRunResult) => {
      if (!lastRunResult) {
        setSettingsRunResult('Hoàn thành.');
      } else if ('error' in lastRunResult) {
        setSettingsRunResult(`Lỗi: ${lastRunResult.error}`);
      } else {
        const resultMsg = `Hoàn thành — đã xử lý: ${lastRunResult.processed}, bỏ qua: ${lastRunResult.skipped}, lỗi: ${lastRunResult.errors}`;
        setSettingsRunResult(
          lastRunResult.predictionError
            ? `${resultMsg} (Prediction API lỗi: ${lastRunResult.predictionError})`
            : resultMsg,
        );
      }
      setSettingsRunning(false);
    };

    const startPolling = () => {
      setSettingsRunning(true);
      runNowPollInterval = setInterval(() => {
        fetch(`${apiBaseUrl}/api/email-scheduler/status`)
          .then((res) =>
            res.ok
              ? (res.json() as Promise<{ isRunning: boolean; lastRunResult: LastRunResult }>)
              : Promise.reject(new Error(`Status ${res.status}`)),
          )
          .then((data) => {
            if (!data.isRunning) {
              if (runNowPollInterval !== null) {
                clearInterval(runNowPollInterval);
                runNowPollInterval = null;
              }
              displayResult(data.lastRunResult);
            }
          })
          .catch(() => {
            if (runNowPollInterval !== null) {
              clearInterval(runNowPollInterval);
              runNowPollInterval = null;
            }
            setSettingsRunResult('Lỗi kết nối.');
            setSettingsRunning(false);
          });
      }, 2000);
    };

    const handleRunNow = async () => {
      setSettingsRunResult(null);

      try {
        const res = await fetch(`${apiBaseUrl}/api/email-scheduler/run`, { method: 'POST' });
        if (res.status === 202) {
          startPolling();
        } else if (res.status === 409) {
          setSettingsRunResult('Đang chạy, vui lòng chờ.');
          startPolling();
        } else {
          setSettingsRunResult('Lỗi khi chạy.');
        }
      } catch {
        setSettingsRunResult('Lỗi kết nối.');
      }
    };

    const handleGetInfoFlows = async (folderName: string) => {
      setLoadingFlowsDialogs(true);
      try {
        const accessToken = getAccessTokenInfoFromLocal();
        const dataResponse = await axios.get(
          `${rootUrlHostApi}get-knowledge-base/knowledge-base-name/${folderName}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (dataResponse.status === 200) {
          setDataFlowModels(
            dataResponse.data.knowledgeBase.chatflows.map((item: any) => ({
              ...item,
              KnowledgeBaseId: dataResponse.data.knowledgeBase.knowledge_base_id,
            })),
          );
        }
      } catch (error) {
      } finally {
        setLoadingFlowsDialogs(false);
      }
    };

    useEffect(() => {
      if (folderToShowFlow && rootUrlHostApi) {
        handleGetInfoFlows(folderToShowFlow);
      }
    }, [folderToShowFlow]);

    useEffect(() => {
      console.log('pre selected files', preSelectedFiles);
    }, [preSelectedFiles]);

    const [syncJobStatus, setSyncJobStatus] = useState<SyncJobStatus | null>(null);

    const [_isMsalReady, setIsMsalReady] = useState(false);
    const [_isAuthenticated, setIsAuthenticated] = useState(false);
    const [showOneDriveModal, setShowOneDriveModal] = useState(false);
    const [folders, setFolders] = useState<OneDriveFile[]>([]);
    const [files, setFiles] = useState<OneDriveFile[]>([]);
    const [breadcrumbOneDrive, setBreadcrumbOneDrive] = useState<{ id: string; name: string }[]>([
      { id: 'root', name: 'OneDrive' },
    ]);
    const [isLoadingOneDrive, setIsLoadingOneDrive] = useState(false);
    const [selectedOneDriveItems, setSelectedOneDriveItems] = useState<OneDriveSelectedItem[]>([]);
    const [excludedOneDriveChildIds, setExcludedOneDriveChildIds] = useState<Set<string>>(
      new Set(),
    );
    const [isLoadingSharePoint, setIsLoadingSharePoint] = useState(false);
    const [sharePointFiles, setSharePointFiles] = useState<SharePointFile[]>([]);
    const [sharePointFolders, setSharePointFolders] = useState<SharePointFile[]>([]);
    const [selectedSharePointItems, setSelectedSharePointItems] = useState<
      SharePointSelectedItem[]
    >([]);
    const [excludedSharePointChildIds, setExcludedSharePointChildIds] = useState<Set<string>>(
      new Set(),
    );
    const [sharePointBreadcrumbs, setSharePointBreadcrumbs] = useState<
      { id: string; name: string }[]
    >([{ id: 'root', name: 'SharePoint' }]);
    // Cache for SharePoint folder contents - persists while modal is open
    const [sharePointCache, setSharePointCache] = useState<SharePointFolderCache>(new Map());
    const [isSharePointModalOpen, setIsSharePointModalOpen] = useState(false);
    const [sharePointSites, setSharePointSites] = useState<SharePointSite[]>([]);
    const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
    const [isSharePointLogoutConfirmOpen, setIsSharePointLogoutConfirmOpen] = useState(false);
    const [isOneDriveLogoutConfirmOpen, setIsOneDriveLogoutConfirmOpen] = useState(false);
    const [isAutoUpdateKB, setIsAutoUpdateKB] = useState(false);

    const url = new URL(window.location.href);
    const prefixParam = url.searchParams.get('prefix') || '';
    const effectivePrefix = prefixParam || forcePrefix || rootPrefix;

    const [_token, setToken] = useState<string | null>(null);
    // Metadata validation errors for edit-metadata tab
    const [metadataErrors, setMetadataErrors] = useState<Record<string, string>>({});

    // Validate metadata form fields
    const validateMetadataFields = (metadata: any): Record<string, string> => {
      const errors: Record<string, string> = {};
      if (!metadata || typeof metadata !== 'object') return errors;

      const requiredFields: Record<string, string> = {
        summary: 'Trích yếu là bắt buộc',
        issueDate: 'Ngày ban hành là bắt buộc',
        effectiveDate: 'Ngày hiệu lực là bắt buộc',
        processingStatus: 'Trạng thái xử lý là bắt buộc',
        status: 'Tình trạng là bắt buộc',
      };

      for (const [field, message] of Object.entries(requiredFields)) {
        const value = metadata[field];
        if (
          value === '' ||
          value === null ||
          value === undefined ||
          (typeof value === 'string' && !value.trim())
        ) {
          errors[field] = message;
        }
      }

      return errors;
    };
    const {
      data: fileSystem,
      isLoading,
      error,
      estimatedTotalHits,
      pagination,
      viewOnly: isViewOnly,
      refetch: refreshFiles,
    } = useFiles(
      apiBaseUrl,
      effectivePrefix,
      displayPrefixes,
      searchQuery,
      currentPage,
      browsePage,
      setSearchQuery,
      idCreator,
      selectedKeys,
      selectStates,
      setSelectedKeys,
    );

    setViewOnlyMode(!!isViewOnly);

    // Cross-browser: hỏi trạng thái OCR của các file đang hiển thị từ server (Redis là nguồn thật).
    useEffect(() => {
      const pdfKeys: string[] = [];
      const boChiTieuKeys: string[] = [];
      for (const files of Object.values(fileSystem || {})) {
        for (const file of files || []) {
          if (!file.isFolder && isChatCnLegalKey(file.key) && isPdfName(file.name)) {
            pdfKeys.push(file.key);
          }
          if (!file.isFolder && isBoChiTieuImageKey(file.key)) {
            boChiTieuKeys.push(file.key);
          }
        }
      }
      if (isChatCnLegalKey(effectivePrefix) && pdfKeys.length > 0) observePdfParseKeys(pdfKeys);
      if (isBoChiTieuPrefix(effectivePrefix) && boChiTieuKeys.length > 0) {
        observeBoChiTieuOcrKeys(boChiTieuKeys);
      }
    }, [fileSystem, effectivePrefix]);

    const [showAddFileMenu, setShowAddFileMenu] = useState(false);
    const addFileMenuRef = useRef<HTMLDivElement>(null);
    const currentUser = getUserInfoFromLocal();

    const user: User | null = (() => {
      const dataLogin = localStorage.getItem('dataLogin');
      try {
        if (dataLogin) {
          const parsed: any = JSON.parse(dataLogin);
          return parsed?.user ?? null;
        }
      } catch {
        return null;
      }
      return null;
    })();

    useEffect(() => {
      if (!showAddFileMenu) return;
      function handleClickOutside(event: MouseEvent) {
        if (addFileMenuRef.current && !addFileMenuRef.current.contains(event.target as Node)) {
          setShowAddFileMenu(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showAddFileMenu]);

    const loginSharePoint = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/login-sharepoint`);
        const { loginUrl } = response.data;

        const popup = window.open(loginUrl, 'SharePoint Login', 'width=600,height=600');
        if (!popup) throw new Error('Không thể mở cửa sổ pop-up');
        popup.focus();

        const interval = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(interval);
              return;
            }

            const currentUrl = popup.location.href;
            if (currentUrl.includes('code=')) {
              const authorizationCode = new URL(currentUrl).searchParams.get('code');
              if (authorizationCode) {
                fetchSharePointAccessToken(authorizationCode); // Hàm này nên lưu access token vào localStorage
                popup.close();
                clearInterval(interval);
              }
            }
          } catch (err: unknown) {
            if (err instanceof Error && !err.message.includes('cross-origin')) {
              console.error('SharePoint login error:', err);
            }
          }
        }, 1000);
      } catch (err) {
        hotToast.error(
          'Lỗi khi đăng nhập SharePoint: ' +
            (err instanceof Error ? err.message : 'Lỗi không xác định'),
        );
      }
    };

    const logoutSharePoint = async () => {
      try {
        setSharePointFiles([]);
        setSharePointFolders([]);
        setSelectedSharePointItems([]);
        setExcludedSharePointChildIds(new Set());
        setSharePointBreadcrumbs([{ id: 'root', name: 'SharePoint' }]);
        setSharePointCache(new Map());
        window.location.href =
          'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=' +
          encodeURIComponent(window.location.origin);
        toast({ title: 'Success', description: 'Logged out from SharePoint' });
      } catch (error) {
        console.error('Error logging out from SharePoint:', error);
        toast({
          title: 'Error',
          description: 'Failed to logout from SharePoint',
          variant: 'destructive',
        });
      }
    };

    const fetchSharePointAccessToken = async (authorizationCode: string) => {
      try {
        const response = await axios.post(`${apiBaseUrl}/api/get-sharepoint-access-token`, {
          code: authorizationCode,
          userId: '1',
        });

        const { accessToken, refreshToken } = response.data;

        localStorage.setItem('sharepoint_access_token', accessToken);
        localStorage.setItem('refresh_sharepoint_access_token', refreshToken);
        setIsAuthenticated(true);
        setIsSharePointModalOpen(true);
      } catch (error) {
        console.error('❌ Error fetching SharePoint access token:', error);
        toast({
          title: 'Error',
          description: 'Failed to connect to SharePoint',
          variant: 'destructive',
        });
      }
    };

    const fetchSharePointSites = async () => {
      try {
        setIsLoadingSharePoint(true);
        const response = await axios.post(`${apiBaseUrl}/api/list-site-sharepoint`, {
          userId: '1',
        });
        const data = response.data.sites.map((item: any): SharePointSite => {
          const url = new URL(item.webUrl);
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            hostname: item.hostname || url.hostname,
            sitePath: item.sitePath || url.pathname.replace(/^\/+/, ''),
          };
        });
        console.log('data', data);
        setSharePointSites(data);
        setIsLoadingSharePoint(false);
      } catch (error) {
        console.error('❌ Error fetching SharePoint sites:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch SharePoint sites',
          variant: 'destructive',
        });
        setIsLoadingSharePoint(false);
      }
    };

    const handleSelectSite = async (site: SharePointSite) => {
      setSelectedSite(site);
      console.log(site);
      await fetchSharePointFiles('root', site.name, site.hostname || '', site.sitePath || '');
    };

    const fetchSharePointFiles = async (
      folderId: string,
      folderName: string,
      hostname: string,
      sitePath: string,
    ) => {
      try {
        setIsLoadingSharePoint(true);

        const response = await axios.post(`${apiBaseUrl}/api/list-file-sharepoint`, {
          folderId,
          userId: '1',
          hostname,
          sitePath,
        });

        const data = response.data.files.map((item: any): SharePointFile => ({
          id: item.id,
          name: item.name,
          size: item.size ?? 0,
          lastModified: item.lastModifiedDateTime
            ? new Date(item.lastModifiedDateTime)
            : new Date(),
          contentType: item.mimeType ?? undefined,
          isFolder: item.type === 'folder',
          parentId: item.parentId ?? '',
        }));

        // Populate cache with folder contents
        const cacheItems: SharePointCacheItem[] = data.map((item: SharePointFile) => ({
          id: item.id,
          name: item.name,
          isFolder: item.isFolder,
          parentId: item.parentId ?? '',
        }));
        setSharePointCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(folderId, cacheItems);
          return newCache;
        });

        setSharePointFolders(data.filter((item: SharePointFile) => item.isFolder));
        setSharePointFiles(data.filter((item: SharePointFile) => !item.isFolder));

        setIsSharePointModalOpen(true);

        setSharePointBreadcrumbs((prev) => {
          if (prev.some((b) => b.id === folderId)) return prev;
          return [
            ...prev,
            { id: folderId, name: folderName === 'root' ? 'SharePoint' : folderName },
          ];
        });

        setIsLoadingSharePoint(false);
      } catch (error) {
        console.error('❌ Error fetching SharePoint files:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch SharePoint files',
          variant: 'destructive',
        });
        setIsLoadingSharePoint(false);
      }
    };

    // Helper function to check if any ancestor folder is selected (SharePoint)
    // Uses breadcrumbs to determine the ancestor chain for items in current view
    const isSharePointAncestorSelected = (
      _item: SharePointFile,
      selectedItems: SharePointSelectedItem[],
      breadcrumbs: { id: string; name: string }[],
    ): boolean => {
      const currentAncestorIds = breadcrumbs.slice(1).map((b) => b.id);
      return selectedItems.some((selected) => {
        if (!selected.isFolder) return false;
        return currentAncestorIds.includes(selected.id);
      });
    };

    // Get all known descendants of a folder from cache (recursive)
    const getKnownDescendantIds = (
      folderId: string,
      cache: SharePointFolderCache,
      visited: Set<string> = new Set(),
    ): Set<string> => {
      if (visited.has(folderId)) return new Set();
      visited.add(folderId);

      const descendants = new Set<string>();
      const children = cache.get(folderId);
      if (!children) return descendants;

      for (const child of children) {
        descendants.add(child.id);
        if (child.isFolder) {
          const subDescendants = getKnownDescendantIds(child.id, cache, visited);
          subDescendants.forEach((id) => descendants.add(id));
        }
      }
      return descendants;
    };

    // Check if a folder has any excluded descendants based on cache
    const hasExcludedDescendantsInCache = (
      folderId: string,
      excludedIds: Set<string>,
      cache: SharePointFolderCache,
    ): boolean => {
      const descendants = getKnownDescendantIds(folderId, cache);
      return Array.from(excludedIds).some((id) => descendants.has(id));
    };

    // Check if all known descendants of a folder are excluded
    const areAllKnownDescendantsExcluded = (
      folderId: string,
      excludedIds: Set<string>,
      selectedItems: SharePointSelectedItem[],
      cache: SharePointFolderCache,
    ): boolean => {
      const children = cache.get(folderId);
      if (!children || children.length === 0) return false;

      for (const child of children) {
        const isChildExcluded = excludedIds.has(child.id);
        const isChildDirectlySelected = selectedItems.some((s) => s.id === child.id);

        if (isChildDirectlySelected) {
          // Child is directly selected, so parent is not fully excluded
          return false;
        }

        if (!isChildExcluded) {
          // Child is not excluded
          if (child.isFolder) {
            // For folders, check if all their known descendants are excluded
            const allSubExcluded = areAllKnownDescendantsExcluded(
              child.id,
              excludedIds,
              selectedItems,
              cache,
            );
            if (!allSubExcluded) return false;
          } else {
            // File is not excluded
            return false;
          }
        }
      }
      return true;
    };

    // Get selection state for SharePoint item: true, false, or 'indeterminate'
    const getSharePointItemSelectionState = (
      item: SharePointFile,
      selectedItems: SharePointSelectedItem[],
      excludedIds: Set<string>,
      breadcrumbs: { id: string; name: string }[],
      cache: SharePointFolderCache,
    ): boolean | 'indeterminate' => {
      const isDirectlySelected = selectedItems.some((s) => s.id === item.id);
      const ancestorSelected = isSharePointAncestorSelected(item, selectedItems, breadcrumbs);
      const isExcluded = excludedIds.has(item.id);

      // If directly selected folder, check if it has any excluded descendants using cache
      if (isDirectlySelected && item.isFolder) {
        const hasExcluded = hasExcludedDescendantsInCache(item.id, excludedIds, cache);
        if (hasExcluded) return 'indeterminate';
        return true;
      }

      // Directly selected (non-folder)
      if (isDirectlySelected) return true;

      // Ancestor selected and not excluded
      if (ancestorSelected && !isExcluded) return true;

      // Excluded under selected ancestor
      if (ancestorSelected && isExcluded) return false;

      return false;
    };

    // Auto-uncheck propagation: when all known descendants are excluded, remove folder from selection
    // and propagate upward through ancestors
    const propagateAutoUncheck = (
      newExcludedIds: Set<string>,
      currentSelectedItems: SharePointSelectedItem[],
      cache: SharePointFolderCache,
    ): { updatedSelected: SharePointSelectedItem[]; updatedExcluded: Set<string> } => {
      let updatedSelected = [...currentSelectedItems];
      let updatedExcluded = new Set(newExcludedIds);
      let changed = true;

      while (changed) {
        changed = false;
        for (const selected of updatedSelected) {
          if (!selected.isFolder) continue;

          const allExcluded = areAllKnownDescendantsExcluded(
            selected.id,
            updatedExcluded,
            updatedSelected,
            cache,
          );

          if (allExcluded) {
            // Remove this folder from selection
            updatedSelected = updatedSelected.filter((s) => s.id !== selected.id);
            // Clean up exclusions for descendants of this folder
            const descendants = getKnownDescendantIds(selected.id, cache);
            descendants.forEach((id) => updatedExcluded.delete(id));
            changed = true;
            break;
          }
        }
      }

      return { updatedSelected, updatedExcluded };
    };

    const handleSharePointSelectItem = (item: SharePointFile) => {
      const isDirectlySelected = selectedSharePointItems.some(
        (selected) => selected.id === item.id,
      );
      const ancestorIsSelected = isSharePointAncestorSelected(
        item,
        selectedSharePointItems,
        sharePointBreadcrumbs,
      );
      const isExcluded = excludedSharePointChildIds.has(item.id);

      if (isDirectlySelected) {
        // Unselect the directly selected item
        setSelectedSharePointItems((prevItems) =>
          prevItems.filter((selected) => selected.id !== item.id),
        );
        // If this was a folder, clean up exclusions for its descendants using cache
        if (item.isFolder) {
          const descendants = getKnownDescendantIds(item.id, sharePointCache);
          setExcludedSharePointChildIds((prev) => {
            const newSet = new Set(prev);
            descendants.forEach((id) => newSet.delete(id));
            return newSet;
          });
        }
      } else if (ancestorIsSelected && !isExcluded) {
        // Ancestor is selected and this item is not excluded, so exclude it
        const newExcludedIds = new Set(excludedSharePointChildIds).add(item.id);

        // Use cache-aware auto-uncheck propagation
        const { updatedSelected, updatedExcluded } = propagateAutoUncheck(
          newExcludedIds,
          selectedSharePointItems,
          sharePointCache,
        );

        setSelectedSharePointItems(updatedSelected);
        setExcludedSharePointChildIds(updatedExcluded);
      } else if (ancestorIsSelected && isExcluded) {
        // Ancestor is selected and this item was excluded, so remove exclusion
        setExcludedSharePointChildIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      } else {
        // Not selected and no ancestor selected, so select it
        const currentAncestorIds = sharePointBreadcrumbs.slice(1).map((b) => b.id);

        setSelectedSharePointItems((prevItems) => {
          if (prevItems.some((selected) => selected.id === item.id)) {
            return prevItems;
          }
          return [
            ...prevItems,
            {
              id: item.id,
              name: item.name,
              size: item.size,
              lastModified: item.lastModified,
              contentType: item.contentType,
              isFolder: item.isFolder,
              parentId: item.parentId,
              ancestorIds: currentAncestorIds,
            },
          ];
        });

        // If selecting a folder, remove explicitly selected descendants and clear their exclusions
        if (item.isFolder) {
          setSelectedSharePointItems((prevItems) =>
            prevItems.filter((selected) => {
              if (selected.id === item.id) return true;
              return !selected.ancestorIds.includes(item.id);
            }),
          );
          // Clear exclusions for all known descendants of this folder
          const descendants = getKnownDescendantIds(item.id, sharePointCache);
          setExcludedSharePointChildIds((prev) => {
            const newSet = new Set(prev);
            descendants.forEach((id) => newSet.delete(id));
            return newSet;
          });
        }
      }
    };

    const handleSharePointFolderNavigation = (index: number) => {
      setSharePointBreadcrumbs((prev) => prev.slice(0, index + 1));
      const folderId = sharePointBreadcrumbs[index]?.id;
      if (folderId) {
        fetchSharePointFiles(
          folderId,
          'SharePoint',
          selectedSite?.hostname || '',
          selectedSite?.sitePath || '',
        );
      }
    };

    const syncSharePointToS3 = async (
      selectedItems: SharePointSelectedItem[],
      prefix: string,
      excludedIds: Set<string>,
    ): Promise<void> => {
      const accessToken = localStorage.getItem('sharepoint_access_token');
      if (!accessToken) {
        hotToast.error('Không tìm thấy access token. Vui lòng đăng nhập lại.');
        return;
      }

      const toastId = hotToast.loading('Đang đồng bộ SharePoint...', {
        position: 'bottom-right',
        autoClose: false,
      });

      try {
        const payload = {
          prefix,
          folders: [] as { id: string; name: string }[],
          files: [] as { id: string; name: string }[],
          hostname: selectedSite?.hostname || '',
          sitePath: selectedSite?.sitePath || '',
          isAutoUpdateKB,
          refreshAccessToken: localStorage.getItem('refresh_sharepoint_access_token') || '',
          excludedIds: Array.from(excludedIds),
        };

        // Deduplicate: collect all selected folder IDs first
        const selectedFolderIds = new Set(
          selectedItems.filter((item) => item.isFolder).map((item) => item.id),
        );
        const addedIds = new Set<string>();

        selectedItems.forEach((item) => {
          // Skip if already added (prevent duplicates)
          if (addedIds.has(item.id)) return;

          // Skip if this item's ancestor is already selected (it will be synced as part of parent)
          const hasSelectedAncestor = item.ancestorIds.some((ancestorId) =>
            selectedFolderIds.has(ancestorId),
          );
          if (hasSelectedAncestor) return;

          addedIds.add(item.id);
          if (item.isFolder) {
            payload.folders.push({ id: item.id, name: item.name });
          } else {
            payload.files.push({ id: item.id, name: item.name });
          }
        });

        const response = await axios.post(`${apiBaseUrl}/api/sync-sharepoint-to-s3`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        void refreshFiles();
        hotToast.update(toastId, {
          render: `Đồng bộ thành công! Đã tải lên ${response.data.uploadedKeys.length} file.`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });

        setIsSharePointModalOpen(false);
      } catch (error) {
        console.error('Lỗi khi đồng bộ file từ SharePoint:', error);
        hotToast.update(toastId, {
          render: 'Không thể đồng bộ file từ SharePoint.',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });
      }
    };

    const handleCloseSharePointModal = () => {
      setIsSharePointModalOpen(false);
      setSharePointFiles([]);
      setSharePointFolders([]);
      setSelectedSharePointItems([]);
      setExcludedSharePointChildIds(new Set());
      setSharePointBreadcrumbs([{ id: 'root', name: 'SharePoint' }]);
      setSharePointCache(new Map());
    };

    useEffect(() => {
      if (isSharePointModalOpen) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
          fetchSharePointAccessToken(code);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    }, [isSharePointModalOpen]);

    useEffect(() => {
      msalInstance
        .initialize()
        .then(() => {
          setIsMsalReady(true);
        })
        .catch((error: unknown) => console.error('❌ Lỗi khởi tạo MSAL:', error));
    }, []);

    useEffect(() => {
      checkLoginStatus();
    }, []);

    async function checkLoginStatus() {
      const token = localStorage.getItem('onedrive_access_token');
      if (token) {
        setIsAuthenticated(true);
      }
    }

    // Helper function to check if any ancestor folder is selected (OneDrive)
    const isOneDriveAncestorSelected = (
      _item: OneDriveFile,
      selectedItems: OneDriveSelectedItem[],
      breadcrumbs: { id: string; name: string }[],
    ): boolean => {
      const currentAncestorIds = breadcrumbs.slice(1).map((b) => b.id);
      return selectedItems.some((selected) => {
        if (!selected.isFolder) return false;
        return currentAncestorIds.includes(selected.id);
      });
    };

    // Helper function to find which selected ancestor folder contains the current view (OneDrive)
    const findOneDriveSelectedAncestor = (
      selectedItems: OneDriveSelectedItem[],
      breadcrumbs: { id: string; name: string }[],
    ): OneDriveSelectedItem | null => {
      const currentAncestorIds = breadcrumbs.slice(1).map((b) => b.id);
      for (let i = currentAncestorIds.length - 1; i >= 0; i--) {
        const ancestorId = currentAncestorIds[i];
        const selectedAncestor = selectedItems.find((s) => s.isFolder && s.id === ancestorId);
        if (selectedAncestor) return selectedAncestor;
      }
      return null;
    };

    // Get selection state for OneDrive item: true, false, or 'indeterminate'
    const getOneDriveItemSelectionState = (
      item: OneDriveFile,
      selectedItems: OneDriveSelectedItem[],
      excludedIds: Set<string>,
      breadcrumbs: { id: string; name: string }[],
    ): boolean | 'indeterminate' => {
      const isDirectlySelected = selectedItems.some((s) => s.id === item.id);
      const ancestorSelected = isOneDriveAncestorSelected(item, selectedItems, breadcrumbs);
      const isExcluded = excludedIds.has(item.id);

      if (isDirectlySelected && item.isFolder) {
        const hasExcludedDescendants = Array.from(excludedIds).some((excludedId) => {
          const excludedItem = selectedItems.find((s) => s.id === excludedId);
          if (excludedItem) {
            return excludedItem.ancestorIds.includes(item.id);
          }
          return breadcrumbs.slice(1).some((b) => b.id === item.id);
        });
        if (hasExcludedDescendants) return 'indeterminate';
        return true;
      }

      if (isDirectlySelected) return true;
      if (ancestorSelected && !isExcluded) return true;
      if (ancestorSelected && isExcluded) return false;

      return false;
    };

    const handleOneDriveSelectItem = (item: OneDriveFile) => {
      const isDirectlySelected = selectedOneDriveItems.some((selected) => selected.id === item.id);
      const ancestorIsSelected = isOneDriveAncestorSelected(
        item,
        selectedOneDriveItems,
        breadcrumbOneDrive,
      );
      const isExcluded = excludedOneDriveChildIds.has(item.id);
      const allVisibleItems = [...folders, ...files];

      if (isDirectlySelected) {
        setSelectedOneDriveItems((prevItems) =>
          prevItems.filter((selected) => selected.id !== item.id),
        );
        if (item.isFolder) {
          setExcludedOneDriveChildIds((prev) => {
            const newSet = new Set(prev);
            prev.forEach((excludedId) => {
              allVisibleItems.forEach((child) => {
                if (child.id === excludedId && child.parentId === item.id) {
                  newSet.delete(excludedId);
                }
              });
            });
            return newSet;
          });
        }
      } else if (ancestorIsSelected && !isExcluded) {
        const newExcludedIds = new Set(excludedOneDriveChildIds).add(item.id);

        const selectedAncestor = findOneDriveSelectedAncestor(
          selectedOneDriveItems,
          breadcrumbOneDrive,
        );
        if (selectedAncestor) {
          const allVisibleExcluded = allVisibleItems.every((visibleItem) =>
            newExcludedIds.has(visibleItem.id),
          );

          if (allVisibleExcluded && allVisibleItems.length > 0) {
            setSelectedOneDriveItems((prevItems) =>
              prevItems.filter((s) => s.id !== selectedAncestor.id),
            );
            setExcludedOneDriveChildIds((prev) => {
              const newSet = new Set(prev);
              allVisibleItems.forEach((child) => newSet.delete(child.id));
              return newSet;
            });
          } else {
            setExcludedOneDriveChildIds(newExcludedIds);
          }
        } else {
          setExcludedOneDriveChildIds(newExcludedIds);
        }
      } else if (ancestorIsSelected && isExcluded) {
        setExcludedOneDriveChildIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      } else {
        const currentAncestorIds = breadcrumbOneDrive.slice(1).map((b) => b.id);

        setSelectedOneDriveItems((prevItems) => {
          if (prevItems.some((selected) => selected.id === item.id)) {
            return prevItems;
          }
          return [
            ...prevItems,
            {
              id: item.id,
              name: item.name,
              size: item.size,
              lastModified: item.lastModified,
              contentType: item.contentType,
              isFolder: item.isFolder,
              parentId: item.parentId,
              ancestorIds: currentAncestorIds,
            },
          ];
        });

        if (item.isFolder) {
          setSelectedOneDriveItems((prevItems) =>
            prevItems.filter((selected) => {
              if (selected.id === item.id) return true;
              return !selected.ancestorIds.includes(item.id);
            }),
          );
          setExcludedOneDriveChildIds((prev) => {
            const newSet = new Set(prev);
            allVisibleItems.forEach((child) => {
              if (child.parentId === item.id) {
                newSet.delete(child.id);
              }
            });
            return newSet;
          });
        }
      }
    };

    function formatBytes(bytes: number): string {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    function formatDate(isoString: string): string {
      const date = new Date(isoString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }

    const loginOneDrive = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/login-one-drive`);
        const { loginUrl } = response.data;

        const popup = window.open(loginUrl, 'OneDrive Login', 'width=600,height=600');
        if (!popup) throw new Error('Không thể mở cửa sổ pop-up');
        popup.focus();

        const interval = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(interval);
              return;
            }
            const currentUrl = popup.location.href;
            if (currentUrl.includes('code=')) {
              const authorizationCode = new URL(currentUrl).searchParams.get('code');
              if (authorizationCode) {
                fetchAccessToken(authorizationCode);
                popup.close();
                clearInterval(interval);
              }
            }
          } catch (err: unknown) {
            // Ignore cross-origin errors
            if (err instanceof Error && !err.message.includes('cross-origin')) {
              console.error('OneDrive login error:', err);
            }
          }
        }, 1000);
      } catch (err) {
        hotToast.error(
          'Lỗi khi đăng nhập OneDrive: ' +
            (err instanceof Error ? err.message : 'Lỗi không xác định'),
        );
      }
    };

    const logoutOneDrive = async () => {
      try {
        await axios.post(`${apiBaseUrl}/api/logout-one-drive`);
        localStorage.removeItem('onedrive_access_token');
        localStorage.removeItem('onedrive_refresh_access_token');

        hotToast.success('Đã đăng xuất khỏi OneDrive');

        window.location.href =
          'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=' +
          encodeURIComponent(window.location.origin);
      } catch (err) {
        hotToast.error(
          'Lỗi khi đăng xuất OneDrive: ' +
            (err instanceof Error ? err.message : 'Lỗi không xác định'),
        );
      }
    };

    const fetchAccessToken = async (authorizationCode: string) => {
      try {
        const userId = user?.id;
        const response = await axios.post(`${apiBaseUrl}/api/get-one-drive-access-token`, {
          authorizationCode,
          userId,
        });

        const { accessToken, refreshToken } = response.data;

        localStorage.setItem('onedrive_access_token', accessToken);
        localStorage.setItem('onedrive_refresh_access_token', refreshToken);
        setIsAuthenticated(true);
        setShowOneDriveModal(true);
        await fetchOneDriveFiles('root', 'One Drive');
      } catch (error) {
        console.error('❌ Lỗi khi lấy Access Token:', error);
      }
    };

    const fetchOneDriveFiles = async (folderId: string, folderName: string) => {
      try {
        setIsLoadingOneDrive(true);
        const userId = user?.id;
        const response = await axios.post(`${apiBaseUrl}/api/list-file-one-drive`, {
          folderId,
          userId,
        });

        const data = response.data.map((item: any): OneDriveFile => ({
          id: item.id,
          name: item.name,
          size: item.size ?? 0,
          lastModified: item.lastModifiedDateTime
            ? new Date(item.lastModifiedDateTime)
            : new Date(),
          contentType: item.mimeType ?? undefined,
          isFolder: item.type === 'folder',
          parentId: item.parentId ?? '',
        }));

        setFolders(data.filter((item: OneDriveFile) => item.isFolder));
        setFiles(data.filter((item: OneDriveFile) => !item.isFolder));

        setShowOneDriveModal(true);
        setBreadcrumbOneDrive((prev) => {
          if (prev.some((b) => b.id === folderId)) return prev;
          return [...prev, { id: folderId, name: folderName === 'root' ? 'OneDrive' : folderName }];
        });

        setIsLoadingOneDrive(false);
      } catch (error) {
        console.error('Lỗi lấy danh sách file:', error);
        setIsLoadingOneDrive(false);
      }
    };

    const handleOneDriveFolderNavigation = (index: number) => {
      if (index === breadcrumbOneDrive.length - 1) return;

      const targetFolder = breadcrumbOneDrive[index];
      if (!targetFolder) return;

      setBreadcrumbOneDrive(breadcrumbOneDrive.slice(0, index + 1));
      fetchOneDriveFiles(targetFolder.id, targetFolder.name);
    };

    const syncOneDriveToS3 = async (
      selectedItems: OneDriveSelectedItem[],
      prefix: string,
      excludedIds: Set<string>,
    ): Promise<void> => {
      const accessToken = localStorage.getItem('onedrive_access_token');
      const refreshAccessToken = localStorage.getItem('onedrive_refresh_access_token');
      if (!accessToken) {
        hotToast.error('Không tìm thấy access token. Vui lòng đăng nhập lại.');
        return;
      }

      const toastId = hotToast.loading('Đang đồng bộ OneDrive...', {
        position: 'bottom-right',
        autoClose: false,
      });

      try {
        const payload = {
          prefix,
          folders: [] as { id: string; name: string }[],
          files: [] as { id: string; name: string }[],
          isAutoUpdateKB: isAutoUpdateKB,
          refreshAccessToken: refreshAccessToken,
          excludedIds: Array.from(excludedIds),
        };

        // Deduplicate: collect all selected folder IDs first
        const selectedFolderIds = new Set(
          selectedItems.filter((item) => item.isFolder).map((item) => item.id),
        );
        const addedIds = new Set<string>();

        selectedItems.forEach((item) => {
          // Skip if already added (prevent duplicates)
          if (addedIds.has(item.id)) return;

          // Skip if this item's ancestor is already selected (it will be synced as part of parent)
          const hasSelectedAncestor = item.ancestorIds.some((ancestorId) =>
            selectedFolderIds.has(ancestorId),
          );
          if (hasSelectedAncestor) return;

          addedIds.add(item.id);
          if (item.isFolder) {
            payload.folders.push({ id: item.id, name: item.name });
          } else {
            payload.files.push({ id: item.id, name: item.name });
          }
        });

        const response = await axios.post(`${apiBaseUrl}/api/sync-one-drive-to-s3`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        void refreshFiles();
        hotToast.update(toastId, {
          render: `Đồng bộ thành công! Đã tải lên ${response.data.uploadedKeys.length} file.`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });
      } catch (error) {
        console.error('Lỗi khi đồng bộ file từ OneDrive:', error);

        hotToast.update(toastId, {
          render: 'Không thể đồng bộ file từ OneDrive.',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });
      }
    };

    const handleCloseOneDriveModal = () => {
      setShowOneDriveModal(false);
      setBreadcrumbOneDrive([{ id: 'root', name: 'OneDrive' }]);
      setFolders([]);
      setFiles([]);
      setSelectedOneDriveItems([]);
      setExcludedOneDriveChildIds(new Set());
      setIsLoadingOneDrive(false);
    };

    const login = useGoogleLogin({
      onSuccess: (tokenResponse) => {
        setToken(tokenResponse.access_token);
        localStorage.setItem('google_access_token', tokenResponse.access_token);
        loadGooglePicker(tokenResponse.access_token);
      },
      onError: () => {
        console.log('Đăng nhập thất bại!');
      },
      scope: SCOPES,
    });

    const loadGooglePicker = (accessToken: string) => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('picker', () => initializePicker(accessToken));
      };
      document.body.appendChild(script);
    };

    const initializePicker = (accessToken: string) => {
      const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setOwnedByMe(false);

      const driveView = new window.google.picker.DocsView(window.google.picker.ViewId.DRIVE)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true);

      const picker = new window.google.picker.PickerBuilder()
        .setDeveloperKey(PICKER_BUILDER_DEVELOPER_KEY)
        .setOAuthToken(accessToken)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED) // Chọn nhiều file
        .setLocale('vi')
        .addView(docsView)
        .addView(driveView)
        .setCallback(async (data: any) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const selectedFiles = data.docs.map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
            }));

            await syncDriveToS3(selectedFiles, currentPrefix);
          }
        })
        .build();

      picker.setVisible(true);
    };

    const syncDriveToS3 = async (selectedFiles: any[], prefix = '') => {
      const accessToken = localStorage.getItem('google_access_token');

      if (!accessToken) {
        console.error('❌ Không tìm thấy access token. Vui lòng đăng nhập lại.');
        hotToast.error('Không tìm thấy access token. Vui lòng đăng nhập lại.', {
          position: 'bottom-right',
        });
        return;
      }

      const toastId = hotToast.loading('Đang đồng bộ...', {
        position: 'bottom-right',
        autoClose: false,
      });

      try {
        const payload = {
          prefix: prefix, // ✅ Thêm prefix vào payload
          folders: [] as { id: string; name: string }[],
          files: [] as { id: string; name: string }[],
        };

        selectedFiles.forEach((file) => {
          if (file.mimeType === 'application/vnd.google-apps.folder') {
            payload.folders.push({ id: file.id, name: file.name });
          } else {
            payload.files.push({ id: file.id, name: file.name });
          }
        });

        const response = await axios.post(`${apiBaseUrl}/api/sync-drive-to-s3`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        ignoreCache.current = true;
        void refreshFiles();

        hotToast.update(toastId, {
          render: `Đồng bộ thành công! Đã tải lên ${response.data.uploadedKeys.length} file.`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });
      } catch (error) {
        console.error('❌ Lỗi khi đồng bộ file từ Google Drive:', error);

        hotToast.update(toastId, {
          render: 'Không thể đồng bộ file từ Google Drive.',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          position: 'bottom-right',
        });
      }
    };

    useEffect(() => {
      const handleLogoutMessage = (event: any) => {
        if (event.data.action === 'logout') {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = 'https://accounts.google.com/Logout';
          document.body.appendChild(iframe);

          setTimeout(() => document.body.removeChild(iframe), 2000);
        }
      };

      window.addEventListener('message', handleLogoutMessage);
      return () => window.removeEventListener('message', handleLogoutMessage);
    }, []);

    useEffect(() => {
      const newSet = new Set(preSelectedFiles);
      setSelectedKeys(newSet);
    }, []);

    // Effect to show notifications for completed/failed/blocked jobs
    useEffect(() => {
      if (currentJob?.status) {
        if (currentJob.status.status === 'completed') {
          // Check if S3 upload is still processing
          if (currentJob.status.isProcessing) {
            toast({
              title: 'Đang xử lý dữ liệu thu thập...',
              description: `Đã thu thập xong ${currentJob.status.completed} trang từ ${currentJob.url}. Đang lưu dữ liệu...`,
            });
            // Do NOT refresh files yet - continue polling
          } else {
            // S3 upload is complete, show completion and refresh
            toast({
              title: 'Thu thập website hoàn tất',
              description: `Đã thu thập thành công ${currentJob.url} với ${currentJob.status.completed} trang`,
            });
            // Refresh files to show new content
            ignoreCache.current = true;
            void refreshFiles();
          }
        } else if (currentJob.status.status === 'failed') {
          toast({
            title: 'Thu thập website thất bại',
            description: `Không thể thu thập ${currentJob.url}. Vui lòng thử lại.`,
            variant: 'destructive',
          });
        } else if (currentJob.status.status === 'blocked') {
          toast({
            title: 'Website chặn thu thập dữ liệu',
            description:
              currentJob.status.message ||
              `Website ${currentJob.url} đang chặn yêu cầu thu thập. Điều này có thể do website có bảo vệ chống bot hoặc hạn chế truy cập.`,
            variant: 'destructive',
          });
        }
      }
    }, [currentJob?.status?.status, currentJob?.status?.isProcessing]);

    const data = useMemo(() => fileSystem[currentPrefix] || [], [fileSystem, currentPrefix]);

    const filteredData = useMemo(
      () => data.filter((d) => canViewDocument(d.permission)),
      [data, currentPrefix, fileSystem],
    );

    // Convert selectedKeys to row selection state for the table
    const rowSelection = useMemo(() => {
      const selection: Record<number, boolean> = {};
      filteredData.forEach((file, index) => {
        if (selectedKeys.has(file.key)) {
          selection[index] = true;
        }
      });
      return selection;
    }, [selectedKeys, filteredData]);

    // Check if a folder is partially selected
    const getFolderSelectionState = (folderKey: string): 'none' | 'partial' | 'all' => {
      const filesUnderFolder = getAllFilesUnderFolder(fileSystem, folderKey);
      // If the folder exists in the fileSystem but has no files, treat it as a single selectable item
      if (filesUnderFolder.length === 0) {
        return selectedKeys.has(folderKey) ? 'all' : 'none';
      }

      const selectedCount = filesUnderFolder.filter((key) => selectedKeys.has(key)).length;
      if (selectedCount === 0) return 'none';
      if (selectedCount === filesUnderFolder.length) return 'all';
      return 'partial';
    };

    const handleFolderClick = (prefix: string, isReplace = true) => {
      if (currentPrefix === prefix) return;

      // Reset browse page when changing folders
      setBrowsePage(1);

      if (idCreator && !currentPrefix) {
        setIsFolderClicking(true);
        setForcePrefix(prefix + idCreator);
        setCurrentPrefix(prefix + idCreator);
        // if (!Boolean(preSelectedFiles)) {
        //   setSelectedKeys(new Set());
        // }
      } else {
        setIsFolderClicking(true);
        setForcePrefix(prefix);
        setCurrentPrefix(prefix);
        // if (!Boolean(preSelectedFiles)) {
        //   setSelectedKeys(new Set());
        // }

        if (isReplace && !asSelector) {
          const basePath = window.location.pathname === '/' ? '' : window.location.pathname;

          const newUrl = prefix
            ? `${window.location.origin}${basePath}?prefix=${encodeURIComponent(prefix)}`
            : `${window.location.origin}${basePath}`;

          window.history.pushState({ path: newUrl }, '', newUrl);
        }
      }
    };

    // Function to handle navigation to failed files
    const handleNavigateToFailedFile = (filePath: string) => {
      // Extract folder path from file path
      const lastSlashIndex = filePath.lastIndexOf('/');
      const folderPath = lastSlashIndex > 0 ? filePath.substring(0, lastSlashIndex + 1) : '';

      // Navigate to the folder containing the file
      if (folderPath) {
        handleFolderClick(folderPath);

        // After navigation, highlight the file by adding it to selectedKeys
        setTimeout(() => {
          setSelectedKeys(new Set([filePath]));
        }, 500); // Small delay to ensure navigation completes
      } else {
        // If file is in root, just highlight it
        setSelectedKeys(new Set([filePath]));
      }
    };

    useEffect(() => {
      if (!isFolderClicking) return;

      ignoreCache.current = true;
      refreshFiles().finally(() => setIsFolderClicking(false));
    }, [isFolderClicking]);

    const createFolder = async (name: string, prefix: string) => {
      setIsCreatingFolder(true);
      try {
        // Check if the folder name already exists in filtered data
        const folderExists = filteredData.some(
          (file) => file.isFolder && file.name.toLowerCase() === name.toLowerCase(),
        );

        if (folderExists) {
          toast({
            title: 'Lỗi',
            description: `Thư mục "${name}" đã tồn tại trong thư mục hiện tại.`,
            variant: 'destructive',
          });
          setIsCreatingFolder(false);
          return;
        }

        const folderPrefix = `${prefix}${name}/`;
        const response = await axios.post(`${apiBaseUrl}/api/create-folder`, {
          prefix: folderPrefix,
          userId: currentUser?.id || '',
        });

        if (response.status !== 200) {
          toast({ title: 'Lỗi', description: 'Không thể tạo thư mục', variant: 'destructive' });
          return;
        }

        ignoreCache.current = true;
        await refreshFiles();

        toast({ title: 'Đã tạo thư mục', description: `Đã tạo thành công thư mục "${name}"` });

        // Reset state
        setNewFolderName('');
        setIsCreateFolderOpen(false);
      } catch (error) {
        console.error('Error creating folder:', error);
        const errorMessage = (error as any)?.response?.data?.error || 'Không thể tạo thư mục';
        toast({ title: 'Lỗi', description: errorMessage, variant: 'destructive' });
      } finally {
        setIsCreatingFolder(false);
      }
    };

    // Lọc tệp theo định dạng cho phép của kho (ví dụ kho_ncc/ chỉ nhận .docx và .pdf).
    // - Kho không có quy tắc: trả lại nguyên danh sách, không toast.
    // - Có tệp sai định dạng: bỏ qua các tệp đó, toast báo danh sách bị bỏ, upload tiếp phần hợp lệ.
    // - Không còn tệp hợp lệ: trả null để phía gọi dừng lại.
    const filterFilesByAllowedExtensions = (
      files: FileList | File[],
      prefix: string,
    ): FileList | null => {
      const { hasRule, validFiles, invalidFiles, allowed } = validateFilesForAllowedExtensions(
        files,
        prefix,
      );

      if (!hasRule) {
        return toFileList(validFiles);
      }

      const allowedLabel = allowed.map((ext) => `.${ext}`).join(', ');

      if (validFiles.length === 0) {
        toast({
          title: 'Lỗi tải lên',
          description: `Kho này chỉ cho phép tải lên tệp định dạng ${allowedLabel}. Không có tệp nào hợp lệ: ${invalidFiles.join(', ')}.`,
          variant: 'destructive',
        });
        return null;
      }

      if (invalidFiles.length > 0) {
        toast({
          title: 'Một số tệp bị bỏ qua',
          description: `Kho này chỉ cho phép tải lên tệp định dạng ${allowedLabel}. Đã bỏ qua ${invalidFiles.length} tệp: ${invalidFiles.join(', ')}. Tiếp tục tải lên ${validFiles.length} tệp hợp lệ.`,
          variant: 'destructive',
        });
      }

      return toFileList(validFiles);
    };

    // Helper function to check if prefix requires metadata form
    // Only applies to kho_doi_ngoai (outside), not kho_doi_noi (inside)
    const requiresMetadataForm = (prefix: string): boolean => {
      return (
        prefix.startsWith('kho_doi_ngoai/') ||
        prefix.startsWith('kho_doi_noi/') ||
        prefix === 'kho_doi_ngoai/' ||
        prefix === 'kho_doi_noi/'
      );
    };

    // Helper function to check if file is a compressed/archive file
    const isCompressedFile = (fileName: string): boolean => {
      const extension = fileName.split('.').pop()?.toLowerCase();
      const compressedExtensions = [
        'zip',
        'rar',
        '7z',
        'tar',
        'gz',
        'bz2',
        'xz',
        'lz',
        'lzma',
        'cab',
        'iso',
        'dmg',
        'pkg',
        'deb',
        'rpm',
        'msi',
        'exe',
        'jar',
        'war',
        'ear',
        'apk',
        'ipa',
        'wim',
        'swm',
        'esd',
      ];
      return compressedExtensions.includes(extension || '');
    };

    // Helper function to validate files for compressed formats
    const validateFilesForCompression = (
      files: FileList,
    ): { hasCompressed: boolean; compressedFiles: string[] } => {
      const compressedFiles: string[] = [];

      Array.from(files).forEach((file) => {
        if (isCompressedFile(file.name)) {
          compressedFiles.push(file.name);
        }
      });

      return {
        hasCompressed: compressedFiles.length > 0,
        compressedFiles,
      };
    };

    const uploadFiles = async (inputFiles: FileList, prefix: string, type: 'files' | 'folder') => {
      // Kho bộ chỉ tiêu chỉ nhận ảnh trực tiếp ở thư mục gốc, không nhận upload folder.
      if (
        isBoChiTieuPrefix(prefix) &&
        (type === 'folder' || Array.from(inputFiles).some((file) => file.webkitRelativePath))
      ) {
        toast({
          title: 'Lỗi tải lên',
          description:
            'Kho bộ chỉ tiêu chỉ cho phép tải ảnh trực tiếp trong thư mục gốc, không tải thư mục.',
          variant: 'destructive',
        });
        return;
      }

      // Lọc tệp sai định dạng theo quy tắc (áp dụng cho cả drag & drop tệp lẻ và cả thư mục)
      const files = filterFilesByAllowedExtensions(inputFiles, prefix);
      if (!files) {
        return;
      }

      // Kiểm tra tổng dung lượng lần upload tối đa 100MB, không giới hạn số lượng file.
      if (checkConditionUploadFiles(files, toast)) {
        return;
      }

      // Check for compressed files first
      const compressionCheck = validateFilesForCompression(files);
      if (compressionCheck.hasCompressed) {
        toast({
          title: 'Lỗi tải lên',
          description: `Không thể tải lên file nén: ${compressionCheck.compressedFiles.join(', ')}. Vui lòng giải nén trước khi tải lên.`,
          variant: 'destructive',
        });
        return;
      }

      // Check if this path requires the metadata form
      if (requiresMetadataForm(prefix)) {
        // Check for compressed files FIRST before proceeding with upload
        const compressionCheck = validateFilesForCompression(files);
        if (compressionCheck.hasCompressed) {
          toast({
            title: 'Lỗi tải lên',
            description: `Không thể tải lên file nén: ${compressionCheck.compressedFiles.join(', ')}. Vui lòng giải nén trước khi tải lên.`,
            variant: 'destructive',
          });
          setShowUploadFormDialog(false);
          setPendingUploadInfo(null);
          setUploadFormErrors([]);
          return;
        }

        setPendingUploadInfo({
          files,
          prefix,
          type,
        });
        setShowUploadFormDialog(true);
        // Reset form for new upload
        setUploadMetadataForm({
          ...defaultUploadMetadata,
          creator: user?.username || '',
          modifier: user?.username || '',
          modifiedDate: new Date().toLocaleDateString('vi-VN'),
        });
        return;
      }

      // For other paths, upload directly without metadata form
      setUploadType(type);
      setIsUploading(true);
      try {
        // Check for empty files
        const emptyFiles = Array.from(files).filter((file) => file.size === 0);
        if (emptyFiles.length > 0) {
          const fileNames = emptyFiles.map((file) => file.name).join(', ');
          toast({
            title: 'Lỗi tải lên',
            description: `Không thể tải lên tập tin rỗng: ${fileNames}`,
            variant: 'destructive',
          });
          setIsUploading(false);
          setUploadType(null);
          return;
        }

        const formData = new FormData();

        if (user?.id) {
          formData.append(
            'metadata',
            JSON.stringify({
              creator: user.username,
              creatorId: user.id,
              DataSource: 'Tệp tải lên',
            }),
          );
        }
        if (type === 'folder' && files[0]) {
          const folderName = files[0].webkitRelativePath.split('/')[0];
          if (folderName) {
            formData.append('folderName', folderName);
          }
        }
        formData.append('prefix', prefix);
        Array.from(files).forEach((file) => {
          if (type === 'folder') {
            return formData.append('files', file, file.webkitRelativePath);
          } else {
            return formData.append('files', file);
          }
        });

        ignoreCache.current = true;

        const checkResponse = await axios.post(`${apiBaseUrl}/api/check-files-existed`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (checkResponse?.data?.status === 200) {
          const toastId = hotToast.loading('Đang tải lên', {
            position: 'bottom-right',
            autoClose: 3000,
          });
          await axios.post(`${apiBaseUrl}/api/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 0, // no timeout (infinite)
          });
          hotToast.update(toastId, {
            render: `Tải lên thành công`,
            type: 'success',
            isLoading: false,
            autoClose: 3000,
            position: 'bottom-right',
          });
          setSearchQuery('');
          setForcePrefix(prefix);
          trackUploadedPdfParse(files, prefix);
          trackUploadedBoChiTieuOcr(files, prefix);
          setTimeout(async () => {
            setUploadType(null);
            await refreshFiles();
            setIsUploading(false);
            await autoSyncAfterFileOperation('upload');
          }, 500);
        } else {
          if (checkResponse?.data?.names?.length > 0 && !checkResponse?.data?.isFolder) {
            const filesFound: any = Array.from(files).filter((file) =>
              checkResponse.data.names.includes(file.name),
            );
            setDuplicateFiles(filesFound);
            setShowDuplicateDialog(true);
          }

          if (checkResponse?.data?.isFolder && checkResponse?.data?.name) {
            setFolderExist(checkResponse?.data?.name);
            setDuplicateFiles(files);
            setShowDuplicateDialog(true);
          }
        }
      } catch (error) {
        console.error('Lỗi tải lên:', error);
        const errorMessage = (error as any)?.response?.data?.error || 'Không thể tải lên tập tin';
        toast({ title: 'Lỗi', description: errorMessage, variant: 'destructive' });
        setUploadType(null);
        setIsUploading(false);
      }
    };

    const overwriteFiles = async () => {
      if (duplicateFiles && checkConditionUploadFiles(duplicateFiles, toast)) {
        return;
      }

      try {
        setIsUploading(true);
        const formData = new FormData();

        if (duplicateFiles) {
          if (Boolean(folderExist)) {
            setUploadType('folder');
          } else {
            setUploadType('files');
          }
          setShowDuplicateDialog(false);
          let dataLogin: any = localStorage.getItem('dataLogin');
          let user: any;

          try {
            if (dataLogin) {
              dataLogin = JSON.parse(dataLogin);
              user = dataLogin.user;
            }
          } catch (error) {
            dataLogin = null;
          }

          if (folderExist && duplicateFiles[0]) {
            const folderName = duplicateFiles[0].webkitRelativePath.split('/')[0];
            if (folderName) {
              formData.append('folderName', folderName);
            }
          }

          formData.append('prefix', currentPrefix);
          Array.from(duplicateFiles).forEach((file) => {
            if (folderExist && duplicateFiles[0]) {
              return formData.append('files', file, file.webkitRelativePath);
            } else {
              return formData.append('files', file);
            }
          });
          if (user?.id) {
            formData.append(
              'metadata',
              JSON.stringify({
                creator: user.username,
                creatorId: user.id,
                DataSource: 'Tệp tải lên',
              }),
            );
          }
          const toastId = hotToast.loading('Đang tải lên', {
            position: 'bottom-right',
            autoClose: 3000,
          });
          await axios.post(`${apiBaseUrl}/api/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 0, // no timeout (infinite)
          });
          hotToast.update(toastId, {
            render: `Tải lên thành công`,
            type: 'success',
            isLoading: false,
            autoClose: 3000,
            position: 'bottom-right',
          });
          setSearchQuery('');
          setForcePrefix(currentPrefix);
          if (duplicateFiles) {
            trackUploadedPdfParse(duplicateFiles, currentPrefix);
            trackUploadedBoChiTieuOcr(duplicateFiles, currentPrefix);
          }
          ignoreCache.current = true;
        }
      } catch (error) {
        console.error('Lỗi tải lên:', error);
        const errorMessage = (error as any)?.response?.data?.error || 'Không thể tải lên tập tin';
        toast({ title: 'Lỗi', description: errorMessage, variant: 'destructive' });
      } finally {
        setTimeout(async () => {
          setUploadType(null);
          await refreshFiles();
          setIsUploading(false);
          setFolderExist('');
          setDuplicateFiles(null);
          await autoSyncAfterFileOperation('overwrite');
        }, 500);
      }
    };

    // Handle upload after form submission
    const handleSubmitUpload = async (
      inputFiles: FileList,
      prefix: string,
      type: 'files' | 'folder',
    ) => {
      if (
        isBoChiTieuPrefix(prefix) &&
        (type === 'folder' || Array.from(inputFiles).some((file) => file.webkitRelativePath))
      ) {
        toast({
          title: 'Lỗi tải lên',
          description:
            'Kho bộ chỉ tiêu chỉ cho phép tải ảnh trực tiếp trong thư mục gốc, không tải thư mục.',
          variant: 'destructive',
        });
        return;
      }

      // Lọc lại tệp sai định dạng theo quy tắc của kho trước khi gửi lên server
      const files = filterFilesByAllowedExtensions(inputFiles, prefix);
      if (!files) {
        setShowUploadFormDialog(false);
        setPendingUploadInfo(null);
        setUploadFormErrors([]);
        return;
      }

      // Kiểm tra tổng dung lượng lần upload tối đa 100MB, không giới hạn số lượng file.
      if (checkConditionUploadFiles(files, toast)) {
        return;
      }
      setIsUploading(true);
      try {
        const formData = new FormData();

        // Add upload metadata form data
        formData.append(
          'uploadMetadata',
          JSON.stringify({
            creator: uploadMetadataForm.creator,
            modifier: uploadMetadataForm.modifier,
            modifiedDate: uploadMetadataForm.modifiedDate,
            summary: uploadMetadataForm.summary,
            effectiveDate: uploadMetadataForm.effectiveDate,
            expirationDate: uploadMetadataForm.expirationDate,
            issueDate: uploadMetadataForm.issueDate,
            processingStatus: uploadMetadataForm.processingStatus,
            status: uploadMetadataForm.status,
            // Additional fields for kho_doi_ngoai/ path
            documentType: uploadMetadataForm.documentType,
            field: uploadMetadataForm.field,
            dataSource: uploadMetadataForm.dataSource,
          }),
        );

        // Add original metadata
        if (user?.id) {
          formData.append(
            'metadata',
            JSON.stringify({
              creatorId: user.id,
              DataSource: 'Tệp tải lên',
              ...uploadMetadataForm,
            }),
          );
        }

        if (type === 'folder' && files[0]) {
          const folderName = files[0].webkitRelativePath.split('/')[0];
          if (folderName) {
            formData.append('folderName', folderName);
          }
        }
        formData.append('prefix', prefix);
        Array.from(files).forEach((file) => {
          if (type === 'folder') {
            return formData.append('files', file, file.webkitRelativePath);
          } else {
            return formData.append('files', file);
          }
        });

        ignoreCache.current = true;

        const checkResponse = await axios.post(`${apiBaseUrl}/api/check-files-existed`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (checkResponse?.data?.status === 200) {
          const toastId = hotToast.loading('Đang tải lên', {
            position: 'bottom-right',
            autoClose: 3000,
          });
          await axios.post(`${apiBaseUrl}/api/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 0,
          });
          hotToast.update(toastId, {
            render: `Tải lên thành công`,
            type: 'success',
            isLoading: false,
            autoClose: 3000,
            position: 'bottom-right',
          });
          setSearchQuery('');
          setForcePrefix(prefix);
          trackUploadedPdfParse(files, prefix);
          trackUploadedBoChiTieuOcr(files, prefix);

          // Reset form
          setUploadMetadataForm({
            ...defaultUploadMetadata,
            creator: user?.username || '',
            modifier: user?.username || '',
            modifiedDate: new Date().toLocaleDateString('vi-VN'),
          });

          setTimeout(async () => {
            setUploadType(null);
            setShowUploadFormDialog(false);
            setPendingUploadInfo(null);
            await refreshFiles();
            setIsUploading(false);
            await autoSyncAfterFileOperation('upload');
          }, 500);
        } else {
          if (checkResponse?.data?.names?.length > 0 && !checkResponse?.data?.isFolder) {
            const filesFound: any = Array.from(files).filter((file) =>
              checkResponse.data.names.includes(file.name),
            );
            setDuplicateFiles(filesFound);
            setShowDuplicateDialog(true);
          }

          if (checkResponse?.data?.isFolder && checkResponse?.data?.name) {
            setFolderExist(checkResponse?.data?.name);
            setDuplicateFiles(files);
            setShowDuplicateDialog(true);
          }
        }
      } catch (error) {
        console.error('Lỗi tải lên:', error);
        const errorMessage = (error as any)?.response?.data?.error || 'Không thể tải lên tập tin';
        toast({ title: 'Lỗi', description: errorMessage, variant: 'destructive' });
        setIsUploading(false);
      } finally {
        setUploadMetadataForm({
          ...defaultUploadMetadata,
          creator: user?.username || '',
          modifier: user?.username || '',
          modifiedDate: new Date().toLocaleDateString('vi-VN'),
        });
        setShowUploadFormDialog(false);
        setPendingUploadInfo(null);
      }
    };

    const handleDelete = async (file: S3File) => {
      setIsDeleting(true);
      try {
        await axios.delete(`${apiBaseUrl}/api/files`, { data: { paths: [file.key] } });

        if (selectedKeys.has(file.key)) {
          const newSet = new Set(selectedKeys);
          newSet.delete(file.key);
          setSelectedKeys(newSet);
        }

        const updatedPrefix =
          file.key?.replace(/^(.*?)(\/.*)?$/, (_, firstPart, slashPart) => {
            if (!slashPart || slashPart === '/') return '';
            return firstPart + '/';
          }) || '';

        setForcePrefix(updatedPrefix);
        ignoreCache.current = true;

        // Show success toast
        toast({ title: 'Đã xóa tập tin', description: `Đã xóa thành công "${file.name}"` });

        setFileToDelete(null);
        await autoSyncAfterFileOperation('delete');
      } catch (error) {
        console.error('Error deleting file:', error);
        toast({ title: 'Lỗi', description: 'Không thể xóa tập tin', variant: 'destructive' });
      } finally {
        setIsDeleting(false);
        await refreshFiles();
      }
    };

    const handleRowSelectionChange = (
      updaterOrValue:
        Record<number, boolean> | ((prev: Record<number, boolean>) => Record<number, boolean>),
    ) => {
      const newSelectedKeys = new Set(selectedKeys);

      const selection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      // Get all indices in the current view
      const currentIndices = new Set(Object.keys(rowSelection).map(Number));

      // Handle deselection for indices that are no longer in the selection
      currentIndices.forEach((index) => {
        const file = filteredData[index];
        if (!file) return;

        if (!selection[index]) {
          newSelectedKeys.delete(file.key);
          if (file.isFolder) {
            getAllFilesUnderFolder(fileSystem, file.key).forEach((key) =>
              newSelectedKeys.delete(key),
            );
          }
        }
      });

      // Handle new selections
      Object.entries(selection).forEach(([indexStr, isSelected]) => {
        const index = parseInt(indexStr);
        const file = filteredData[index];
        if (!file) return;

        if (isSelected) {
          newSelectedKeys.add(file.key);
          if (file.isFolder) {
            getAllFilesUnderFolder(fileSystem, file.key).forEach((key) => newSelectedKeys.add(key));
          }
        }
      });

      setSelectedKeys(newSelectedKeys);
    };

    const handleRemoveSelected = async () => {
      setIsBulkDeleting(true);

      try {
        const selectedKeysArray = Array.from(selectedKeys);

        // Find selected files from the file system
        const selectedFiles = selectedKeysArray
          .map((key) =>
            Object.values(fileSystem)
              .flat()
              .find((f) => f.key === key),
          )
          .filter((file): file is S3File => file !== undefined);

        if (selectedFiles.length === 0) {
          toast({
            title: 'Lỗi',
            description: 'Không có tập tin nào được chọn để xóa',
            variant: 'destructive',
          });
          return;
        }

        await axios.delete(`${apiBaseUrl}/api/files`, { data: { paths: selectedKeysArray } });

        // Xử lý commonPrefix với regex mới
        const commonPrefix =
          selectedFiles[0]?.key.replace(/^(.*?)(\/.*)?$/, (_, firstPart, slashPart) => {
            if (!slashPart || slashPart === '/') return '';
            return firstPart + '/';
          }) || '';

        setForcePrefix(commonPrefix);
        // setCurrentPrefix(commonPrefix);
        ignoreCache.current = true;
        void refreshFiles();

        // Show success toast
        toast({
          title: 'Đã xóa tập tin',
          description: `Đã xóa thành công ${selectedFiles.length} mục`,
        });

        setShowBulkDeleteDialog(false);
        setSelectedKeys(new Set());
        await autoSyncAfterFileOperation('bulk-delete');
      } catch (error) {
        console.error('Error deleting files:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể xóa các tập tin đã chọn',
          variant: 'destructive',
        });
      } finally {
        setIsBulkDeleting(false);
      }
    };

    const handleRename = async (file: S3File, newName: string) => {
      if (!currentPrefix.trim()) {
        toast({
          title: 'Lỗi',
          description: 'Không thể đổi tên tập tin hoặc thư mục ở thư mục gốc. Chỉ có thể xóa.',
          variant: 'destructive',
        });
        return;
      }
      setIsRenaming(true);
      try {
        const oldPath = file.key;
        let parentPath;
        if (file.isFolder) {
          // For folders, go up two levels to skip the folder name and trailing slash
          parentPath = oldPath.split('/').slice(0, -2).join('/') + '/';
        } else {
          // For files, just get the directory path
          parentPath = oldPath.substring(0, oldPath.lastIndexOf('/') + 1);
        }
        const newPath = parentPath + newName + (file.isFolder ? '/' : '');

        await axios.post(`${apiBaseUrl}/api/rename`, { oldPath, newPath });

        // Refresh the file list
        ignoreCache.current = true;
        await refreshFiles();

        // Show success toast
        toast({
          title: 'Đã đổi tên',
          description: `Đã đổi tên thành công "${file.name}" thành "${newName}"`,
        });

        setFileToRename(null);
        setNewName('');
        setMetadataForEdit(null);

        // Auto sync knowledge base after rename
        await autoSyncAfterFileOperation('rename');
      } catch (error) {
        console.error('Error renaming item:', error);
        toast({ title: 'Lỗi', description: 'Không thể đổi tên', variant: 'destructive' });
      } finally {
        setIsRenaming(false);
      }
    };

    const handleDownload = async (paths: string[]) => {
      // Add all paths to downloading set
      setDownloadingItems((prev) => new Set([...prev, ...paths]));
      try {
        const response = await axios.post(
          `${apiBaseUrl}/api/download`,
          { paths },
          { responseType: 'blob' },
        );

        // Create a URL for the blob
        const url = window.URL.createObjectURL(response.data);

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;

        // Get the filename from the Content-Disposition header
        const contentDisposition =
          response.headers['Content-Disposition'] || response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);

        // For single files, use the filename from the header
        // For multiple files or folders, fallback to download.zip
        const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'download.zip';

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Bắt đầu tải xuống',
          description: `Đã bắt đầu tải xuống ${paths.length} mục${paths.length > 1 ? '' : ''}`,
        });
      } catch (error) {
        console.error('Error downloading files:', error);
        toast({ title: 'Lỗi', description: 'Không thể tải xuống tập tin', variant: 'destructive' });
      } finally {
        // Remove all paths from downloading set
        setDownloadingItems((prev) => {
          const next = new Set(prev);
          paths.forEach((path) => next.delete(path));
          return next;
        });
      }
    };

    const handlePublicDocument = async (prefix: string, isPublic: boolean) => {
      try {
        const response = await axios.post(`${apiBaseUrl}/document-permission/public`, {
          path: prefix,
          isPublic,
          grandtedBy: currentUser?.id,
        });

        toast({
          title: response.data?.isPublic
            ? 'Chỉnh sửa tài liệu công khai thành công'
            : 'Chỉnh sửa tài liệu riêng tư thành công',
          description: `Chuyển trạng thái thành công tài liệu "${prefix}".`,
        });

        await refreshFiles();
      } catch (error) {
        console.log('error', error);
      } finally {
      }
    };

    const prefixSegments = currentPrefix
      ? currentPrefix
          .replace(rootPrefix, '') // Remove ROOT_PREFIX from the display
          .split('/')
          .filter(Boolean)
      : [];

    // Get selected files
    const getSelectedFiles = () => {
      let currentSelections = Array.from(selectedKeys).map((key) => ({ key })) as S3File[];

      if (currentSelections.length > 1) {
        currentSelections = currentSelections.filter((v) => v.key !== rootPrefix);
      }

      const haveFile = (folderKey: string) => {
        return (
          findIndex(
            currentSelections,
            (file) => !file.key.endsWith('/') && file.key.startsWith(folderKey),
          ) !== -1
        );
      };

      currentSelections = currentSelections.filter((file) => {
        if (file.key.endsWith('/')) return !haveFile(file.key);
        return true;
      });

      // console.log('currentSelected', currentSelections, rootPrefix);

      return currentSelections;
    };

    // Define columns based on selector mode
    const columns = useMemo<ColumnDef<S3File>[]>(() => {
      const baseColumns = [
        // Selection is a UI convenience only; every destructive operation is
        // re-authorized by the backend for each selected key.
        ...[
          {
            id: 'select',
            header: () => {
              const filesInView = getAllFilesInCurrentView(fileSystem, currentPrefix);
              const allSelected =
                filesInView.length > 0 && filesInView.every((key) => selectedKeys.has(key));
              const someSelected = filesInView.some((key) => selectedKeys.has(key));

              return (
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const allSelection = filteredData.reduce(
                        (acc, _, index) => {
                          acc[index] = true;
                          return acc;
                        },
                        {} as Record<number, boolean>,
                      );

                      handleRowSelectionChange(allSelection);
                    } else {
                      for (const selectedKey of Array.from(selectedKeys)) {
                        if (selectedKey.startsWith(currentPrefix)) {
                          selectedKeys.delete(selectedKey);
                          selectStates.current[selectedKey] = false;
                        }
                      }

                      forEach(selectStates.current, (_value, key) => {
                        if (key.startsWith(currentPrefix)) {
                          selectStates.current[key] = false;
                        }
                      });

                      handleRowSelectionChange({});
                    }
                  }}
                  aria-label="Chọn tất cả"
                />
              );
            },
            cell: ({ row }: any) => {
              const file = row.original;
              const isFolder = file.isFolder;
              let selectionState = isFolder
                ? getFolderSelectionState(file.key)
                : selectedKeys.has(file.key)
                  ? 'all'
                  : 'none';

              if (selectionState === 'none' && file.key.endsWith('/')) {
                for (const selectedKey of Array.from(selectedKeys)) {
                  if (selectedKey.startsWith(file.key)) {
                    selectionState = 'all';
                    break;
                  }
                }
              }

              return (
                <div className="flex items-center">
                  <Checkbox
                    checked={
                      selectionState === 'partial' ? 'indeterminate' : selectionState === 'all'
                    }
                    onCheckedChange={(checked) => {
                      const newSelection = { ...rowSelection };
                      if (checked) {
                        newSelection[row.index] = true;
                      } else {
                        delete newSelection[row.index];
                      }

                      selectStates.current[row.original?.key || '_'] = Boolean(checked);

                      if (row.original?.key.endsWith('/')) {
                        for (const selectedKey of Array.from(selectedKeys)) {
                          if (selectedKey.startsWith(row.original.key)) {
                            selectStates.current[row.original.key] = false;
                            selectedKeys.delete(selectedKey);
                          }
                        }
                      }

                      handleRowSelectionChange(newSelection);
                    }}
                    aria-label="Chọn dòng"
                  />
                </div>
              );
            },
            enableSorting: false,
          },
        ],
        {
          id: 'fileName',
          header: ({ column }: any) => {
            return (
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              >
                Tên tập tin
                <ArrowUpDown />
              </Button>
            );
          },
          accessorKey: 'name',
          cell: ({ row }: any) => {
            const file = row.original;
            const parentPrefix = file.key.substring(0, file.key.lastIndexOf('/')) || '/';
            const showPrefix = searchQuery.trim() !== '' && parentPrefix !== currentPrefix;

            // Split the parent prefix into segments for individual folder links
            const prefixSegments = parentPrefix.split('/').filter(Boolean);

            return (
              <div className="flex flex-col group">
                <div className="flex items-center">
                  <div
                    className={`flex items-center gap-2 ${file.isFolder ? 'cursor-pointer hover:text-blue-600' : ''}`}
                    onClick={() => {
                      if (file.isFolder) {
                        // if (!preSelectedFiles) {
                        //   setSelectedKeys(new Set());
                        // }
                        handleFolderClick(file.key);
                      }
                    }}
                  >
                    {file.isFolder ? <FolderIcon className="h-5 w-5 text-blue-500" /> : null}
                    {file.name}
                  </div>
                  {(() => {
                    if (file.isFolder) return null;
                    if (isBoChiTieuImageKey(file.key)) {
                      const record = boChiTieuOcrStatus[file.key];
                      if (!record) return null;
                      if (record.status === 'queued' || record.status === 'processing') {
                        const percent =
                          typeof record.progress === 'number'
                            ? Math.max(0, Math.min(100, Math.round(record.progress)))
                            : null;
                        return (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Đang tạo JSON{percent !== null ? ` ${percent}%` : ''}
                          </span>
                        );
                      }
                      if (record.status === 'failed') {
                        return (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                            title={record.error || 'Trích xuất thất bại'}
                          >
                            Trích xuất thất bại
                          </span>
                        );
                      }
                      return null;
                    }

                    // Chỉ hiển thị trạng thái parse cho file PDF trong kho quy định pháp luật.
                    if (!isChatCnLegalKey(file.key) || !isPdfName(file.name)) return null;
                    const record = pdfParseStatus[file.key];
                    if (!record) return null;

                    if (record.status === 'queued' || record.status === 'processing') {
                      const percent =
                        typeof record.progress === 'number'
                          ? Math.max(0, Math.min(100, Math.round(record.progress)))
                          : null;
                      const label =
                        record.status === 'queued'
                          ? 'Đang chờ trích xuất...'
                          : 'Đang trích xuất dữ liệu PDF...';
                      return (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {label}
                          {percent !== null ? ` ${percent}%` : ''}
                        </span>
                      );
                    }

                    if (record.status === 'failed') {
                      return (
                        <span
                          className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                          title={record.error || 'Trích xuất thất bại'}
                        >
                          Trích xuất thất bại
                        </span>
                      );
                    }

                    return null;
                  })()}
                  <button
                    className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(file.key);
                      toast({
                        title: 'Copied to clipboard',
                        description: `Path: ${file.key}`,
                        duration: 2000,
                      });
                    }}
                    title="Copy path to clipboard"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
                {showPrefix && (
                  <div className="text-sm text-gray-500 flex items-center">
                    <FolderIcon className="h-4 w-4 text-gray-500 my-auto" />
                    <span className="flex items-center ml-1">
                      <span
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => {
                          // searchBoxRef.current?.setValue('');
                          handleFolderClick('');
                        }}
                      >
                        /
                      </span>
                      {prefixSegments.map((segment: string, index: number) => (
                        <span key={index} className="flex items-center">
                          <span
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => {
                              // searchBoxRef.current?.setValue('');
                              handleFolderClick(prefixSegments.slice(0, index + 1).join('/') + '/');
                            }}
                          >
                            {segment}
                          </span>
                          {index < prefixSegments.length - 1 && <span>/</span>}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            );
          },
        },
      ];

      if (!asSelector) {
        baseColumns.push(
          {
            id: 'size',
            header: ({ column }: any) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                >
                  Kích thước
                  <ArrowUpDown />
                </Button>
              );
            },
            accessorKey: 'size',
            cell: ({ getValue, row }) => {
              const value = getValue();
              return <span>{row.original.isFolder ? '-' : formatBytes(value as number)}</span>;
            },
          },
          {
            id: 'lastModified',
            header: ({ column }: any) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                  className={'px-0 w-full flex-row justify-end'}
                >
                  Sửa đổi lần cuối
                  <ArrowUpDown />
                </Button>
              );
            },
            accessorKey: 'lastModified',
            cell: ({ row }: any) => {
              const value = row.original.lastModified as Date;
              return (
                <div className={'w-full flex-row justify-end items-center text-right'}>
                  <span>
                    {value && dayjs(value).isValid()
                      ? dayjs(value).format('DD/MM/YYYY HH:mm')
                      : '-'}
                  </span>
                </div>
              );
            },
            size: 200,
          } as any,
          {
            // Người sửa đổi gần nhất, đặt cạnh cột "Sửa đổi lần cuối".
            id: 'modifier',
            header: ({ column }: any) => {
              return (
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                  className={'px-0 w-full flex-row justify-end'}
                >
                  Người sửa đổi
                  <ArrowUpDown />
                </Button>
              );
            },
            accessorKey: 'modifier',
            cell: ({ row }: any) => {
              // Thư mục không có metadata sidecar nên không hiển thị người sửa đổi.
              const value = row.original.isFolder ? '' : (row.original.modifier as string);
              return (
                <div className={'w-full flex-row justify-end items-center text-right'}>
                  <span className="truncate" title={value || ''}>
                    {value || '-'}
                  </span>
                </div>
              );
            },
            size: 160,
          } as any,
          {
            id: 'actions',
            header: () => {
              return <span>Hành động</span>;
            },
            accessorKey: 'key',
            cell: ({ row }) => (
              <div className="flex items-center gap-2">
                {/* Đã ẩn theo yêu cầu: nút "Những Flow sử dụng Folder này làm Data" */}
                {false && currentPrefix === '' && rootUrlHostApi && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setFolderToShowFlow(row.original.key);
                            setShowInfoFlowsDialogs(true);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <CircleStackIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Những Flow sử dụng Folder này làm Data</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload([row.original.key])}
                        className="h-8 w-8 p-0"
                        disabled={
                          !canDownloadDocument(row.original.permission) ||
                          downloadingItems.has(row.original.key)
                        }
                      >
                        {downloadingItems.has(row.original.key) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Tải xuống</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {currentPrefix.trim() !== '' &&
                  (canEditDocument(row.original.permission) ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setFileToRename(row.original);
                              // Extract name from key if name is empty
                              const displayName =
                                row.original.name ||
                                row.original.key.split('/').filter(Boolean).pop() ||
                                '';
                              setNewName(displayName);
                              // Set default tab based on file type
                              setActiveTab(row.original.isFolder ? 'rename' : 'edit-metadata');
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Đổi tên</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              console.log('call');
                              setFileToView(row.original);
                            }}
                            className="h-8 w-8 p-0"
                            disabled={row.original.isFolder}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Xem</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}

                {
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setOpenDialogRename(true);
                            setFileToMove(row.original);
                          }}
                          className="h-8 w-8 p-0"
                          disabled={!canEditDocument(row.original.permission)}
                        >
                          <MoveUpRight className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Di chuyển</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                }

                {!(currentPrefix === '' || currentPrefix === '/') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setFileToDelete(row.original)}
                          className="h-8 w-8 p-0"
                          disabled={!canEditDocument(row.original.permission)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Xóa</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {canEditDocument(row.original.permission) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setFileToShare(row.original)}
                          className="h-8 w-8 p-0"
                        >
                          <ShareIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Chia sẻ</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {currentPrefix !== '' &&
                  currentPrefix !== '/' &&
                  canEditDocument(row.original.permission) && (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full 
                      ${!!row.original.isPublic ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} 
                      px-3 py-1 text-sm font-medium cursor-pointer 
                      whitespace-nowrap overflow-hidden text-ellipsis
                    `}
                        onClick={() => setFileToEditPrivacy(row.original)}
                      >
                        {!!row.original.isPublic ? 'Công khai' : 'Riêng tư'}
                      </span>
                      <PrivacyDialog
                        file={fileToEditPrivacy}
                        open={!!fileToEditPrivacy && fileToEditPrivacy.key === row.original.key}
                        onClose={() => setFileToEditPrivacy(null)}
                        onSave={handlePublicDocument}
                      />
                    </>
                  )}
              </div>
            ),
          },
        );
      } else {
        baseColumns.push({
          id: 'actions',
          header: () => {
            return <span>Hành động</span>;
          },
          accessorKey: 'key',
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              {canEditDocument(row.original.permission) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFileToShare(row.original)}
                        className="h-8 w-8 p-0"
                      >
                        <ShareIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Chia sẻ</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {canEditDocument(row.original.permission) && (
                <>
                  <ToggleButton
                    enabled={!!row.original.isPublic}
                    onChange={() => handlePublicDocument(row.original.key, !row.original.isPublic)}
                  />
                  <span
                    className={`inline-flex items-center gap-1 rounded-full ${!!row.original.isPublic ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} px-3 py-1 text-sm font-medium cursor-pointer`}
                    onClick={() => setFileToEditPrivacy(row.original)}
                  >
                    {!!row.original.isPublic ? 'Công khai' : 'Riêng tư'}
                  </span>
                  {/* <PrivacyDialog file={fileToEditPrivacy} open={!!fileToEditPrivacy} onClose={() => setFileToEditPrivacy(null)} onSave={handlePublicDocument}/> */}
                </>
              )}
            </div>
          ),
        });
      }

      return baseColumns;
    }, [selectedKeys, filteredData, rowSelection, downloadingItems, asSelector, fileToEditPrivacy]);

    // Create table instance
    const table = useReactTable({
      data: filteredData,
      columns,
      state: { rowSelection, sorting },
      onSortingChange: setSorting,
      enableRowSelection: true,
      onRowSelectionChange: handleRowSelectionChange,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    // Add loadFileContent function
    const loadFileContent = async (file: S3File) => {
      setIsLoadingContent(true);
      try {
        let pathToLoad = file.key;

        // For image files, load the .ocr.txt file instead
        if (isImageFile(file)) {
          pathToLoad = getOcrFilePath(file);
        }

        if (isTextFile(file) || isImageFile(file)) {
          try {
            const responseContentFile = await axios.get(`${apiBaseUrl}/api/read-text`, {
              params: { path: pathToLoad },
            });
            setFileContent(responseContentFile.data.content || '');
          } catch (error) {
            // For images without OCR file, set empty content (not an error)
            if (isImageFile(file)) {
              setFileContent('');
            } else {
              throw error;
            }
          }
        }

        try {
          const responseMetadata = await axios.get(`${apiBaseUrl}/api/read-json`, {
            params: { path: file.key + '.metadata.json' },
          });
          const metadataAttributes = responseMetadata?.data?.content?.metadataAttributes || null;

          return setMetadataForEdit({
            fullPath: fileToRename?.key,
            ...normalizeMetadataForDisplay(metadataAttributes),
          });
        } catch (error) {
          return setMetadataForEdit('Lỗi phân tích siêu dữ liệu');
        }
      } catch (error) {
        console.error('Error loading file content:', error);
        toast({
          title: 'Lỗi',
          description: isImageFile(file)
            ? 'Không thể tải nội dung OCR'
            : 'Không thể tải nội dung tập tin',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingContent(false);
      }
    };

    // Add saveFileContent function
    const saveFileContent = async (file: S3File, content: string) => {
      setIsSavingContent(true);
      try {
        let pathToSave = file.key;

        // For image files, save to the .ocr.txt file
        if (isImageFile(file)) {
          pathToSave = getOcrFilePath(file);
        }

        await axios.post(`${apiBaseUrl}/api/modify-text`, { path: pathToSave, content });

        // Show success toast
        toast({
          title: 'Đã lưu tập tin',
          description: isImageFile(file)
            ? 'Nội dung OCR đã được cập nhật thành công'
            : 'Nội dung tập tin đã được cập nhật thành công',
        });

        // Refresh the file list to update metadata
        ignoreCache.current = true;
        await refreshFiles();

        // Close the dialog after successful save
        setFileToRename(null);
        setFileContent('');
        setActiveTab('rename');
        await autoSyncAfterFileOperation('edit-content');
      } catch (error) {
        console.error('Error saving file content:', error);
        toast({
          title: 'Lỗi',
          description: isImageFile(file)
            ? 'Không thể lưu nội dung OCR'
            : 'Không thể lưu nội dung tập tin',
          variant: 'destructive',
        });
      } finally {
        setIsSavingContent(false);
      }
    };

    // Add saveFileContent function
    const saveJsonFile = async (file: S3File) => {
      // Validate required fields before saving
      const errors = validateMetadataFields(metadataForEdit);
      if (Object.keys(errors).length > 0) {
        setMetadataErrors(errors);
        toast({
          title: 'Lỗi xác thực',
          description: 'Vui lòng điền đầy đủ các trường bắt buộc',
          variant: 'destructive',
        });
        return;
      }

      setIsSavingContent(true);
      try {
        // console.log('🚀 ~ saveJsonFile ~ metadataForEdit:', metadataForEdit);

        // Không gửi người tạo / người sửa lên server: server tự giữ người tạo và
        // đóng dấu người sửa theo tài khoản đang thực hiện thao tác.
        const { fullPath, ...editableMetadata } = metadataForEdit as Record<string, any>;

        await axios.post(`${apiBaseUrl}/api/modify-json`, {
          path: file.key + '.metadata.json',
          content: { metadataAttributes: stripServerOwnedMetadata(editableMetadata) },
        });

        // Show success toast
        toast({
          title: 'Đã lưu tập tin',
          description: 'Nội dung tập tin đã được cập nhật thành công',
        });

        // Refresh the file list to update metadata
        ignoreCache.current = true;
        await refreshFiles();

        // Close the dialog after successful save
        setFileToRename(null);
        setFileContent('');
        setActiveTab('rename');
        setMetadataErrors({});
      } catch (error) {
        console.error('Error saving file content:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể lưu nội dung tập tin',
          variant: 'destructive',
        });
      } finally {
        setIsSavingContent(false);
      }
    };

    // Run validation when fileToRename changes (when dialog opens)
    useEffect(() => {
      if (fileToRename && metadataForEdit && typeof metadataForEdit === 'object') {
        const errors = validateMetadataFields(metadataForEdit);
        setMetadataErrors(errors);
      }
    }, [fileToRename, metadataForEdit]);

    // Modify the useEffect to load content when fileToRename changes
    useEffect(() => {
      if (fileToRename) {
        if (!fileToRename.isFolder) {
          void loadFileContent(fileToRename);
        }
        // Note: activeTab is set by the click handler in the actions column
        // based on file type (folder -> 'rename', file -> 'edit-metadata')
      }
    }, [fileToRename]);

    useEffect(() => {
      if (fileToView && !fileToView.isFolder) {
        void loadFileContent(fileToView);
      }
    }, [fileToView]);

    // Function to fetch S3 folders
    const fetchS3Folders = async (prefix: string = '') => {
      setIsFetchingFolders(true);
      try {
        await axios.get(`${apiBaseUrl}/api/list-s3-folders`, {
          params: { prefix },
        });
      } catch (error) {
        console.error('Error fetching S3 folders:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể tải danh sách thư mục S3',
          variant: 'destructive',
        });
      } finally {
        setIsFetchingFolders(false);
      }
    };

    // Load folders when dialog opens
    useEffect(() => {
      if (isWebsiteDialogOpen) {
        // Load folders based on current prefix
        fetchS3Folders(currentPrefix);
      } else {
        // Clear folders when dialog closes
        setSelectedS3Folder('');
      }
    }, [isWebsiteDialogOpen]);

    const crawlWebsite = async (
      url: string,
      limit: number,
      maxDepth: number,
      maxPagesToCrawl?: number,
    ) => {
      // Validate folder selection is required
      if (!selectedS3Folder) {
        toast({
          title: 'Lỗi',
          description: 'Vui lòng chọn thư mục lưu trữ trước khi bắt đầu thu thập.',
          variant: 'destructive',
        });
        return;
      }

      // If there's already a job running, don't start another one
      if (currentJob) {
        toast({
          title: 'Đang thu thập website',
          description: 'Vui lòng đợi cho công việc thu thập hiện tại hoàn thành.',
          variant: 'destructive',
        });
        return;
      }

      setIsCrawling(true);

      // try {
      //   const response = await axios.get(url);
      //   const status = response.status;
      //   if (status !== 200) {
      //     toast({
      //       title: 'Lỗi',
      //       description: `Không thể lấy dữ liệu. Vui lòng kiểm tra lại đường dẫn.`,
      //       variant: 'destructive',
      //     });
      //     setIsCrawling(false);
      //     return;
      //   }
      // } catch (error) {
      //   toast({
      //     title: 'Lỗi',
      //     description: `Đường dẫn không hợp lệ. Vui lòng kiểm tra lại.`,
      //     variant: 'destructive',
      //   });
      //   setIsCrawling(false);
      //   return;
      // }

      try {
        // Extract URL pattern from the seed URL for dynamic pattern matching
        let urlPattern: string | undefined = undefined;
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/').filter(Boolean);
          if (pathParts.length > 0) {
            urlPattern = `/${pathParts[0]}`;
          }
        } catch {
          // URL parsing failed, keep urlPattern as undefined
        }

        console.log('[Frontend] Seed URL:', url);
        console.log('[Frontend] Extracted URL Pattern:', urlPattern);
        console.log('[Frontend] Max Pages to Crawl:', maxPagesToCrawl);

        // Get user info from localStorage for creator/creatorId
        const userInfo = getUserInfoFromLocal();

        const { data: jobInfo } = await axios.post(`${apiBaseUrl}/api/crawl`, {
          url,
          limit,
          max_depth: maxDepth || 10,
          prefix: selectedS3Folder || currentPrefix,
          isAutoUpdateKB,
          urlPattern,
          maxPagesToCrawl,
          creator: userInfo?.username || '',
          creatorId: userInfo?.id || '',
        });

        if (jobInfo.id) {
          // Set current job
          setCurrentJob({
            id: jobInfo.id,
            url,
            limit,
            prefix: currentPrefix,
            taskId: jobInfo.task?.id || '',
          });

          // Store in history
          const storedJobs = localStorage.getItem(CRAWL_JOBS_KEY);
          const crawlJobs = storedJobs
            ? (JSON.parse(storedJobs) as {
                url: string;
                id: string;
                limit: number;
                prefix: string;
                timestamp: number;
              }[])
            : [];

          if (!crawlJobs.some((job) => job.id === jobInfo.id)) {
            crawlJobs.unshift({
              url,
              id: jobInfo.id,
              limit,
              prefix: currentPrefix,
              timestamp: Date.now(),
            });
            if (crawlJobs.length > 20) {
              crawlJobs.length = 20;
            }
            localStorage.setItem(CRAWL_JOBS_KEY, JSON.stringify(crawlJobs));
          }

          toast({
            title: 'Bắt đầu thu thập website',
            description: `Đã thêm nhiệm vụ thu thập ${url} với giới hạn ${limit}. Dữ liệu sẽ có sẵn sau khi thu thập hoàn tất.`,
          });

          setIsWebsiteDialogOpen(false);
          setWebsiteUrl('');
          setCrawlLimit('10');
          setMaxDepth('2');
          setMaxPagesToCrawl('10');
        }
      } catch (error) {
        console.error('Error crawling website:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể bắt đầu thu thập website',
          variant: 'destructive',
        });
      } finally {
        setIsCrawling(false);
      }
    };

    const stopCurrentJob = async () => {
      try {
        if (!currentJob?.id) return;

        // Call backend to set stopped flag
        await axios.post(`${apiBaseUrl}/api/crawl/${currentJob.id}/stop`);

        // Clear current job from state and localStorage
        setCurrentJob(null);
        localStorage.removeItem(CURRENT_CRAWL_JOB_KEY);

        toast({
          title: 'Đã dừng thu thập',
          description: `Đã dừng thu thập website ${currentJob.url}`,
        });
      } catch (error) {
        console.error('Error stopping job:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể dừng thu thập website',
          variant: 'destructive',
        });
      }
    };

    useEffect(() => {
      if (asSelector) return;

      const handlePopState = (changeSelectKey = true) => {
        const url = new URL(window.location.href);
        const prefixParam = url.searchParams.get('prefix') || '';

        if (prefixParam) {
          handleFolderClick(prefixParam, false);
        } else {
          if (changeSelectKey && !Boolean(preSelectedFiles)) {
            setSelectedKeys(new Set());
          }
          setForcePrefix(rootPrefix);
          setCurrentPrefix(rootPrefix);
          ignoreCache.current = true;

          setTimeout(() => {
            void refreshFiles();
          }, 0);
        }
      };

      // Handle initial URL on mount
      handlePopState(false);

      window.addEventListener('popstate', () => handlePopState(true));
      return () => {
        window.removeEventListener('popstate', () => handlePopState(true));
      };
    }, []);

    const handleExportReport = async () => {
      setIsLoadingHandleExportReport(true);
      try {
        await axios.post(`${apiBaseUrl}/api/prediction-report`);

        // console.log('🚀 ~ Home.tsx:1585 ~ handleExportReport ~ response:', response);

        toast({ title: 'Xuất báo cáo thành công', description: 'Báo cáo dự đoán đã được xuất' });
      } catch (error) {
        console.error('Error exporting report:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể xuất báo cáo dự đoán',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingHandleExportReport(false);
      }
    };

    const handleSyncKnowledgeBase = async () => {
      try {
        const user = getUserInfoFromLocal();

        if (user) {
          const response = await axios.post(`${apiBaseUrl}/api/start-ingestion-job`, {
            prefix: currentPrefix,
            user_id: user.id,
            userName: user.username,
          });
          if (response.status === 200) {
            await checkSyncingStatus();
          }
        }
      } catch (error) {
        setIsSyncing(false);
        console.log(error);
      }
    };

    // Helper function to auto sync knowledge base after file operations
    const autoSyncAfterFileOperation = async (operationType: string) => {
      try {
        // Only auto sync if we're in a knowledge base folder (has a root folder name)
        if (rootFolderName && currentPrefix.includes(rootFolderName)) {
          console.log(`Auto syncing knowledge base after ${operationType} operation`);
          await checkSyncingStatus();
        }
      } catch (error) {
        console.log(`Error auto syncing after ${operationType}:`, error);
      }
    };

    const interval = useRef<any>();

    const checkSyncingStatus = async () => {
      try {
        setIsSyncing(true);
        const response = await axios.post(`${apiBaseUrl}/api/get-ingestion-job-status`, {
          prefix: currentPrefix,
        });

        // status: "STARTING" || "IN_PROGRESS" || "COMPLETE" || "FAILED" || "STOPPING" || "STOPPED"
        const { status } = response?.data;
        if (
          status === 'FAILED' ||
          status === 'COMPLETE' ||
          status === 'STOPPED' ||
          status === 'STOPPING' ||
          !status
        ) {
          setIsSyncing(false);
          if (interval.current) {
            clearInterval(interval.current);
            interval.current = null;
          }
          setSyncJobStatus(response?.data);
        } else {
          if (!interval.current) {
            interval.current = setInterval(() => checkSyncingStatus(), 5000);
          }
          setSyncJobStatus(response?.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const calculateSelectedFiles = () => {
      const keys = Array.from(selectedKeys);
      let count: number;

      if (!currentPrefix) {
        count = new Set(keys.filter((key) => key.split('/').shift())).size;
      } else {
        count = keys.filter((key) => key.startsWith(currentPrefix) && key !== currentPrefix).length;
      }

      return `Đang chọn: ${count}`;
    };

    // console.log(syncJobStatus);

    // Check sync status every 5s
    useEffect(() => {
      const newRootFolderName = currentPrefix?.split('/')[0];

      if (newRootFolderName !== rootFolderName) {
        setIsSyncing(false);
        if (interval.current) {
          clearInterval(interval.current);
          interval.current = null;
        }
        setRootFolderName(newRootFolderName || '');
        setSyncJobStatus(null);

        if (newRootFolderName) {
          checkSyncingStatus();
        }
      }

      return () => {};
    }, [rootFolderName, currentPrefix]);

    useEffect(() => {
      if (isSharePointModalOpen && sharePointSites.length === 0) {
        fetchSharePointSites();
        setSelectedSite(null);
      }
    }, [isSharePointModalOpen]);

    useEffect(() => {
      if (preSelectedFiles) {
        for (const key of Array.from(preSelectedFiles)) {
          selectStates.current[key] = true;
        }
      }
    }, []);

    return (
      <div className="space-y-4 s3-explr-container">
        <Toaster />

        {/* Upload Metadata Form Dialog */}
        <Dialog
          open={showUploadFormDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowUploadFormDialog(false);
              setUploadFormErrors([]);
              setPendingUploadInfo(null);
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Thông tin văn bản</DialogTitle>
              <DialogDescription>Vui lòng điền thông tin trước khi tải lên</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Người tạo - readonly */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="creator" className="text-xs text-gray-500">
                    Người tạo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="creator"
                    value={uploadMetadataForm.creator}
                    readOnly
                    className="bg-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modifier" className="text-xs text-gray-500">
                    Người sửa <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="modifier"
                    value={uploadMetadataForm.modifier}
                    readOnly
                    className="bg-gray-100"
                  />
                </div>
              </div>

              {/* Ngày sửa - readonly */}
              <div className="space-y-2">
                <Label htmlFor="modifiedDate" className="text-xs text-gray-500">
                  Ngày sửa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="modifiedDate"
                  value={uploadMetadataForm.modifiedDate}
                  readOnly
                  className="bg-gray-100"
                />
              </div>

              {/* Trích yếu */}
              <div className="space-y-2">
                <Label htmlFor="summary" className="text-xs text-gray-500">
                  Trích yếu <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="summary"
                  value={uploadMetadataForm.summary}
                  onChange={(e) =>
                    setUploadMetadataForm((prev) => ({
                      ...prev,
                      summary: e.target.value,
                    }))
                  }
                  placeholder="Tóm tắt văn bản"
                  rows={3}
                />
              </div>

              {/* Ngày hiệu lực & Ngày hết hiệu lực */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="effectiveDate" className="text-xs text-gray-500">
                    Ngày hiệu lực <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="effectiveDate"
                    type="date"
                    value={uploadMetadataForm.effectiveDate}
                    onChange={(e) =>
                      setUploadMetadataForm((prev) => ({
                        ...prev,
                        effectiveDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expirationDate" className="text-xs text-gray-500">
                    Ngày hết hiệu lực
                  </Label>
                  <Input
                    id="expirationDate"
                    type="date"
                    value={uploadMetadataForm.expirationDate}
                    onChange={(e) => {
                      setUploadMetadataForm((prev) => ({
                        ...prev,
                        expirationDate: e.target.value,
                      }));
                      // Real-time validation
                      const error = validateExpirationDate(
                        uploadMetadataForm.effectiveDate,
                        e.target.value,
                      );
                      setExpirationDateError(error);
                    }}
                  />
                  {expirationDateError && (
                    <p className="text-xs text-red-500">{expirationDateError}</p>
                  )}
                </div>
              </div>

              {/* Ngày ban hành */}
              <div className="space-y-2">
                <Label htmlFor="issueDate" className="text-xs text-gray-500">
                  Ngày ban hành <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="issueDate"
                  type="date"
                  value={uploadMetadataForm.issueDate}
                  onChange={(e) =>
                    setUploadMetadataForm((prev) => ({
                      ...prev,
                      issueDate: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Trạng thái xử lý */}
              <div className="space-y-2">
                <Label htmlFor="processingStatus" className="text-xs text-gray-500">
                  Trạng thái xử lý <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={uploadMetadataForm.processingStatus}
                  onValueChange={(value) =>
                    setUploadMetadataForm((prev) => ({
                      ...prev,
                      processingStatus: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn trạng thái xử lý" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phat_hanh">Phát hành</SelectItem>
                    <SelectItem value="da_phat_hanh">Đã phát hành</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tình trạng */}
              <div className="space-y-2">
                <Label htmlFor="status" className="text-xs text-gray-500">
                  Tình trạng <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={uploadMetadataForm.status}
                  onValueChange={(value) =>
                    setUploadMetadataForm((prev) => ({
                      ...prev,
                      status: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn tình trạng" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hieu_luc">Hiệu lực</SelectItem>
                    <SelectItem value="het_hieu_luc">Hết hiệu lực</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Additional fields for kho_doi_ngoai/ path only */}
              {(currentPrefix.startsWith('kho_doi_ngoai/') ||
                currentPrefix === 'kho_doi_ngoai/') && (
                <>
                  {/* Loại văn bản */}
                  <div className="space-y-2">
                    <Label htmlFor="documentType" className="text-xs text-gray-500">
                      Loại văn bản <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="documentType"
                      value={uploadMetadataForm.documentType}
                      onChange={(e) =>
                        setUploadMetadataForm((prev) => ({
                          ...prev,
                          documentType: e.target.value,
                        }))
                      }
                      placeholder="Nhập loại văn bản"
                    />
                  </div>

                  {/* Lĩnh vực */}
                  <div className="space-y-2">
                    <Label htmlFor="field" className="text-xs text-gray-500">
                      Lĩnh vực <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="field"
                      value={uploadMetadataForm.field}
                      onChange={(e) =>
                        setUploadMetadataForm((prev) => ({
                          ...prev,
                          field: e.target.value,
                        }))
                      }
                      placeholder="Nhập lĩnh vực"
                    />
                  </div>

                  {/* Nguồn dữ liệu - readonly, auto-filled */}
                  <div className="space-y-2">
                    <Label htmlFor="dataSource" className="text-xs text-gray-500">
                      Nguồn dữ liệu
                    </Label>
                    <Input id="dataSource" value="Tệp tải lên" readOnly className="bg-gray-100" />
                  </div>
                </>
              )}

              {/* Form errors display */}
              {uploadFormErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                  <p className="text-sm font-semibold text-red-700">Lỗi xác thực:</p>
                  {uploadFormErrors.map((err, idx) => (
                    <p key={idx} className="text-sm text-red-600">
                      • {err}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowUploadFormDialog(false);
                  setUploadFormErrors([]);
                  setPendingUploadInfo(null);
                }}
              >
                Hủy
              </Button>
              <Button
                className="bg-[#1682fe] hover:bg-[#0f6cd4] text-white"
                onClick={() => {
                  // Validate form
                  const errors = validateUploadForm(uploadMetadataForm);

                  // Additional validation for kho_doi_ngoai/ path only
                  const isKhoDoiNgoai =
                    pendingUploadInfo?.prefix.startsWith('kho_doi_ngoai/') ||
                    pendingUploadInfo?.prefix === 'kho_doi_ngoai/';

                  if (isKhoDoiNgoai) {
                    if (!uploadMetadataForm.documentType?.trim())
                      errors.push('Loại văn bản là bắt buộc');
                    if (!uploadMetadataForm.field?.trim()) errors.push('Lĩnh vực là bắt buộc');
                  }

                  if (errors.length > 0) {
                    setUploadFormErrors(errors);
                    return;
                  }

                  // Submit form and proceed with upload
                  setUploadFormErrors([]);
                  if (pendingUploadInfo) {
                    const { files, prefix, type } = pendingUploadInfo;
                    handleSubmitUpload(files, prefix, type);
                  }
                }}
                disabled={_isUploading}
              >
                {_isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Đang tải lên...
                  </>
                ) : (
                  'Tiếp tục tải lên'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showDuplicateDialog}
          onOpenChange={(e) => {
            setShowDuplicateDialog(e);
            if (!e) {
              setFolderExist('');
              setDuplicateFiles(null);
              setIsUploading(false);
              setUploadType(null);
            }
          }}
        >
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cảnh báo trùng lặp</DialogTitle>
              <DialogDescription>
                {folderExist && <>Folder ${folderExist} đã có.</>}
                {duplicateFiles && duplicateFiles.length > 0 && (
                  <>
                    Những tệp sau bị lặp:
                    <ul className="mt-2 max-h-40 overflow-y-auto list-disc pl-5 text-sm text-red-600">
                      {Array.from(duplicateFiles).map((file: File, index: number) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setFolderExist('');
                  setDuplicateFiles(null);
                  setIsUploading(false);
                  setUploadType(null);
                }}
              >
                Bỏ qua
              </Button>
              <Button variant="destructive" onClick={() => overwriteFiles()}>
                Ghi đè
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Breadcrumb className="s3-explr-breadcrumb">
          <BreadcrumbList className="s3-explr-breadcrumb-list">
            <BreadcrumbItem className="s3-explr-breadcrumb-item">
              <BreadcrumbLink
                onClick={() => handleFolderClick(rootPrefix)}
                className="flex items-center gap-1 cursor-pointer s3-explr-breadcrumb-home"
              >
                {homeLabel}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {!idCreator &&
              prefixSegments.map((segment, index) => (
                <div key={index} className="flex items-center">
                  <BreadcrumbSeparator
                    key={`sep-${index}`}
                    className="s3-explr-breadcrumb-separator"
                  />
                  <BreadcrumbItem key={`item-${segment}`} className="s3-explr-breadcrumb-item">
                    <BreadcrumbLink
                      onClick={() =>
                        handleFolderClick(
                          rootPrefix + prefixSegments.slice(0, index + 1).join('/') + '/',
                        )
                      }
                      className="cursor-pointer s3-explr-breadcrumb-link"
                    >
                      {segment}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </div>
              ))}
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center space-x-4 s3-explr-toolbar">
          <div className="relative max-w-sm w-full s3-explr-search-container">
            <SearchBox
              ref={searchBoxRef}
              onChange={(searchQuery1) => setSearchQuery(searchQuery1)}
              loading={isLoading}
            />
          </div>

          {currentPrefix && currentPrefix !== '/' && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (isLoading || isSyncing) return;
                      setIsSyncing(true);
                      handleSyncKnowledgeBase();
                    }}
                    aria-disabled={isLoading || isSyncing}
                    className="flex items-center gap-2 s3-explr-refresh-btn aria-disabled:opacity-50"
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 s3-explr-refresh-icon ${isLoading || isSyncing ? 'animate-spin' : ''}`}
                    />
                    Đồng bộ KB
                  </Button>
                </TooltipTrigger>
                {syncJobStatus && (
                  <TooltipContent side="right" sideOffset={5}>
                    <div className="space-y-2 p-3 max-w-md">
                      <p className="font-semibold text-lg border-b pb-1">Thông tin đồng bộ</p>

                      {/* Status Section */}
                      <div className="space-y-1">
                        <p className="flex items-center gap-2">
                          <span className="font-medium">Trạng thái:</span>
                          <span
                            className={`flex items-center gap-1 font-semibold ${
                              syncJobStatus.status === 'COMPLETE'
                                ? 'text-green-600'
                                : syncJobStatus.status === 'FAILED'
                                  ? 'text-red-600'
                                  : 'text-yellow-600'
                            }`}
                          >
                            {syncJobStatus.status === 'COMPLETE' && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                            {syncJobStatus.status === 'FAILED' && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            )}
                            {syncJobStatus.status !== 'COMPLETE' &&
                              syncJobStatus.status !== 'FAILED' &&
                              syncJobStatus.status !== 'STOPPED' && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                              )}
                            {getSyncStatusInVietnamese(syncJobStatus.status)}
                          </span>
                        </p>
                      </div>

                      {/* Statistics Section */}
                      {syncJobStatus.statistics && (
                        <div className="space-y-1 border-t pt-2">
                          <p className="font-medium text-gray-50">Thống kê xử lý:</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Tài liệu quét:</span>
                              <span className="font-medium">
                                {syncJobStatus.statistics.numberOfDocumentsScanned}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Metadata quét:</span>
                              <span className="font-medium">
                                {syncJobStatus.statistics.numberOfMetadataDocumentsScanned}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Tài liệu mới:</span>
                              <span className="font-medium text-green-600">
                                {syncJobStatus.statistics.numberOfNewDocumentsIndexed}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Tài liệu sửa:</span>
                              <span className="font-medium text-blue-600">
                                {syncJobStatus.statistics.numberOfModifiedDocumentsIndexed}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Metadata sửa:</span>
                              <span className="font-medium text-blue-600">
                                {syncJobStatus.statistics.numberOfMetadataDocumentsModified}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Tài liệu xóa:</span>
                              <span className="font-medium text-orange-600">
                                {syncJobStatus.statistics.numberOfDocumentsDeleted}
                              </span>
                            </div>
                            <div className="flex justify-between col-span-2">
                              <span>Tài liệu lỗi:</span>
                              <span className="font-medium text-red-600">
                                {syncJobStatus.statistics.numberOfDocumentsFailed}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Job Details Section */}
                      <div className="space-y-1 border-t pt-2 text-sm text-gray-200">
                        <p className="font-medium text-gray-50">Chi tiết Job:</p>
                        <p>
                          <span className="font-medium">DataSource ID:</span>{' '}
                          {syncJobStatus.dataSourceId}
                        </p>
                        <p>
                          <span className="font-medium">Ingestion Job ID:</span>{' '}
                          {syncJobStatus.ingestionJobId}
                        </p>
                        <p>
                          <span className="font-medium">Knowledge Base ID:</span>{' '}
                          {syncJobStatus.knowledgeBaseId}
                        </p>
                        <p className="break-all">
                          <span className="font-medium">OpenSearch Node:</span>{' '}
                          {syncJobStatus.node ? (
                            <span className="text-gray-100">{syncJobStatus.node}</span>
                          ) : (
                            <span className="italic text-gray-400">Chưa có</span>
                          )}
                        </p>
                        <p className="break-all">
                          <span className="font-medium">Index Name:</span>{' '}
                          {syncJobStatus.index_name ? (
                            <span className="text-gray-100">{syncJobStatus.index_name}</span>
                          ) : (
                            <span className="italic text-gray-400">Chưa có</span>
                          )}
                        </p>
                        <p>
                          <span className="font-medium">Job trong queue:</span>{' '}
                          {syncJobStatus.is_still_in_stock ? 'Còn' : 'Không'}
                        </p>
                      </div>

                      {/* Error Message */}
                      {syncJobStatus.errorMessage && (
                        <div className="border-t pt-2">
                          <p className="text-red-600 font-medium">Lỗi:</p>
                          <p className="text-red-600 text-sm bg-red-50 p-2 rounded">
                            {syncJobStatus.errorMessage}
                          </p>
                        </div>
                      )}

                      {/* Failed Files Section */}
                      {syncJobStatus.failureReasons && syncJobStatus.failureReasons.length > 0 && (
                        <FailedFilesDisplay
                          failureReasons={syncJobStatus.failureReasons}
                          onNavigateToFile={handleNavigateToFailedFile}
                        />
                      )}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Nút lịch sử báo cáo - chỉ hiển thị trong thư mục `bld-reports/` */}
          {isBldReportsFolder && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportHistoryOpen(true)}
              className="flex items-center gap-2 s3-explr-report-history-btn"
            >
              <ClockIcon className="h-4 w-4" />
              Lịch sử báo cáo
            </Button>
          )}
          {/* Nút Add file mới */}
          {canEditDocument((fileSystem as any)?.prefixPermission) && (
            <div className="relative" ref={addFileMenuRef}>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 s3-explr-add-file-btn bg-[#1682fe] hover:bg-[#0f6cd4] text-white rounded-md px-4 py-2 transition-colors aria-disabled:hidden"
                onClick={() => setShowAddFileMenu((prev) => !prev)}
                aria-disabled={false}
              >
                <PlusIcon className="w-4 h-4" />
                Add file
              </Button>
              {showAddFileMenu && (
                <div className="absolute z-50 mt-2 right-0 w-56 bg-white border rounded-lg shadow-lg py-2 flex flex-col">
                  <button
                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left"
                    onClick={() => {
                      setShowAddFileMenu(false);
                      // Create a hidden file input for files
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.onchange = (e) => {
                        const pickedFiles = (e.target as HTMLInputElement).files;
                        if (pickedFiles && pickedFiles.length > 0) {
                          // Lọc tệp sai định dạng theo quy tắc của kho (tải tệp thủ công)
                          const files = filterFilesByAllowedExtensions(pickedFiles, currentPrefix);
                          if (!files || files.length === 0) {
                            return;
                          }
                          // Kiểm tra tổng dung lượng lần upload tối đa 100MB, không giới hạn số lượng file.
                          if (checkConditionUploadFiles(files, toast)) {
                            return;
                          }
                          // Check for empty files
                          const emptyFiles = Array.from(files).filter((file) => file.size === 0);
                          if (emptyFiles.length > 0) {
                            toast({
                              title: 'Lỗi tải lên',
                              description: `Không thể tải lên tập tin rỗng: ${emptyFiles.map((f) => f.name).join(', ')}`,
                              variant: 'destructive',
                            });
                            return;
                          }
                          // Check if this path requires the metadata form
                          if (requiresMetadataForm(currentPrefix)) {
                            // Check for compressed files FIRST before proceeding with upload
                            const compressionCheck = validateFilesForCompression(files);
                            if (compressionCheck.hasCompressed) {
                              toast({
                                title: 'Lỗi tải lên',
                                description: `Không thể tải lên file nén: ${compressionCheck.compressedFiles.join(', ')}. Vui lòng giải nén trước khi tải lên.`,
                                variant: 'destructive',
                              });
                              setShowUploadFormDialog(false);
                              setPendingUploadInfo(null);
                              setUploadFormErrors([]);
                              return;
                            }

                            setPendingUploadInfo({
                              files,
                              prefix: currentPrefix,
                              type: 'files',
                            });
                            setShowUploadFormDialog(true);
                            // Reset form for new upload
                            setUploadMetadataForm({
                              ...defaultUploadMetadata,
                              creator: user?.username || '',
                              modifier: user?.username || '',
                              modifiedDate: new Date().toLocaleDateString('vi-VN'),
                            });
                          } else {
                            // Upload directly without form dialog
                            void uploadFiles(files, currentPrefix, 'files');
                          }
                        }
                      };
                      input.click();
                    }}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4 mr-2" /> Tải tệp tin
                  </button>
                  <button
                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left"
                    onClick={() => {
                      setShowAddFileMenu(false);
                      // Create a hidden file input for folder
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.webkitdirectory = true;
                      input.onchange = (e) => {
                        const pickedFiles = (e.target as HTMLInputElement).files;
                        if (pickedFiles && pickedFiles.length > 0) {
                          // Lọc tệp sai định dạng trong thư mục theo quy tắc của kho (tải thư mục thủ công)
                          const files = filterFilesByAllowedExtensions(pickedFiles, currentPrefix);
                          if (!files || files.length === 0) {
                            return;
                          }
                          // Kiểm tra tổng dung lượng lần upload tối đa 100MB, không giới hạn số lượng file.
                          if (checkConditionUploadFiles(files, toast)) {
                            return;
                          }
                          // Check for empty files
                          const emptyFiles = Array.from(files).filter((file) => file.size === 0);
                          if (emptyFiles.length > 0) {
                            toast({
                              title: 'Lỗi tải lên',
                              description: `Không thể tải lên tập tin rỗng: ${emptyFiles.map((f) => f.name).join(', ')}`,
                              variant: 'destructive',
                            });
                            return;
                          }
                          // Check if this path requires the metadata form
                          if (requiresMetadataForm(currentPrefix)) {
                            // Check for compressed files FIRST before proceeding with upload
                            const compressionCheck = validateFilesForCompression(files);
                            if (compressionCheck.hasCompressed) {
                              toast({
                                title: 'Lỗi tải lên',
                                description: `Không thể tải lên file nén: ${compressionCheck.compressedFiles.join(', ')}. Vui lòng giải nén trước khi tải lên.`,
                                variant: 'destructive',
                              });
                              setShowUploadFormDialog(false);
                              setPendingUploadInfo(null);
                              setUploadFormErrors([]);
                              return;
                            }

                            setPendingUploadInfo({
                              files,
                              prefix: currentPrefix,
                              type: 'folder',
                            });
                            setShowUploadFormDialog(true);
                            // Reset form for new upload
                            setUploadMetadataForm({
                              ...defaultUploadMetadata,
                              creator: user?.username || '',
                              modifier: user?.username || '',
                              modifiedDate: new Date().toLocaleDateString('vi-VN'),
                            });
                          } else {
                            // Upload directly without form dialog
                            void uploadFiles(files, currentPrefix, 'folder');
                          }
                        }
                      };
                      input.click();
                    }}
                  >
                    <FolderIcon className="h-4 w-4 mr-2" /> Tải lên thư mục
                  </button>
                  <button
                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left"
                    disabled={Boolean(displayPrefixes) && !currentPrefix}
                    onClick={() => setIsCreateFolderOpen(true)}
                  >
                    <FolderPlusIcon className="h-4 w-4 mr-2 s3-explr-folder-plus-icon" />
                    Thư mục mới
                  </button>
                  <button
                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left"
                    onClick={() => {
                      setShowAddFileMenu(false);
                      setIsWebsiteDialogOpen(true);
                    }}
                  >
                    <GlobeAltIcon className="h-4 w-4 mr-2" /> Thêm Website
                  </button>
                  <button
                    className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left ${hiddenSynchronization ? 'hidden' : ''}`}
                    onClick={() => {
                      setShowAddFileMenu(false);
                      login();
                    }}
                  >
                    <svg
                      viewBox="0 0 87.3 78"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                    >
                      <path
                        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                        fill="#0066da"
                      />
                      <path
                        d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                        fill="#00ac47"
                      />
                      <path
                        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                        fill="#ea4335"
                      />
                      <path
                        d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                        fill="#00832d"
                      />
                      <path
                        d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                        fill="#2684fc"
                      />
                      <path
                        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                        fill="#ffba00"
                      />
                    </svg>{' '}
                    Đồng bộ Google Drive
                  </button>
                  <button
                    className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left ${hiddenSynchronization ? 'hidden' : ''}`}
                    onClick={() => {
                      setShowAddFileMenu(false);
                      loginOneDrive();
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 64 64"
                      className="h-4 w-4 mr-2"
                    >
                      <path
                        fill="#094ab2"
                        d="M47.3 28.2C45.3 18.6 37.1 12 28 12c-8.5 0-15.9 5.6-18.4 13.5C4.2 27.2 0 32.2 0 38c0 6.6 5.4 12 12 12h36c8.3 0 16-6.6 16-15.5 0-6.8-4.9-12.4-11.7-13.3z"
                      />
                      <path
                        fill="#0067c5"
                        d="M44.9 30.1c-.2 0-.4-.1-.6-.1H26c-3.3 0-6 2.7-6 6 0 .9.2 1.8.6 2.6-1.7-.7-3.5-1.1-5.5-1.1C7.6 37.5 4 41.5 4 46s3.6 8.5 8.1 8.5h34.3c6.8 0 12.6-5.4 12.6-12s-5.5-11.6-11.1-12.4z"
                      />
                    </svg>{' '}
                    Đồng bộ OneDrive
                  </button>
                  <button
                    className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-left ${hiddenSynchronization ? 'hidden' : ''}`}
                    onClick={() => {
                      setShowAddFileMenu(false);
                      loginSharePoint();
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="h-4 w-4 mr-2"
                    >
                      <path
                        fill="#036C70"
                        d="M19 14.5v-9c0-.28-.22-.5-.5-.5h-13c-.28 0-.5.22-.5.5v9c0 .28.22.5.5.5h13c.28 0 .5-.22.5-.5z"
                      />
                      <path
                        fill="#17828F"
                        d="M16 17.5v-9c0-.28-.22-.5-.5-.5h-13c-.28 0-.5.22-.5.5v9c0 .28.22.5.5.5h13c.28 0 .5-.22.5-.5z"
                      />
                      <path
                        fill="#4DB6AC"
                        d="M13 20.5v-9c0-.28-.22-.5-.5-.5h-13c-.28 0-.5.22-.5.5v9c0 .28.22.5.5.5h13c.28 0 .5-.22.5-.5z"
                      />
                      <path
                        fill="#fff"
                        d="M4.5 15.5v-3c0-.28.22-.5.5-.5h3c.28 0 .5.22.5.5v3c0 .28-.22.5-.5.5H5c-.28 0-.5-.22-.5-.5z"
                      />
                    </svg>{' '}
                    Đồng bộ SharePoint
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PendingApprovalPage ở đây*/}

          {!isViewOnly &&
            (currentPrefix === 'kho_doi_ngoai/' || currentPrefix.startsWith('kho_doi_ngoai/')) && (
              <PendingApprovalPage
                currentPrefix={currentPrefix}
                apiBaseUrl={apiBaseUrl}
                isUploading={_isUploading}
              />
            )}

          {/* Email crawler settings button - `bld-reports/` (BLD Reports) hoặc crawler theo folder */}
          {showCrawlerSettingsButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={openCrawlerSettingsModal}
              disabled={resolvedCrawlerSourceLoading}
              className="h-11 w-11 p-0"
              aria-label={
                crawlerProcessorType === 'recruitment'
                  ? 'Cấu hình crawler tuyển dụng'
                  : 'Cấu hình Email Scheduler'
              }
              title={
                crawlerProcessorType === 'recruitment'
                  ? 'Cấu hình crawler tuyển dụng'
                  : 'Cấu hình Email Scheduler'
              }
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </Button>
          )}

          {canEditDocument(fileSystem['prefixPermission'] as any) &&
            currentPrefix.split('/').length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 s3-explr-add-file-btn bg-[#1682fe] hover:bg-[#0f6cd4] text-white rounded-md px-4 py-2 transition-colors aria-disabled:hidden"
                onClick={() =>
                  setFileToShare({
                    contentType: 'application/x-directory',
                    isFolder: true,
                    key: currentPrefix || '/',
                    lastModified: '-' as unknown as Date,
                    name: '',
                    size: 0,
                  })
                }
              >
                <ShareIcon className="w-4 h-4" />
                Chia sẻ
              </Button>
            )}

          {!asSelector && (
            <>
              {selectedKeys.size > 0 && canEditDocument((fileSystem as any)?.prefixPermission) && (
                <>
                  <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex items-center gap-2 s3-explr-bulk-delete-btn"
                      >
                        <TrashIcon className="h-4 w-4 s3-explr-trash-icon" />
                        Xóa đã chọn ({selectedKeys.size})
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="s3-explr-dialog-content">
                      <DialogHeader className="s3-explr-dialog-header">
                        <DialogTitle className="s3-explr-dialog-title">
                          Xác nhận xóa hàng loạt
                        </DialogTitle>
                        <DialogDescription className="s3-explr-dialog-description">
                          Bạn có chắc chắn muốn xóa {selectedKeys.size} mục đã chọn? Hành động này
                          không thể hoàn tác.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="s3-explr-dialog-footer">
                        <Button
                          variant="outline"
                          onClick={() => setShowBulkDeleteDialog(false)}
                          className="s3-explr-cancel-btn"
                        >
                          Hủy
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleRemoveSelected}
                          disabled={isBulkDeleting}
                          className="s3-explr-confirm-delete-btn"
                        >
                          {isBulkDeleting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                              Đang xóa...
                            </>
                          ) : (
                            'Xóa đã chọn'
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              {!idCreator && (
                <Dialog open={isCreateFolderOpen} onOpenChange={setIsCreateFolderOpen}>
                  <DialogTrigger asChild></DialogTrigger>
                  <DialogContent className="s3-explr-dialog-content">
                    <DialogHeader className="s3-explr-dialog-header">
                      <DialogTitle className="s3-explr-dialog-title">Tạo thư mục mới</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 s3-explr-dialog-body">
                      <div className="space-y-2 s3-explr-input-group">
                        <Label htmlFor="name" className="s3-explr-label">
                          Tên thư mục
                        </Label>
                        <Input
                          disabled={isCreatingFolder}
                          id="name"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Nhập tên thư mục"
                          className="s3-explr-input"
                        />
                      </div>
                      <Button
                        onClick={() => {
                          if (newFolderName.trim()) {
                            void createFolder(newFolderName, currentPrefix);
                          }
                        }}
                        disabled={isCreatingFolder}
                        className="s3-explr-create-btn"
                      >
                        {isCreatingFolder && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                        )}
                        {isCreatingFolder ? 'Đang tạo...' : 'Tạo'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              {idCreator && !currentPrefix ? (
                <></>
              ) : (
                <div className="flex items-center gap-2 s3-explr-upload-group">
                  {/*<Button*/}
                  {/*  variant="outline"*/}
                  {/*  size="sm"*/}
                  {/*  className="flex items-center gap-2 s3-explr-upload-files-btn"*/}
                  {/*  onClick={() => {*/}
                  {/*    const input = document.createElement('input');*/}
                  {/*    input.type = 'file';*/}
                  {/*    input.multiple = true;*/}
                  {/*    input.onchange = (e) => {*/}
                  {/*      const files = (e.target as HTMLInputElement).files;*/}
                  {/*      if (files) {*/}
                  {/*        void uploadFiles(files, currentPrefix, 'files');*/}
                  {/*      }*/}
                  {/*    };*/}
                  {/*    input.click();*/}
                  {/*  }}*/}
                  {/*  disabled={uploadType !== null || (Boolean(displayPrefixes) && !currentPrefix)}*/}
                  {/*>*/}
                  {/*  {uploadType === 'files' ? (*/}
                  {/*    <>*/}
                  {/*      <Loader2 className="h-4 w-4 animate-spin mr-2 s3-explr-loader" />*/}
                  {/*      Đang xử lý...*/}
                  {/*    </>*/}
                  {/*  ) : (*/}
                  {/*    <>*/}
                  {/*      <ArrowUpTrayIcon className="h-4 w-4 s3-explr-upload-icon" />*/}
                  {/*      Tải lên tập tin*/}
                  {/*    </>*/}
                  {/*  )}*/}
                  {/*</Button>*/}

                  {/*<Button*/}
                  {/*  variant="outline"*/}
                  {/*  size="sm"*/}
                  {/*  className="flex items-center gap-2 s3-explr-upload-folder-btn"*/}
                  {/*  onClick={() => {*/}
                  {/*    const input = document.createElement('input');*/}
                  {/*    input.type = 'file';*/}
                  {/*    input.multiple = true;*/}
                  {/*    input.webkitdirectory = true;*/}
                  {/*    input.onchange = (e) => {*/}
                  {/*      const files = (e.target as HTMLInputElement).files;*/}
                  {/*      if (files) {*/}
                  {/*        void uploadFiles(files, currentPrefix, 'folder');*/}
                  {/*      }*/}
                  {/*    };*/}
                  {/*    input.click();*/}
                  {/*  }}*/}
                  {/*  disabled={uploadType !== null || (Boolean(displayPrefixes) && !currentPrefix)}*/}
                  {/*>*/}
                  {/*  {uploadType === 'folder' ? (*/}
                  {/*    <>*/}
                  {/*      <Loader2 className="h-4 w-4 animate-spin mr-2 s3-explr-loader" />*/}
                  {/*      Đang tải lên...*/}
                  {/*    </>*/}
                  {/*  ) : (*/}
                  {/*    <>*/}
                  {/*      <FolderIcon className="h-4 w-4 s3-explr-folder-icon" />*/}
                  {/*      Tải lên thư mục*/}
                  {/*    </>*/}
                  {/*  )}*/}
                  {/*</Button>*/}

                  {currentJob ? (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 s3-explr-add-website-btn cursor-pointer"
                            onClick={(e) => {
                              // Check if click is on stop icon area
                              const target = e.target as HTMLElement;
                              if (target.closest('.stop-icon-wrapper')) {
                                e.stopPropagation();
                                void stopCurrentJob();
                              } else {
                                setIsCrawlProgressDialogOpen(true);
                              }
                            }}
                          >
                            <Loader2 className="h-4 w-4 animate-spin s3-explr-loader" />
                            {(() => {
                              const hours = Math.floor(elapsedTime / 3600);
                              const mins = Math.floor((elapsedTime % 3600) / 60);
                              const secs = elapsedTime % 60;
                              const timeStr =
                                hours > 0
                                  ? `${hours}h${mins}m${secs}s`
                                  : mins > 0
                                    ? `${mins}m${secs}s`
                                    : `${secs}s`;

                              // Get status text based on crawl state
                              const status = currentJob?.status;
                              let statusText = 'Đang xử lý';

                              if (!status || (status.total === 0 && status.completed === 0)) {
                                statusText = 'Đang khởi tạo';
                              } else if (status.status === 'completed' && status.isProcessing) {
                                statusText = 'Đang lưu dữ liệu';
                              } else if (status.status === 'scraping') {
                                statusText = status.isProcessing
                                  ? 'Đang thu thập'
                                  : 'Đang quét website';
                              }

                              return `${statusText} (${timeStr})`;
                            })()}
                            <span
                              className="stop-icon-wrapper ml-1 hover:bg-red-100 rounded p-1 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                void stopCurrentJob();
                              }}
                            >
                              <StopIcon className="h-4 w-4 text-red-600" />
                            </span>
                          </Button>
                        </TooltipTrigger>
                        {currentJob.status && (
                          <TooltipContent side="right" sideOffset={5}>
                            <div className="space-y-1 p-2 max-w-md break-words">
                              <p className="font-semibold">Đang thu thập {currentJob?.url}</p>
                              <p>
                                Tiến độ: {currentJob?.status.completed}/{currentJob?.status.total}{' '}
                                trang
                              </p>
                              <p>
                                Thời gian:{' '}
                                {(() => {
                                  const hours = Math.floor(elapsedTime / 3600);
                                  const mins = Math.floor((elapsedTime % 3600) / 60);
                                  const secs = elapsedTime % 60;
                                  return hours > 0
                                    ? `${hours}h${mins}m${secs}s`
                                    : mins > 0
                                      ? `${mins}m${secs}s`
                                      : `${secs}s`;
                                })()}
                              </p>
                              <p>
                                Trạng thái:{' '}
                                {currentJob?.status.status === 'scraping'
                                  ? 'Đang thu thập'
                                  : currentJob?.status.status === 'completed'
                                    ? 'Hoàn thành'
                                    : 'Thất bại'}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">Nhấn để xem chi tiết</p>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    !idCreator && (
                      <Dialog open={isWebsiteDialogOpen} onOpenChange={setIsWebsiteDialogOpen}>
                        <DialogTrigger asChild>
                          {/*<Button*/}
                          {/*  variant="outline"*/}
                          {/*  size="sm"*/}
                          {/*  className="flex items-center gap-2 s3-explr-add-website-btn"*/}
                          {/*  disabled={Boolean(displayPrefixes) && !currentPrefix}*/}
                          {/*>*/}
                          {/*  <GlobeAltIcon className="h-4 w-4 s3-explr-globe-icon" />*/}
                          {/*  Thêm website*/}
                          {/*</Button>*/}
                        </DialogTrigger>
                        <DialogContent className="s3-explr-dialog-content max-h-[80vh] overflow-y-auto">
                          <DialogHeader className="s3-explr-dialog-header">
                            <DialogTitle className="s3-explr-dialog-title">
                              Thêm website
                            </DialogTitle>
                            <DialogDescription className="s3-explr-dialog-description">
                              Nhập URL website để thu thập và lưu trữ.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4 s3-explr-dialog-body">
                            <div className="space-y-2 s3-explr-input-group">
                              <Label htmlFor="websiteUrl" className="s3-explr-label">
                                URL Website
                              </Label>
                              <Input
                                id="websiteUrl"
                                value={websiteUrl}
                                onChange={(e) => {
                                  setWebsiteUrl(e.target.value);
                                  // Real-time validation: check if URL starts with https://
                                  if (
                                    e.target.value &&
                                    !e.target.value.trim().startsWith('https://')
                                  ) {
                                    setWebsiteUrlError('URL phải bắt đầu với https://');
                                  } else {
                                    setWebsiteUrlError('');
                                  }
                                }}
                                placeholder="https://example.com"
                                type="url"
                                disabled={isCrawling}
                                className="s3-explr-input"
                              />
                              {websiteUrlError && (
                                <p className="text-xs text-red-500">{websiteUrlError}</p>
                              )}
                            </div>

                            {/* S3 Folder Selection Section - Tree Selector */}
                            <div className="space-y-2 s3-explr-input-group border rounded-lg p-3 border-red-300">
                              <S3FolderTreeSelector
                                apiBaseUrl={apiBaseUrl}
                                value={selectedS3Folder}
                                onChange={(value) => setSelectedS3Folder(value)}
                                label="Thư mục lưu trữ trong S3"
                                required
                                showSelectedInfo
                              />
                            </div>

                            <div className="space-y-2 s3-explr-input-group">
                              <div className="flex items-center justify-between">
                                <Label htmlFor="crawlLimit" className="s3-explr-label">
                                  Số trang đồng bộ
                                </Label>
                              </div>
                            </div>
                            <div className="space-y-2 s3-explr-input-group">
                              <p className="text-xs text-gray-500 leading-relaxed">
                                Khi bật, hệ thống sẽ tự động phân tích URL và thu thập tất cả các
                                liên kết có prefix giống với URL gốc (ví dụ: news → thu thập news-1,
                                news-2, news-3...).
                              </p>
                              <Input
                                id="crawlLimit"
                                value={crawlLimit}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    // Only allow digits
                                    setCrawlLimit(value);
                                  }
                                }}
                                placeholder="1"
                                type="text"
                                disabled={isCrawling}
                                className="s3-explr-input"
                              />
                            </div>
                            <div className="space-y-2 s3-explr-input-group">
                              <p className="text-xs text-gray-500 leading-relaxed">
                                Khi bật, hệ thống sẽ tự động phát hiện URL có phân trang (ví dụ:{' '}
                                <code className="bg-gray-100 px-1 rounded">?page=1</code>) và crawl
                                các trang tiếp theo (page=2, page=3, ...).
                              </p>
                              <Input
                                id="maxPagesToCrawl"
                                value={maxPagesToCrawl}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    setMaxPagesToCrawl(value);
                                  }
                                }}
                                placeholder="10"
                                type="text"
                                disabled={isCrawling}
                                className="s3-explr-input"
                              />
                              <p className="text-xs text-gray-500">
                                Số trang tối đa sẽ crawl (mặc định: 10)
                              </p>
                            </div>
                            <div className="space-y-2 s3-explr-input-group">
                              <Label htmlFor="maxDepth" className="s3-explr-label">
                                Độ sâu thu thập
                              </Label>
                              <Input
                                id="maxDepth"
                                value={maxDepth}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    // Only allow digits
                                    setMaxDepth(value);
                                  }
                                }}
                                placeholder="10"
                                type="text"
                                disabled={isCrawling}
                                className="s3-explr-input"
                              />
                            </div>
                          </div>
                          <DialogFooter className="s3-explr-dialog-footer">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setIsWebsiteDialogOpen(false);
                                setWebsiteUrl('');
                                setCrawlLimit('10');
                                setMaxDepth('2');
                                setMaxPagesToCrawl('10');
                              }}
                              disabled={isCrawling}
                              className="s3-explr-cancel-btn"
                            >
                              Hủy
                            </Button>
                            {currentJob ? (
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button disabled className="s3-explr-crawl-btn">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                                        Đang thu thập...
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" sideOffset={5}>
                                    Đang có một tiến trình thu thập đang chạy. Vui lòng chờ hoàn
                                    thành trước khi bắt đầu mới.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <Button
                                onClick={() => {
                                  // Validate URL starts with https://
                                  if (!websiteUrl.trim().startsWith('https://')) {
                                    toast({
                                      title: 'Lỗi',
                                      description: 'URL phải bắt đầu với https://',
                                      variant: 'destructive',
                                    });
                                    return;
                                  }

                                  if (selectedS3Folder) {
                                    void crawlWebsite(
                                      websiteUrl.trim(),
                                      parseInt(crawlLimit) || 1,
                                      parseInt(maxDepth) || 10,
                                      parseInt(maxPagesToCrawl) || 10,
                                    );
                                  }
                                }}
                                disabled={
                                  isCrawling ||
                                  !websiteUrl.trim() ||
                                  !websiteUrl.trim().startsWith('https://') ||
                                  !selectedS3Folder ||
                                  isFetchingFolders
                                }
                                className="s3-explr-crawl-btn"
                              >
                                {isCrawling ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                                    Đang thu thập...
                                  </>
                                ) : (
                                  'Bắt đầu thu thập'
                                )}
                              </Button>
                            )}
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )
                  )}

                  {currentPrefix.startsWith('BKTTW/') && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 s3-explr-add-website-btn"
                            disabled={Boolean(displayPrefixes) && !currentPrefix}
                            onClick={handleExportReport}
                          >
                            {isLoadingHandleExportReport && (
                              <ArrowPathIcon
                                className={`h-4 w-4 s3-explr-refresh-icon ${isLoadingHandleExportReport ? 'animate-spin' : ''}`}
                              />
                            )}
                            Xuất báo cáo
                          </Button>
                        </TooltipTrigger>
                        {currentJob?.status && (
                          <TooltipContent side="right" sideOffset={5}>
                            <div className="space-y-1 p-2">
                              <p className="font-semibold">Đang thu thập {currentJob!.url}</p>
                              <p>
                                Tiến độ: {currentJob!.status.completed}/{currentJob!.status.total}{' '}
                                trang
                              </p>
                              <p>
                                Thời gian:{' '}
                                {(() => {
                                  const hours = Math.floor(elapsedTime / 3600);
                                  const mins = Math.floor((elapsedTime % 3600) / 60);
                                  const secs = elapsedTime % 60;
                                  return hours > 0
                                    ? `${hours}h${mins}m${secs}s`
                                    : mins > 0
                                      ? `${mins}m${secs}s`
                                      : `${secs}s`;
                                })()}
                              </p>
                              <p>
                                Trạng thái:{' '}
                                {currentJob!.status.status === 'scraping'
                                  ? 'Đang thu thập'
                                  : currentJob!.status.status === 'completed'
                                    ? 'Hoàn thành'
                                    : 'Thất bại'}
                              </p>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}

              {currentPrefix.startsWith('BKTTW/') && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 s3-explr-add-website-btn"
                        disabled={Boolean(displayPrefixes) && !currentPrefix}
                        onClick={handleExportReport}
                      >
                        {isLoadingHandleExportReport && (
                          <ArrowPathIcon
                            className={`h-4 w-4 s3-explr-refresh-icon ${isLoadingHandleExportReport ? 'animate-spin' : ''}`}
                          />
                        )}
                        Xuất báo cáo
                      </Button>
                    </TooltipTrigger>
                    {isLoadingHandleExportReport && (
                      <TooltipContent sideOffset={5}>Đang xuất báo cáo</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}

              <div className="flex items-center gap-4">
                {showOneDriveModal && (
                  <Modal.Root open={showOneDriveModal} onOpenChange={setShowOneDriveModal}>
                    <Modal.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
                    <Modal.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-w-[90vw] max-h-[80vh] z-50 flex flex-col">
                      <div className="relative">
                        <Modal.Close asChild>
                          <button
                            className="absolute top-2 right-2 text-gray-600"
                            onClick={handleCloseOneDriveModal}
                          >
                            ✖
                          </button>
                        </Modal.Close>
                        <h2 className="text-lg font-semibold">My OneDrive</h2>
                        <Breadcrumb className="onedrive-breadcrumb">
                          <BreadcrumbList className="onedrive-breadcrumb-list">
                            {breadcrumbOneDrive.map((item, index) => (
                              <div key={index} className="flex items-center">
                                {index > 0 && (
                                  <BreadcrumbSeparator className="onedrive-breadcrumb-separator" />
                                )}
                                <BreadcrumbItem className="onedrive-breadcrumb-item">
                                  <BreadcrumbLink
                                    onClick={() => handleOneDriveFolderNavigation(index)}
                                    className="cursor-pointer onedrive-breadcrumb-link"
                                  >
                                    {item.name}
                                  </BreadcrumbLink>
                                </BreadcrumbItem>
                              </div>
                            ))}
                          </BreadcrumbList>
                        </Breadcrumb>
                      </div>
                      <div className="space-y-2 mt-4 flex-1 overflow-y-auto">
                        {isLoadingOneDrive ? (
                          <div className="text-sm text-gray-500">
                            Đang tải nội dung thư mục... Vui lòng đợi.
                          </div>
                        ) : (
                          <>
                            {[...folders, ...files].length > 0 ? (
                              [...folders, ...files].map((item) => {
                                const selectionState = getOneDriveItemSelectionState(
                                  item,
                                  selectedOneDriveItems,
                                  excludedOneDriveChildIds,
                                  breadcrumbOneDrive,
                                );
                                return (
                                  <div
                                    key={item.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 ${
                                      selectionState === true || selectionState === 'indeterminate'
                                        ? 'bg-blue-100'
                                        : ''
                                    }`}
                                  >
                                    <Checkbox
                                      checked={selectionState}
                                      onCheckedChange={() => handleOneDriveSelectItem(item)}
                                      className="cursor-pointer"
                                    />

                                    {item.isFolder ? (
                                      <FolderIcon
                                        className="text-blue-500 w-5 h-5 cursor-pointer"
                                        onClick={() => fetchOneDriveFiles(item.id, item.name)}
                                      />
                                    ) : (
                                      <ArrowUpTrayIcon className="text-gray-500 w-5 h-5" />
                                    )}

                                    <div className="flex-1">
                                      <div
                                        className={`font-medium ${item.isFolder ? 'cursor-pointer text-blue-600' : ''}`}
                                        onClick={() => {
                                          if (item.isFolder) {
                                            fetchOneDriveFiles(item.id, item.name);
                                          } else {
                                            handleOneDriveSelectItem(item);
                                          }
                                        }}
                                      >
                                        {item.name}
                                      </div>

                                      {!item.isFolder && (
                                        <div className="text-xs text-gray-500">
                                          {formatBytes(item.size)} •{' '}
                                          {formatDate(item.lastModified.toISOString())}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-sm text-gray-500">
                                Không có thư mục hoặc tệp nào.
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <div className="flex justify-end">
                          <button
                            onClick={() => setIsOneDriveLogoutConfirmOpen(true)}
                            className="bottom-4 left-4 text-gray-600 hover:text-gray-800"
                            title="Đăng xuất OneDrive"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                              <polyline points="16 17 21 12 16 7" />
                              <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                          </button>
                          <Button
                            className={`absolute left-4 flex items-center gap-2 px-3 py-2 rounded-md text-white transition-colors ${
                              isAutoUpdateKB
                                ? 'bg-green-500 hover:bg-green-600'
                                : 'bg-gray-400 hover:bg-gray-500'
                            }`}
                            onClick={() => setIsAutoUpdateKB((prev) => !prev)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="2" y="6" width="20" height="12" rx="6" ry="6" />
                              <circle
                                cx={isAutoUpdateKB ? '16' : '8'}
                                cy="12"
                                r="2"
                                fill="currentColor"
                              />
                            </svg>
                            <span className="text-sm">Tự động cập nhật KB</span>
                          </Button>
                        </div>

                        <Button
                          onClick={() => {
                            syncOneDriveToS3(
                              selectedOneDriveItems,
                              currentPrefix,
                              excludedOneDriveChildIds,
                            );
                            handleCloseOneDriveModal();
                          }}
                          className="bg-[#1682fe] hover:bg-[#0f6cd4] text-white rounded-md px-4 py-2 transition-colors"
                          disabled={selectedOneDriveItems.length === 0}
                        >
                          Đồng bộ
                        </Button>
                      </div>
                    </Modal.Content>
                  </Modal.Root>
                )}

                <Modal.Root
                  open={isOneDriveLogoutConfirmOpen}
                  onOpenChange={setIsOneDriveLogoutConfirmOpen}
                >
                  <Modal.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-[90]" />
                  <Modal.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[400px] max-w-[90vw] z-[100]">
                    <h3 className="text-lg font-semibold mb-4">Xác nhận đăng xuất</h3>
                    <p className="text-gray-600 mb-6">
                      Bạn có chắc chắn muốn đăng xuất khỏi OneDrive?
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsOneDriveLogoutConfirmOpen(false)}
                      >
                        Hủy
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          logoutOneDrive();
                          setIsOneDriveLogoutConfirmOpen(false);
                        }}
                      >
                        Đăng xuất
                      </Button>
                    </div>
                  </Modal.Content>
                </Modal.Root>
                {isSharePointModalOpen && (
                  <Modal.Root open={isSharePointModalOpen} onOpenChange={setIsSharePointModalOpen}>
                    <Modal.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
                    <Modal.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-w-[90vw] max-h-[80vh] z-50 flex flex-col">
                      <div className="relative">
                        <Modal.Close asChild>
                          <button
                            className="absolute top-2 right-2 text-gray-600"
                            onClick={handleCloseSharePointModal}
                          >
                            ✖
                          </button>
                        </Modal.Close>
                        <div className="flex items-center gap-2">
                          {selectedSite && (
                            <button
                              onClick={() => setSelectedSite(null)}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                              </svg>
                            </button>
                          )}
                          <h2 className="text-lg font-semibold">My SharePoint</h2>
                        </div>
                        {selectedSite && (
                          <Breadcrumb className="sharepoint-breadcrumb">
                            <BreadcrumbList className="sharepoint-breadcrumb-list">
                              {sharePointBreadcrumbs.map((item, index) => (
                                <div key={index} className="flex items-center">
                                  {index > 0 && (
                                    <BreadcrumbSeparator className="sharepoint-breadcrumb-separator" />
                                  )}
                                  <BreadcrumbItem className="sharepoint-breadcrumb-item">
                                    <BreadcrumbLink
                                      onClick={() => handleSharePointFolderNavigation(index)}
                                      className="cursor-pointer sharepoint-breadcrumb-link"
                                    >
                                      {item.name}
                                    </BreadcrumbLink>
                                  </BreadcrumbItem>
                                </div>
                              ))}
                            </BreadcrumbList>
                          </Breadcrumb>
                        )}
                      </div>

                      {/* Vùng danh sách file/folder có thể cuộn */}
                      <div className="space-y-2 mt-4 flex-1 overflow-y-auto">
                        {isLoadingSharePoint ? (
                          <div className="text-sm text-gray-500">
                            Đang tải nội dung thư mục... Vui lòng đợi.
                          </div>
                        ) : selectedSite ? (
                          [...sharePointFolders, ...sharePointFiles].length === 0 ? (
                            <div className="text-sm text-gray-500">
                              Không có thư mục hoặc tệp nào.
                            </div>
                          ) : (
                            [...sharePointFolders, ...sharePointFiles].map((item) => {
                              const selectionState = getSharePointItemSelectionState(
                                item,
                                selectedSharePointItems,
                                excludedSharePointChildIds,
                                sharePointBreadcrumbs,
                                sharePointCache,
                              );

                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 ${
                                    selectionState === true || selectionState === 'indeterminate'
                                      ? 'bg-blue-100'
                                      : ''
                                  }`}
                                >
                                  <Checkbox
                                    checked={selectionState}
                                    onCheckedChange={() => handleSharePointSelectItem(item)}
                                    className="cursor-pointer"
                                  />
                                  {item.isFolder ? (
                                    <FolderIcon
                                      className="text-blue-500 w-5 h-5 cursor-pointer"
                                      onClick={() =>
                                        fetchSharePointFiles(
                                          item.id,
                                          item.name,
                                          selectedSite?.hostname || '',
                                          selectedSite?.sitePath || '',
                                        )
                                      }
                                    />
                                  ) : (
                                    <ArrowUpTrayIcon className="text-gray-500 w-5 h-5" />
                                  )}
                                  <div className="flex-1">
                                    <div
                                      className={`font-medium ${item.isFolder ? 'cursor-pointer text-blue-600' : ''}`}
                                      onClick={() => {
                                        if (item.isFolder) {
                                          fetchSharePointFiles(
                                            item.id,
                                            item.name,
                                            selectedSite?.hostname || '',
                                            selectedSite?.sitePath || '',
                                          );
                                        }
                                      }}
                                    >
                                      {item.name}
                                    </div>
                                    {!item.isFolder && (
                                      <div className="text-xs text-gray-500">
                                        {formatBytes(item.size)} •{' '}
                                        {item.lastModified.toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {sharePointSites.map((site) => (
                              <div
                                key={site.id}
                                className="cursor-pointer bg-white shadow-md rounded-xl p-5 flex flex-col justify-center items-start transition-shadow duration-200 hover:shadow-lg border border-gray-100 min-h-[120px]"
                                style={{
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'normal',
                                }}
                                onClick={() => handleSelectSite(site)}
                              >
                                <div
                                  className="font-bold text-lg mb-1 truncate w-full"
                                  title={site.name}
                                >
                                  {site.name}
                                </div>
                                <div
                                  className="text-sm text-gray-500 truncate w-full"
                                  title={site.description}
                                >
                                  {site.description}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Các nút hành động ở dưới cùng */}
                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        {selectedSite && (
                          <>
                            <Button
                              onClick={() => {
                                syncSharePointToS3(
                                  selectedSharePointItems,
                                  currentPrefix,
                                  excludedSharePointChildIds,
                                );
                                handleCloseSharePointModal();
                              }}
                              className="bg-[#1682fe] hover:bg-[#0f6cd4] text-white rounded-md px-4 py-2 transition-colors"
                              disabled={selectedSharePointItems.length === 0}
                            >
                              Đồng bộ
                            </Button>
                            <Button onClick={() => setSelectedSite(null)}>Quay lại</Button>
                          </>
                        )}
                      </div>
                      {selectedSite && (
                        <button
                          onClick={() => setIsAutoUpdateKB((prev) => !prev)}
                          className={`absolute bottom-4 left-4 flex items-center gap-2 px-3 py-2 rounded-md text-white transition-colors ${
                            isAutoUpdateKB
                              ? 'bg-green-500 hover:bg-green-600'
                              : 'bg-gray-400 hover:bg-gray-500'
                          }`}
                          title="Bật/tắt tự động cập nhật KB"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="2" y="6" width="20" height="12" rx="6" ry="6" />
                            <circle
                              cx={isAutoUpdateKB ? '16' : '8'}
                              cy="12"
                              r="2"
                              fill="currentColor"
                            />
                          </svg>
                          <span className="text-sm">Tự động cập nhật KB</span>
                        </button>
                      )}
                      {/* <button
                        onClick={() => setIsSharePointLogoutConfirmOpen(true)}
                        className="absolute bottom-4 left-4 text-gray-600 hover:text-gray-800"
                        title="Đăng xuất SharePoint"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </button> */}
                    </Modal.Content>
                  </Modal.Root>
                )}

                {/* SharePoint Logout Confirmation Modal */}
                <Modal.Root
                  open={isSharePointLogoutConfirmOpen}
                  onOpenChange={setIsSharePointLogoutConfirmOpen}
                >
                  <Modal.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-[60]" />
                  <Modal.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[400px] max-w-[90vw] z-[60]">
                    <h3 className="text-lg font-semibold mb-4">Xác nhận đăng xuất</h3>
                    <p className="text-gray-600 mb-6">
                      Bạn có chắc chắn muốn đăng xuất khỏi SharePoint?
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsSharePointLogoutConfirmOpen(false)}
                      >
                        Hủy
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          logoutSharePoint();
                          setIsSharePointLogoutConfirmOpen(false);
                        }}
                      >
                        Đăng xuất
                      </Button>
                    </div>
                  </Modal.Content>
                </Modal.Root>
              </div>

              <Dialog
                open={!!showInfoFlowsDialogs}
                onOpenChange={(open) => {
                  if (!open) {
                    setShowInfoFlowsDialogs(false);
                    setDataFlowModels([]);
                    setFolderToShowFlow('');
                  }
                }}
              >
                <DialogContent className="max-h-[700px] pb-3 px-1 s3-explr-input-group max-w-5xl w-[90%] p-6 bg-white rounded-lg shadow-lg">
                  <DialogHeader className="s3-explr-dialog-header">
                    <DialogTitle className="s3-explr-dialog-title">
                      <h3 className="font-medium mb-2">
                        Danh sách flows đang sử dụng dữ liệu từ folder(
                        <span className="font-bold">{folderToShowFlow}</span>):{' '}
                      </h3>
                    </DialogTitle>
                    <DialogDescription className="s3-explr-dialog-description">
                      {loadingFlowsDialogs ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span className="ml-2">Đang tải thông tin flows...</span>
                        </div>
                      ) : (
                        <div>
                          {dataFlowModels &&
                          Array.isArray(dataFlowModels) &&
                          dataFlowModels.length > 0 ? (
                            <div className="max-h-[400px] overflow-y-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="border border-gray-300 px-4 py-2 text-left">
                                      Tên Flow
                                    </th>
                                    <th className="border border-gray-300 px-4 py-2 text-left">
                                      KnowledgeBase Id
                                    </th>
                                    <th className="border border-gray-300 px-4 py-2 text-left">
                                      Loại
                                    </th>
                                    <th className="border border-gray-300 px-4 py-2 text-left">
                                      Ngày tạo
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dataFlowModels.map((flow: any, index: number) => (
                                    <tr
                                      key={index}
                                      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                    >
                                      <td className="border border-gray-300 px-4 py-2">
                                        {flow.name}
                                      </td>
                                      <td className="border border-gray-300 px-4 py-2">
                                        {flow.KnowledgeBaseId}
                                      </td>
                                      <td className="border border-gray-300 px-4 py-2">
                                        {flow.type === 'MULTIAGENT' ? 'Agent Flow' : 'Chat Flow'}
                                      </td>
                                      <td className="border border-gray-300 px-4 py-2">
                                        {flow.createdDate
                                          ? new Date(flow.createdDate).toLocaleString('vi-VN')
                                          : 'N/A'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-gray-500 italic py-2">
                              Không có flow nào sử dụng dữ liệu từ folder này
                            </div>
                          )}
                        </div>
                      )}
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>

              <Dialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
                <DialogContent className="s3-explr-dialog-content">
                  <DialogHeader className="s3-explr-dialog-header">
                    <DialogTitle className="s3-explr-dialog-title">Xác nhận xóa</DialogTitle>
                    <DialogDescription className="s3-explr-dialog-description break-words whitespace-pre-wrap">
                      Bạn có chắc chắn muốn xóa{' '}
                      <span className="font-medium break-all">{fileToDelete?.name}</span>? Hành động
                      này không thể hoàn tác.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="s3-explr-dialog-footer">
                    <Button
                      variant="outline"
                      onClick={() => setFileToDelete(null)}
                      className="s3-explr-cancel-btn"
                    >
                      Hủy
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(fileToDelete!)}
                      disabled={isDeleting}
                      className="s3-explr-confirm-delete-btn"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                          Đang xóa...
                        </>
                      ) : (
                        'Xóa'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog
                open={!!fileToRename}
                onOpenChange={(open) => {
                  if (!open) {
                    setFileToRename(null);
                    setMetadataForEdit(null);
                    setNewName('');
                    setFileContent('');
                    setActiveTab('rename');
                  }
                }}
              >
                <DialogContent className="s3-explr-dialog-content max-w-5xl w-[90%]">
                  <DialogHeader className="s3-explr-dialog-header">
                    <DialogTitle className="s3-explr-dialog-title">
                      {fileToRename?.isFolder
                        ? 'Đổi tên thư mục'
                        : isTextFile(fileToRename!) || isImageFile(fileToRename)
                          ? 'Quản lý tập tin'
                          : 'Sửa metadata tập tin'}
                    </DialogTitle>
                  </DialogHeader>
                  {fileToRename && (
                    <Tabs
                      value={activeTab}
                      onValueChange={(value: any) =>
                        setActiveTab(value as 'rename' | 'edit' | 'edit-metadata')
                      }
                    >
                      <TabsList
                        aria-checked={!isTextFile(fileToRename) && !isImageFile(fileToRename)}
                        aria-disabled={fileToRename.isFolder}
                        className="grid w-full grid-cols-3 aria-checked:grid-cols-2  aria-disabled:grid-cols-1"
                      >
                        {!fileToRename.isFolder && (
                          <>
                            <TabsTrigger value="edit-metadata">Sửa metadata file</TabsTrigger>
                            {(isTextFile(fileToRename) || isImageFile(fileToRename)) && (
                              <TabsTrigger value="edit">
                                {isImageFile(fileToRename) ? 'Sửa nội dung OCR' : 'Sửa nội dung'}
                              </TabsTrigger>
                            )}
                          </>
                        )}
                        <TabsTrigger value="rename">Đổi tên</TabsTrigger>
                      </TabsList>
                      <TabsContent value="rename">
                        <div className="space-y-4 py-4 s3-explr-dialog-body">
                          <div className="h-[70dvh] min-h-[500px] overflow-hidden overflow-y-auto pb-3 px-1 s3-explr-input-group">
                            <Label htmlFor="newName" className="s3-explr-label">
                              Tên mới
                            </Label>
                            <Input
                              id="newName"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              placeholder="Nhập tên mới"
                              disabled={isRenaming}
                              className="s3-explr-input"
                            />
                          </div>
                        </div>
                      </TabsContent>
                      {!fileToRename.isFolder && (
                        <>
                          {(isTextFile(fileToRename) || isImageFile(fileToRename)) && (
                            <TabsContent value="edit">
                              <div className="space-y-4 py-4">
                                {isLoadingContent ? (
                                  <div className="flex items-center justify-center py-10">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                  </div>
                                ) : (
                                  <div className="max-h-[70dvh] overflow-auto rounded-md border px-4 py-3 bg-muted/30">
                                    <Label
                                      htmlFor="content"
                                      className="block text-sm font-medium mb-2 text-muted-foreground"
                                    >
                                      {isImageFile(fileToRename)
                                        ? 'Nội dung OCR (đang chỉnh sửa nội dung OCR được trích xuất từ ảnh)'
                                        : 'Nội dung tập tin'}
                                    </Label>
                                    <Textarea
                                      id="content"
                                      value={fileContent}
                                      onChange={(e) => setFileContent(e.target.value)}
                                      placeholder={
                                        isImageFile(fileToRename)
                                          ? 'Chưa có nội dung OCR...'
                                          : 'Nhập nội dung tập tin'
                                      }
                                      className="font-mono text-sm resize-none min-h-[200px] w-full"
                                      disabled={isSavingContent}
                                      style={{ height: 'auto', maxHeight: '60vh' }}
                                      rows={Math.max(10, fileContent.split('\n').length + 1)}
                                    />
                                  </div>
                                )}
                              </div>
                            </TabsContent>
                          )}

                          <TabsContent value="edit-metadata">
                            <div className="space-y-4 py-4">
                              {isLoadingContent ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="h-6 w-6 animate-spin" />
                                </div>
                              ) : (
                                <div className="h-[70dvh] min-h-[500px] overflow-hidden overflow-y-auto pb-3 px-1">
                                  {metadataForEdit &&
                                    typeof metadataForEdit === 'object' &&
                                    Object.entries(metadataForEdit).map(([key, value], index) => {
                                      // Check if this is a required Select field
                                      const isProcessingStatus = key === 'processingStatus';
                                      const isStatus = key === 'status';
                                      const isRequired = [
                                        'summary',
                                        'issueDate',
                                        'effectiveDate',
                                        'processingStatus',
                                        'status',
                                      ].includes(key);

                                      // Get the error message for this field
                                      const fieldError = metadataErrors[key];

                                      return (
                                        <div key={index} className="pb-3 flex flex-col gap-2">
                                          <Label
                                            htmlFor={key}
                                            className="s3-explr-label flex items-center gap-1"
                                          >
                                            {keyMapMetadata[key] || key}
                                            {isRequired && <span className="text-red-500">*</span>}
                                            {isDisableField(key) && (
                                              <PenOff className="h-[12px] w-[12px] text-red-500" />
                                            )}
                                          </Label>

                                          {/* ProcessingStatus - Select */}
                                          {isProcessingStatus ? (
                                            <div className="space-y-1">
                                              <Select
                                                value={
                                                  typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)
                                                }
                                                onValueChange={(newValue) => {
                                                  const newMetadata = {
                                                    ...metadataForEdit,
                                                    [key]: newValue,
                                                  };
                                                  setMetadataForEdit(newMetadata);
                                                }}
                                                disabled={isSavingContent || isDisableField(key)}
                                              >
                                                <SelectTrigger className="s3-explr-input">
                                                  <SelectValue placeholder="Chọn trạng thái xử lý" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="phat_hanh">
                                                    Phát hành
                                                  </SelectItem>
                                                  <SelectItem value="da_phat_hanh">
                                                    Đã phát hành
                                                  </SelectItem>
                                                </SelectContent>
                                              </Select>
                                              {isRequired && fieldError && (
                                                <p className="text-xs text-red-500">{fieldError}</p>
                                              )}
                                            </div>
                                          ) : // Status - Select
                                          isStatus ? (
                                            <div className="space-y-1">
                                              <Select
                                                value={
                                                  typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)
                                                }
                                                onValueChange={(newValue) => {
                                                  const newMetadata = {
                                                    ...metadataForEdit,
                                                    [key]: newValue,
                                                  };
                                                  setMetadataForEdit(newMetadata);
                                                }}
                                                disabled={isSavingContent || isDisableField(key)}
                                              >
                                                <SelectTrigger className="s3-explr-input">
                                                  <SelectValue placeholder="Chọn tình trạng" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="hieu_luc">Hiệu lực</SelectItem>
                                                  <SelectItem value="het_hieu_luc">
                                                    Hết hiệu lực
                                                  </SelectItem>
                                                </SelectContent>
                                              </Select>
                                              {isRequired && fieldError && (
                                                <p className="text-xs text-red-500">{fieldError}</p>
                                              )}
                                            </div>
                                          ) : isTextarea(key) ? (
                                            <div className="space-y-1">
                                              <Textarea
                                                id={key}
                                                value={
                                                  typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)
                                                }
                                                onChange={(e: any) => {
                                                  const newMetadata = {
                                                    ...metadataForEdit,
                                                    [key]: e.target.value,
                                                  };
                                                  setMetadataForEdit(newMetadata);
                                                }}
                                                placeholder="Nhập nội dung tập tin"
                                                className="min-h-[200px] font-mono"
                                                disabled={isSavingContent || isDisableField(key)}
                                              />
                                              {isRequired && fieldError && (
                                                <p className="text-xs text-red-500">{fieldError}</p>
                                              )}
                                            </div>
                                          ) : isDisableField(key) ||
                                            [
                                              'createdDate',
                                              'effectiveDate',
                                              'expirationDate',
                                              'issueDate',
                                            ].includes(key) ? (
                                            <div className="space-y-1">
                                              <Input
                                                id={key}
                                                type={
                                                  [
                                                    'createdDate',
                                                    'effectiveDate',
                                                    'expirationDate',
                                                    'issueDate',
                                                  ].includes(key)
                                                    ? 'date'
                                                    : undefined
                                                }
                                                value={
                                                  typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)
                                                }
                                                onChange={(e: any) => {
                                                  const newMetadata = {
                                                    ...metadataForEdit,
                                                    [key]: e.target.value,
                                                  };
                                                  setMetadataForEdit(newMetadata);
                                                }}
                                                placeholder="Nhập nội dung"
                                                className="s3-explr-input"
                                                disabled={isSavingContent || isDisableField(key)}
                                              />
                                              {isRequired && fieldError && (
                                                <p className="text-xs text-red-500">{fieldError}</p>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="space-y-1">
                                              <Input
                                                id={key}
                                                value={
                                                  typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)
                                                }
                                                onChange={(e: any) => {
                                                  const newMetadata = {
                                                    ...metadataForEdit,
                                                    [key]: e.target.value,
                                                  };
                                                  setMetadataForEdit(newMetadata);
                                                }}
                                                placeholder="Nhập nội dung"
                                                className="s3-explr-input"
                                                disabled={isSavingContent || isDisableField(key)}
                                              />
                                              {isRequired && fieldError && (
                                                <p className="text-xs text-red-500">{fieldError}</p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          </TabsContent>
                        </>
                      )}
                    </Tabs>
                  )}
                  <DialogFooter className="s3-explr-dialog-footer">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFileToRename(null);
                        setMetadataForEdit(null);
                        setNewName('');
                        setFileContent('');
                      }}
                      className="s3-explr-cancel-btn"
                    >
                      Hủy
                    </Button>
                    {activeTab === 'edit' ? (
                      <Button
                        onClick={() => fileToRename && saveFileContent(fileToRename, fileContent)}
                        disabled={isSavingContent || isLoadingContent}
                        className="s3-explr-save-btn"
                      >
                        {isSavingContent ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                            Đang lưu...
                          </>
                        ) : (
                          'Lưu thay đổi'
                        )}
                      </Button>
                    ) : activeTab === 'edit-metadata' ? (
                      <Button
                        onClick={() =>
                          metadataForEdit &&
                          typeof metadataForEdit === 'object' &&
                          fileToRename &&
                          saveJsonFile(fileToRename)
                        }
                        disabled={isSavingContent || isLoadingContent}
                        className="s3-explr-save-btn"
                      >
                        {isSavingContent ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                            Đang lưu...
                          </>
                        ) : (
                          'Lưu thay đổi'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          if (newName.trim() && fileToRename) {
                            void handleRename(fileToRename, newName.trim());
                          }
                        }}
                        disabled={
                          isRenaming || !newName.trim() || newName.trim() === fileToRename?.name
                        }
                        className="s3-explr-rename-btn"
                      >
                        {isRenaming ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin s3-explr-loader" />
                            Đang đổi tên...
                          </>
                        ) : (
                          'Đổi tên'
                        )}
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog
                open={!!fileToView}
                onOpenChange={(open) => {
                  if (!open) {
                    setFileToView(null);
                    setFileContent('');
                  }
                }}
              >
                <DialogContent className="s3-explr-dialog-content max-w-5xl w-[90%]">
                  <DialogHeader className="s3-explr-dialog-header">
                    <DialogTitle className="s3-explr-dialog-title">Xem tập tin</DialogTitle>
                  </DialogHeader>

                  {fileToView && (
                    <Tabs value={'view'}>
                      <TabsList className="grid w-full grid-cols-1 ">
                        {!fileToView.isFolder && isTextFile(fileToView) && (
                          <TabsTrigger value="view">Nội dung</TabsTrigger>
                        )}
                      </TabsList>
                      {!fileToView.isFolder && isTextFile(fileToView) ? (
                        <TabsContent value="view">
                          <div className="space-y-4 py-4">
                            {isLoadingContent ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin" />
                              </div>
                            ) : (
                              <div className="overflow-hidden overflow-y-auto pb-3 px-1">
                                <div className="rounded-md bg-muted p-4 font-mono text-sm overflow-auto max-h-[60vh] border">
                                  <pre className="whitespace-pre-wrap break-words">
                                    {fileContent}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </TabsContent>
                      ) : (
                        <TabsContent value="view">
                          <div className="space-y-4 py-4">
                            {isLoadingContent ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin" />
                              </div>
                            ) : (
                              <div className="overflow-hidden overflow-y-auto pb-3 px-1">
                                <div className="rounded-md bg-muted p-4 font-mono text-sm overflow-auto max-h-[60vh] border">
                                  <pre className="whitespace-pre-wrap break-words">
                                    Định dạng file không xem trực tiếp được, hãy tải file về để xem!
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </TabsContent>
                      )}
                    </Tabs>
                  )}
                  <DialogFooter className="s3-explr-dialog-footer">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFileToView(null);
                        setFileContent('');
                      }}
                      className="s3-explr-cancel-btn"
                    >
                      Đóng
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {fileToMove && openDialogRename && (
                <DialogRemoveFiles
                  openDialogRename={openDialogRename}
                  setOpenDialogRename={setOpenDialogRename}
                  apiBaseUrl={apiBaseUrl}
                  setFileToMove={setFileToMove}
                  fileToMove={fileToMove}
                  refreshFiles={refreshFiles}
                  ignoreCache={ignoreCache}
                  autoSyncAfterFileOperation={autoSyncAfterFileOperation}
                />
              )}
            </>
          )}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative s3-explr-error">
            Đã xảy ra lỗi khi tải danh sách tập tin
          </div>
        )}

        <FileUpload
          onFilesSelected={(e: any, typeUpload) => uploadFiles(e, currentPrefix, typeUpload)}
        >
          <div className="rounded-md border s3-explr-table-container">
            <Table className="s3-explr-table">
              <TableHeader className="s3-explr-table-header">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent s3-explr-header-row"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="s3-explr-header-cell">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="s3-explr-table-body">
                {isLoading ? (
                  // Skeleton rows: giu chieu cao bang on dinh khi dang tai/tim kiem,
                  // tranh nhap nhay giua trang thai rong va co du lieu.
                  Array.from({ length: 6 }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`} className="h-12 s3-explr-skeleton-row">
                      {columns.map((_col, colIndex) => (
                        <TableCell
                          key={`skeleton-cell-${rowIndex}-${colIndex}`}
                          className="align-middle s3-explr-skeleton-cell"
                        >
                          <div className="h-4 w-full max-w-[200px] animate-pulse rounded bg-gray-200" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : table.getRowModel().rows.length === 0 ? (
                  <TableRow className="s3-explr-empty-row">
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center s3-explr-empty-cell"
                    >
                      {searchQuery ? (
                        <div className="text-gray-500 s3-explr-no-results">
                          Không tìm thấy tập tin hoặc thư mục nào khớp với "{searchQuery}"
                        </div>
                      ) : (
                        <div className="text-gray-500 s3-explr-empty-message">
                          Thư mục này không chứa tập tin hoặc thư mục con nào
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="h-12 s3-explr-row">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="align-middle s3-explr-cell">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </FileUpload>

        {searchQuery && estimatedTotalHits !== 0 && (
          <>
            <Pagination>
              <PaginationContent>
                <PaginationPrevious
                  onClick={() => !isLoading && setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  aria-disabled={currentPage === 1 || isLoading}
                  className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
                />
                {Array.from({ length: Math.ceil(estimatedTotalHits / 20) }, (_, index) => {
                  const pageIndex = index + 1;
                  const totalPages = Math.ceil(estimatedTotalHits / 20);
                  const isHidden =
                    totalPages > 7 &&
                    !(
                      pageIndex === 1 ||
                      pageIndex === totalPages ||
                      (pageIndex >= currentPage - 1 && pageIndex <= currentPage + 1)
                    );

                  if (isHidden) {
                    if (
                      (pageIndex === currentPage - 2 && currentPage > 4) ||
                      (pageIndex === currentPage + 2 && currentPage < totalPages - 3)
                    ) {
                      return (
                        <PaginationEllipsis key={index} className="pointer-events-none">
                          ...
                        </PaginationEllipsis>
                      );
                    }
                    return null;
                  }

                  return (
                    <PaginationItem className="cursor-pointer" key={index}>
                      <PaginationLink
                        isActive={currentPage === pageIndex}
                        onClick={() => setCurrentPage(pageIndex)}
                        className={`${
                          currentPage === pageIndex
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-black'
                        } hover:bg-blue-100`}
                      >
                        {pageIndex}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      !isLoading &&
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, Math.ceil(estimatedTotalHits / 20)),
                      )
                    }
                    aria-disabled={currentPage === Math.ceil(estimatedTotalHits / 20) || isLoading}
                    className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </>
        )}

        {!searchQuery && pagination && pagination.totalPages > 1 && (
          <>
            <div className="flex items-center justify-between mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationPrevious
                    onClick={() => !isLoading && setBrowsePage((prev) => Math.max(prev - 1, 1))}
                    aria-disabled={browsePage === 1 || isLoading}
                    className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
                  />
                  {isLoading ? (
                    <PaginationItem>
                      <div className="flex items-center gap-2 px-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Đang tải...</span>
                      </div>
                    </PaginationItem>
                  ) : (
                    Array.from({ length: pagination.totalPages }, (_, index) => {
                      const pageIndex = index + 1;
                      const totalPages = pagination.totalPages;
                      const isHidden =
                        totalPages > 7 &&
                        !(
                          pageIndex === 1 ||
                          pageIndex === totalPages ||
                          (pageIndex >= browsePage - 1 && pageIndex <= browsePage + 1)
                        );

                      if (isHidden) {
                        if (
                          (pageIndex === browsePage - 2 && browsePage > 4) ||
                          (pageIndex === browsePage + 2 && browsePage < totalPages - 3)
                        ) {
                          return (
                            <PaginationEllipsis key={index} className="pointer-events-none">
                              ...
                            </PaginationEllipsis>
                          );
                        }
                        return null;
                      }

                      return (
                        <PaginationItem className="cursor-pointer" key={index}>
                          <PaginationLink
                            isActive={browsePage === pageIndex}
                            onClick={() => setBrowsePage(pageIndex)}
                            className={`${
                              browsePage === pageIndex
                                ? 'bg-blue-500 text-white'
                                : 'bg-white text-black'
                            } hover:bg-blue-100`}
                          >
                            {pageIndex}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        !isLoading &&
                        setBrowsePage((prev) => Math.min(prev + 1, pagination.totalPages))
                      }
                      aria-disabled={browsePage === pagination.totalPages || isLoading}
                      className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </>
        )}
        {asSelector && onSelected && (
          <div className="flex justify-between items-center mt-4 s3-explr-selector-footer">
            <div className="text-sm text-gray-500 s3-explr-selection-count">
              {calculateSelectedFiles()}
            </div>
            <Button
              variant="default"
              onClick={() => onSelected(getSelectedFiles())}
              disabled={selectedKeys.size === 0}
              className="s3-explr-ok-btn"
            >
              OK
            </Button>
          </div>
        )}
        <DialogShare
          open={!!fileToShare}
          onClose={() => setFileToShare(null)}
          file={fileToShare}
          apiBaseUrl={apiBaseUrl}
          rootUrlHostApi={rootUrlHostApi}
        />
        <CrawlProgressDialog
          open={isCrawlProgressDialogOpen}
          onClose={() => setIsCrawlProgressDialogOpen(false)}
          currentJob={currentJob}
          elapsedTime={elapsedTime}
          onStop={stopCurrentJob}
          pages={pages}
        />
        <Dialog
          open={outlookSettingsOpen}
          onOpenChange={(open) => {
            setOutlookSettingsOpen(open);
            if (open) {
              void loadEmailConfig();
              if (!settingsRunning) {
                fetch(`${apiBaseUrl}/api/email-scheduler/status`)
                  .then((res) =>
                    res.ok
                      ? (res.json() as Promise<{
                          isRunning: boolean;
                          lastRunResult: LastRunResult;
                        }>)
                      : null,
                  )
                  .then((data) => {
                    if (data && data.isRunning) {
                      startPolling();
                    } else if (data && !data.isRunning && data.lastRunResult) {
                      displayResult(data.lastRunResult);
                    }
                  })
                  .catch(() => {});
              }
            }
          }}
        >
          <DialogContent className="sm:max-w-lg max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Cấu hình Email Scheduler</DialogTitle>
              <DialogDescription>Quản lý cấu hình và xác thực email scheduler.</DialogDescription>
            </DialogHeader>

            <div className="min-h-0 space-y-4 overflow-y-auto py-2 pr-1">
              {/* Auth status */}
              <div className="rounded-md border p-3 space-y-2">
                {outlookEmail ? (
                  <div className="flex items-center gap-1.5 text-sm text-green-700">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                    {outlookEmail}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Chưa xác thực</p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={() => {
                    if (!resolvedCrawlerKnownFolder) return;
                    setOutlookDialogContext(resolvedCrawlerKnownFolder);
                    setOutlookDialogOpen(true);
                    void handleBootstrap(resolvedCrawlerKnownFolder);
                  }}
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  Đăng nhập Outlook
                </Button>
              </div>

              {configLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Đang tải cấu hình...
                </div>
              )}

              {!configLoading && emailConfig !== null && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="settings-enabled">Bật/Tắt</Label>
                    <Switch
                      id="settings-enabled"
                      checked={formEnabled}
                      onCheckedChange={(checked) => {
                        setFormEnabled(checked);
                        void saveConfig('enabled', checked);
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="settings-cron">Lịch chạy hàng ngày</Label>
                    <Select
                      value={formVnTime}
                      onValueChange={(vnTime) => {
                        const cron = vnTimeToUtcCron(vnTime);
                        setFormCron(cron);
                        void saveConfig('schedule_cron', cron);
                      }}
                      disabled={configSaving}
                    >
                      <SelectTrigger id="settings-cron">
                        <SelectValue placeholder="Chọn giờ chạy" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {vnTimeOptions.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formVnTime && (
                      <p className="text-xs text-muted-foreground">
                        Hàng ngày lúc {formVnTime} (giờ VN)
                      </p>
                    )}
                  </div>

                  {/* <div className="space-y-1">
                    <Label htmlFor="settings-prefix">Thư mục S3</Label>
                    <Input
                      id="settings-prefix"
                      value={formPrefix}
                      onChange={(e) => setFormPrefix(e.target.value)}
                      onBlur={() => void saveConfig('s3_prefix', formPrefix)}
                      placeholder="email-reports"
                      disabled={configSaving}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="settings-max-emails">Số email tối đa mỗi lần</Label>
                    <Input
                      id="settings-max-emails"
                      type="number"
                      value={formMaxEmails}
                      onChange={(e) => setFormMaxEmails(Number(e.target.value))}
                      onBlur={() => void saveConfig('max_emails_per_run', formMaxEmails)}
                      disabled={configSaving}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="settings-client-id">Outlook Client ID</Label>
                    <Input
                      id="settings-client-id"
                      type="password"
                      value={formClientId}
                      onChange={(e) => setFormClientId(e.target.value)}
                      onBlur={() => void saveConfig('outlook_client_id', formClientId)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      disabled={configSaving}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="settings-authority">Outlook Authority</Label>
                    <Input
                      id="settings-authority"
                      type="password"
                      value={formAuthority}
                      onChange={(e) => setFormAuthority(e.target.value)}
                      onBlur={() => void saveConfig('outlook_authority', formAuthority)}
                      placeholder="https://login.microsoftonline.com/..."
                      disabled={configSaving}
                    />
                  </div> */}

                  <div className="space-y-2">
                    <Label>Bộ lọc tiêu đề báo cáo ngày</Label>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-1">
                      {DAILY_REPORT_FILTER_FIELDS.map((field) =>
                        renderSubjectFilterInput({
                          id: `settings-${field.key}-filter`,
                          label: field.label,
                          value: formFilters[field.key],
                          placeholder: field.placeholder,
                          onChange: (value) =>
                            setFormFilters((prev) => ({ ...prev, [field.key]: value })),
                          onBlur: () => void saveConfig('subject_filters', formFilters),
                        }),
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bộ lọc tiêu đề báo cáo tuần</Label>
                    {renderSubjectFilterInput({
                      id: 'settings-week-equipment-status-filter',
                      label: 'Tình trạng thiết bị',
                      value: formFiltersWeek.equipment_status,
                      placeholder: 'Báo cáo tình trạng thiết bị tuần',
                      onChange: (value) =>
                        setFormFiltersWeek((prev) => ({ ...prev, equipment_status: value })),
                      onBlur: () => void saveConfig('subject_filters_week', formFiltersWeek),
                    })}
                  </div>

                  <div className="space-y-2">
                    <Label>Bộ lọc tiêu đề báo cáo tháng</Label>
                    {renderSubjectFilterInput({
                      id: 'settings-month-equipment-status-filter',
                      label: 'Tình trạng thiết bị',
                      value: formFiltersMonth.equipment_status,
                      placeholder: 'Báo cáo tình trạng thiết bị tháng',
                      onChange: (value) =>
                        setFormFiltersMonth((prev) => ({ ...prev, equipment_status: value })),
                      onBlur: () => void saveConfig('subject_filters_month', formFiltersMonth),
                    })}
                  </div>

                  <div className="space-y-2">
                    <Label>Bộ lọc tiêu đề báo cáo quý</Label>
                    {renderSubjectFilterInput({
                      id: 'settings-quarter-equipment-status-filter',
                      label: 'Tình trạng thiết bị',
                      value: formFiltersQuarter.equipment_status,
                      placeholder: 'Báo cáo tình trạng thiết bị quý',
                      onChange: (value) =>
                        setFormFiltersQuarter((prev) => ({ ...prev, equipment_status: value })),
                      onBlur: () => void saveConfig('subject_filters_quarter', formFiltersQuarter),
                    })}
                  </div>
                </div>
              )}

              {settingsRunResult && (
                <p className="text-sm text-muted-foreground">{settingsRunResult}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={settingsRunning}
                onClick={() => void handleRunNow()}
              >
                {settingsRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {settingsRunning ? 'Đang chạy...' : 'Chạy ngay'}
              </Button>
              <Button variant="outline" onClick={() => setOutlookSettingsOpen(false)}>
                Đóng
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <EmailCrawlerSourceModal
          open={crawlerSourceModalOpen}
          onOpenChange={(open) => {
            setCrawlerSourceModalOpen(open);
            if (!open) {
              setCrawlerCreateDefaults(null);
              void refreshResolvedCrawlerSource();
            }
          }}
          source={crawlerSource}
          createDefaults={crawlerCreateDefaults}
          bldReports={crawlerBldReports}
          apiBaseUrl={apiBaseUrl}
          onSourceUpdated={(updated) => setCrawlerSource(updated)}
          onSourceCreated={() => {
            setCrawlerCreateDefaults(null);
          }}
        />

        <ReportHistoryModal
          open={reportHistoryOpen}
          onOpenChange={setReportHistoryOpen}
          apiBaseUrl={apiBaseUrl}
        />

        <Dialog
          open={outlookDialogOpen}
          // modal=false: dialog này có thể được mở khi modal "Cấu hình tuyển
          // dụng" (EmailCrawlerSourceModal) vẫn đang mở phía dưới (xem
          // handleCrawlerOutlookLogin). Hai Dialog Radix độc lập cùng mở modal
          // (mặc định) khiến Radix quản lý pointer-events/aria-hidden trên
          // document.body qua 2 root riêng biệt; khi dialog này tự đóng bằng
          // setTimeout, dọn dẹp không đồng bộ khiến body bị kẹt pointer-events
          // và modal phía dưới không thể click/lưu được nữa. Tắt modal-trap ở
          // đây, lớp overlay (bg-black/80) của chính dialog vẫn chặn tương tác
          // với modal phía dưới trong lúc đang mở.
          modal={false}
          onOpenChange={(open) => {
            setOutlookDialogOpen(open);
            if (!open) {
              setBootstrapId(null);
              setBootstrapInfo(null);
              setBootstrapStatus(null);
              setBootstrapError(null);
              setOutlookDialogContext(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Login Outlook</DialogTitle>
              <DialogDescription>
                Xác thực với Microsoft để hệ thống có thể đọc email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {bootstrapLoading && !bootstrapInfo && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Đang khởi tạo...
                </div>
              )}
              {bootstrapInfo?.userCode && bootstrapStatus === 'pending' && (
                <>
                  <p className="text-sm">Truy cập link bên dưới và nhập mã:</p>
                  <div className="flex items-center gap-2 rounded-md border p-3 bg-muted/50">
                    <code className="flex-1 text-lg font-mono font-bold tracking-widest text-center">
                      {bootstrapInfo.userCode}
                    </code>
                    <Button variant="ghost" size="sm" onClick={handleCopyCode}>
                      {copied ? '✓' : 'Copy'}
                    </Button>
                  </div>
                  <a
                    href={bootstrapInfo.verificationUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {bootstrapInfo.verificationUri}
                    <MoveUpRight className="h-3 w-3" />
                  </a>
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Đang chờ xác thực...
                  </div>
                </>
              )}
              {bootstrapStatus === 'success' && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 text-sm text-green-800 font-medium"
                >
                  Đã xác thực: {outlookEmail}
                </div>
              )}
              {bootstrapStatus === 'error' && (
                <div role="alert" className="space-y-2">
                  <p className="text-sm text-destructive">{bootstrapError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleBootstrap(bootstrapContext)}
                  >
                    Thử lại
                  </Button>
                </div>
              )}
              {bootstrapStatus === 'timeout' && (
                <div role="alert" className="space-y-2">
                  <p className="text-sm text-yellow-800">Hết thời gian chờ. Vui lòng thử lại.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleBootstrap(bootstrapContext)}
                  >
                    Thử lại
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOutlookDialogOpen(false)}>
                Đóng
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
);

export const Home = (props: any) => {
  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <HomeWithoutContext
        {...props}
        // asSelector={true}
        // preSelectedFiles={['PO/', 'abs-sample/FAQs.txt']}
      />
      <ToastContainer />
    </GoogleOAuthProvider>
  );
};
